/**
 * SciChartJsChartBuilder — the imperative SciChart.js shell.
 *
 * All model→render decisions live in the pure {@link buildPanePlan} layer
 * (unit-tested without a browser). This file translates a resolved
 * {@link PanePlan} into SciChart objects.
 *
 * A chart group renders as ONE parent SciChartSurface subdivided into a stacked
 * SubChart per pane. Sub-surfaces share the parent's single WebAssembly context,
 * which is what makes multi-pane work: separate top-level surfaces on a page
 * share a process-global font cache across contexts and throw binding errors,
 * while a shared context does not. It also gives a shared X coordinate space for
 * cross-pane alignment.
 *
 * Typecheck-verified against the SciChart v5 API; runtime/visual verification
 * happens in the web app (WebGL/WASM in a browser).
 */

import {
  resolveColorRef,
  mintUlid,
  DEFAULT_THEME,
  type Annotation,
  type Pane,
  type Plot,
  type AxisInfo,
  type PlotStyle,
  type PlotPalette,
  type Theme,
  type Ulid,
} from "@qufolio/model";
import {
  SciChartSurface,
  SciChartSubSurface,
  SciChartSurfaceBase,
  DiscontinuousDateAxis,
  NumericAxis,
  LogarithmicAxis,
  NumericTickProvider,
  EAxisAlignment,
  EAutoRange,
  NumberRange,
  OhlcDataSeries,
  DpiHelper,
  XyDataSeries,
  XyyDataSeries,
  FastCandlestickRenderableSeries,
  FastOhlcRenderableSeries,
  FastLineRenderableSeries,
  FastMountainRenderableSeries,
  FastColumnRenderableSeries,
  FastBandRenderableSeries,
  XyScatterRenderableSeries,
  EllipsePointMarker,
  TrianglePointMarker,
  SquarePointMarker,
  CrossPointMarker,
  XPointMarker,
  type IPointMarker,
  DefaultPaletteProvider,
  EStrokePaletteMode,
  EFillPaletteMode,
  ZoomPanModifier,
  MouseWheelZoomModifier,
  ZoomExtentsModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  translateFromCanvasToSeriesViewRect,
  type ICursorModifierOptions,
  type ModifierMouseArgs,
  AxisMarkerAnnotation,
  BoxAnnotation,
  CompositeAnnotation,
  AnnotationHoverModifier,
  TextAnnotation,
  EAnnotationLayer,
  ELabelPlacement,
  ECoordinateMode,
  EHoverMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  HorizontalLineAnnotation,
  VerticalLineAnnotation,
  LineAnnotation,
  NativeTextAnnotation,
  EDragMode,
  EXyDirection,
  SciChartJSDarkv2Theme,
  SciChartJSLightTheme,
  Thickness,
  type TSciChart,
  type IRenderableSeries,
  type IThemeProvider,
  type AxisBase2D,
  Point,
  EBaseType,
  createType,
} from "scichart";

import type { ChartBuilder, EstimateLine, EventMarker, PlotHit, SeriesDelta } from "../chart-builder.js";
import {
  buildPanePlan,
  type AxisSpecLookup,
  type EventKindLookup,
  type PanePlan,
  type ReferenceLinePlan,
  type SeriesPlan,
} from "../plan/pane-plan.js";
import {
  anchoredSignalY,
  effectiveSignalShape,
  glyphOf,
  paneLaneRange,
  signalColumnSpan,
  signalOffset,
  signalPoints,
  withSignalAt,
  DEFAULT_MARKER_SHAPE,
  type EventKind,
  type GlyphShape,
  type MarkerShape,
  type SignalDirection,
} from "../plan/signal.js";
import { resolvePalettePlan, resolveBarColorsArgb, type PalettePlan } from "../plan/palette.js";
import { signedLogLabel, signedLogTicks, toSignedLog } from "../plan/signed-log.js";
import {
  visibleBarRange,
  volumeBarX,
  volumeByPricePlan,
  volumeByPriceParams,
  type VolumeByPriceBar,
  type VolumeByPricePlan,
  type VolumeByPriceSide,
} from "../plan/volume-by-price.js";
// The legend's own volume formatter, so a zone's delta abbreviates exactly as
// the volume pane's reading of the same quantity does.
import { legendValue } from "../plan/legend.js";
import { resolvePlotDash } from "../plan/dash.js";
import { nearestBarTime } from "../plan/snap.js";
import { barSlot } from "../plan/bar-slot.js";
import { PLOT_HIT_TOLERANCE, indexOfTime, isHittableKind, nearestPlot } from "../plan/plot-hit.js";
import { notesFit } from "../plan/marker-notes.js";
import { layoutAxisTags, readableTextOn } from "../plan/axis-tags.js";

/** An axis tag's height in CSS px: 12 px text plus its padding. */
const AXIS_TAG_HEIGHT = 18;
import { COMPACT_FROM, compactNumber } from "../plan/compact.js";
import { candleColors, type CandleArgb, type CandleColors, type CandlePaint } from "../plan/candle-colors.js";
import { sciChartThemeOverrides } from "./scichart-theme.js";
import { parseColorToArgb, withOpacityArgb } from "../plan/color.js";
import {
  DATE_LABELS,
  DEFAULT_GRID_LINES,
  maxMajorGridLines,
  resolveAxisPlan,
  timeAxisFormatters,
  type AxisGridPlan,
  type AxisPlan,
  type GridLineStyle,
  type TimeAxisLabels,
} from "../plan/axis.js";
import {
  fibPrices,
  isPlaceable,
  isRay,
  placedByPoints,
  type AnnotationKind,
  hoverStyle,
  needsTwoPoints,
  resolveAnnotation,
  resolveAnnotations,
  type AnnotationPlan,
  type DerivedAnnotation,
} from "../plan/annotation.js";
import {
  containingIndex,
  indexAtTime,
  offsetAtTime,
  placeRayStart,
  placeX,
  timeAtIndex,
} from "../plan/annotation-x.js";
import {
  BAR_HIGHLIGHT_OPACITY,
  draftAnnotation,
  fibDefaults,
  isWorthKeeping,
  simplifyPath,
  type DrawPoint,
} from "../plan/annotation-draw.js";
import { DrawingModifier } from "./drawing-modifier.js";
import {
  ETradingAnnotationType,
  FreehandDrawingModifier,
  MultiPointAnnotationPlacementModifier,
} from "scichart-financial-tools";
import { kindForTradingClass, tradingClassFor } from "./trading-annotation-map.js";
import {
  bandedRange,
  fitsAxis,
  fitWindow,
  fitYRange,
  seriesForFit,
  type PriceFit,
} from "../plan/y-fit.js";
import { resolveInteractions, type InteractionPlan } from "../plan/interactions.js";
import { paneWeight, paneWeights, stackFractions } from "../plan/stack.js";

/**
 * How often to ask whether a placement has finished, in milliseconds.
 *
 * Slow enough to be free — it runs only while a drawing is being placed — and
 * fast enough that the style bar opens with the drawing rather than visibly
 * after it. A frame-rate poll would buy nothing: the wait is for a human to
 * click, not for the renderer.
 */
const PLACEMENT_POLL_MS = 100;

/**
 * As much of a `scichart-financial-tools` annotation as the adoption path uses.
 *
 * Structural rather than the real class union: naming twenty-five types to read
 * two properties off them would import the whole palette into this file for
 * nothing, and the properties are declared on their shared base.
 */
/**
 * One highlighted bar for {@link SciChartJsChartBuilder.setBarHighlights}: its
 * time, a token or hex, and optionally a price span — given, the box runs from
 * `low` to `high` around the candle; absent, it is the pane's full height.
 */
/** One background range for `setSpans`: axis times, a token or hex, optional opacity. */
export interface TimeSpan {
  x1: number;
  x2: number;
  color: string;
  /** 0–1; absent is the forming band's ~13%. */
  opacity?: number;
}

export interface BarHighlight {
  t: number;
  color?: string;
  low?: number;
  high?: number;
  /** 0–1; absent is the forming band's ~13%. */
  opacity?: number;
}

interface PlacedAnnotation {
  readonly type?: string;
  readonly points?: readonly { x: number; y: number }[];
}

/** SciChart's implicit default axis id (used when a plot has no axisRef). */
const DEFAULT_Y_AXIS_ID = "DefaultAxisId";

/**
 * Marker size for a signal, in device pixels.
 *
 * 5, the owner's call (2026-10-02): it was 9, on the argument that a mark
 * against a mostly empty strip must be findable at a glance — but DOTS stacks
 * eleven lanes, and at 9 they crowd each other. 7 since 2026-10-14, the
 * owner's call again (Pressure's markers). A plot's own `markerSize` wins.
 */
const SIGNAL_MARKER_PX = 7;

/** A scatter point's default size — it traces a path. */
const SCATTER_MARKER_PX = 7;

/**
 * How long `captureImage` waits for a rendered frame before giving up.
 *
 * A redraw that has been asked for lands in a frame or two; anything past that
 * is a surface that is frozen, disposed or out of view, and waiting on it
 * blocks the Summary tab for as long as the chart stays still. Two seconds is
 * far past a real render and far short of a wait a reader would notice —
 * the note is simply written without a picture, which it says.
 */
const CAPTURE_TIMEOUT_MS = 2000;

/**
 * Distinct per builder, and that is the whole point — see {@link SciChartBuilderOptions.modifierGroup}.
 */
let modifierGroupSeq = 0;

/**
 * Volume-by-Price — the parameters, every one of them settled by the user.
 *
 * They live here rather than on the plot because a `volumeByPrice` plot carries
 * **no parameters at all** today: the contract gives it a type and a style and
 * nothing else, and the catalog cannot describe it either — its `compute`
 * returns one expression per bar, which a histogram over *price* is not. So the
 * shell holds the defaults, the style panel already owns `opacity`, and
 * `lookback`, `bins` and `width` become plot parameters the day the catalog can
 * carry them. See `docs/volume-by-price-design.html`.
 *
 * None of these is a number to re-derive by taste. `250` is a year of sessions;
 * `12` is StockCharts' own bin count; `0.20` and `0.30` are readability calls
 * made by eye against real candles, which is the only way either can be made —
 * the band fills went 0.08 → 0.12 → 0.25 → 0.12 before landing, and every move
 * away from "look at it and say" cost a round trip.
 */
/**
 * Used only when the plot's own `PlotStyle.opacity` says nothing.
 *
 * The other three settled numbers — lookback, bins and width — are **not** here.
 * They are editable catalog parameters, so they arrive on the plot's alias and
 * are read by `volumeByPriceParams` in the plan layer; their defaults live
 * beside the binner. Holding copies here is what made all three inert in the
 * first build: the picker edited the alias and this file never read it.
 */
const VOLUME_BY_PRICE_OPACITY = 0.18;

/**
 * The zones' colours when a plot names none: two greys, not the candles' pair.
 *
 * 0.30 of `positive`/`negative` was the first answer, and it is what "the whole
 * chart looks like Christmas" turned out to mean (owner, 2026-09-19): the zones
 * cover most of the pane, so the red and green that should say *this bar went
 * up* ended up saying nothing in particular over half the screen. Colour is
 * back to meaning direction, and the zones are furniture.
 *
 * **Still two of them.** Up and down volume in one colour would leave the
 * segment boundary as the only thing telling them apart, which is no
 * distinction at all — the objection `restyleSignal` makes. `neutral` against
 * `axis` is a light/dark pair in every shipped theme, and a `PlotPalette` still
 * overrides both, so anyone who wants the old pair can pick it.
 */
const VOLUME_BY_PRICE_TONES = ["neutral", "axis"] as const;

/**
 * Point size of a zone's delta label.
 *
 * Smaller than the legend's and smaller than an event glyph's, because there
 * can be sixty of them down one edge where there are a dozen of those. A
 * readability number picked to be read *when looked for* rather than to be
 * noticed — the zones are furniture and the arithmetic on them is too.
 */
const VOLUME_BY_PRICE_LABEL_SIZE = 10;

/**
 * The column a Volume-by-Price plot reads its volume out of.
 *
 * **It is fed through another plot, and that is the awkward part of this
 * feature.** A `volumeByPrice` plot *does* carry an `outputAlias` — that is how
 * every indicator carries its parameters, and this one's three are read back off
 * it — but **no server answers it**: `plotFeeds` skips this plot type, so no
 * column is requested on its behalf and no `SeriesDelta` is ever addressed to
 * it. The whole indicator is computed on the client from bars already loaded. The
 * closes come from the pane's own candles; the volumes come from whichever plot
 * is drawing the `volume` column, which on every chart the app builds is the
 * volume pane's histogram.
 *
 * The consequence to know about: **delete the volume pane and this indicator
 * has no volume**, and it stops drawing rather than drawing something wrong.
 * Fixing that properly means letting a plot request a column it does not draw,
 * which is a change to the fetch planner and not to this file.
 */
const VOLUME_BY_PRICE_SOURCE_ALIAS = "volume";

/**
 * Stroke width per series kind, for a plot that names none.
 *
 * Deliberately not one shared number. `PlotStyle.thickness` means the same thing
 * everywhere — device pixels of ink — but the *unstated* weight differs by shape:
 * two pixels is right for a moving average and heavy for a candle outline, and
 * zero is right for a column, whose edge was a transparent stroke before this
 * property reached it. A single default would have redrawn every existing chart.
 */
const DEFAULT_THICKNESS: Record<SeriesPlan["seriesKind"], number> = {
  candlestick: 1,
  ohlc: 1,
  line: 2,
  mountain: 2,
  band: 2,
  column: 0,
  scatter: 1,
};

/**
 * The appearance half of a {@link SeriesPlan} — everything a restyle may change.
 *
 * Named apart from `SeriesPlan` because the other half (kind, data kind, axis
 * ref) is structural: changing it means a different renderable class over a
 * different DataSeries, which is a rebuild and not a repaint. Keeping the two
 * sets distinct is what stops a restyle path quietly accepting a plot type.
 */
type SeriesStyle = Pick<
  SeriesPlan,
  "stroke" | "fill" | "opacity" | "thickness" | "dash" | "palette" | "digital" | "markerShape" | "markerSize" | "markerText" | "candleStyle"
>;

/**
 * The stroked-line family — line, mountain, band.
 *
 * SciChart hangs both `strokeDashArray` and `isDigitalLine` off this base and
 * nowhere else, so the two settings share one answer. A dashed column or a
 * stepped candle is not something the renderer can say, and the picker disables
 * the control rather than offering one that does nothing.
 */
/**
 * The last finite value in a series, or `undefined` when it holds none.
 *
 * A series can end in `NaN` now that a frame may carry rows past the last bar:
 * a projection null-fills every column it does not produce, and null decodes to
 * `NaN`. "The current value" is the last one there actually is.
 */
function lastFinite(values: readonly number[]): number | undefined {
  for (let i = values.length - 1; i >= 0; i--) {
    const v = values[i];
    if (v !== undefined && Number.isFinite(v)) return v;
  }
  return undefined;
}

function hasDashArray(kind: SeriesPlan["seriesKind"]): boolean {
  return kind === "line" || kind === "mountain" || kind === "band";
}

/** The user's word for a zoom direction, in SciChart's spelling. */
function xyDirectionFor(zoomAxis: InteractionPlan["zoomAxis"]): EXyDirection {
  switch (zoomAxis) {
    case "y":
      return EXyDirection.YDirection;
    case "both":
      return EXyDirection.XyDirection;
    default:
      return EXyDirection.XDirection;
  }
}

type AnyDataSeries = OhlcDataSeries | XyDataSeries | XyyDataSeries;

/**
 * A band's fill from its line colour — the same hue at a fifth of the ink.
 *
 * A fill at full strength buries the candles or the oscillator underneath it,
 * which is the whole thing the band is meant to be read against. `33` is the
 * alpha the single-fill path already used; this only extends it to the paired
 * case so the two sides stay consistent.
 */
/** `#rrggbb` at `opacity` as `#rrggbbaa`; any other form is returned as given. */
function atOpacity(color: string, opacity: number): string {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return color;
  const a = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  return `${color}${a.toString(16).padStart(2, "0")}`;
}

function translucent(color: string | undefined): string | undefined {
  if (!color) return undefined;
  // Already carries alpha (#rrggbbaa or a named/rgba form) — leave it alone
  // rather than appending a second one and producing nonsense.
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}33` : color;
}

/**
 * A band's two fills — **one decision, made in one place**.
 *
 * It was made in two, and they drifted: the construction path was corrected for
 * the double-alpha bug and `restyleSeries` was not. That is not a cosmetic
 * duplication, because **restyle wins**. `ChartGroupView` calls `applyTheme`
 * from its own effect on mount, so a freshly built band was constructed
 * correctly and then immediately repainted through the stale formula — which is
 * why the bug survived a fix that was demonstrably in the shipped bundle, and
 * why reading the construction site alone proved nothing.
 *
 * **The alpha is applied once, and `opacity` is where it lives.**
 * `series.opacity` carries the band's transparency, so a colour carrying its own
 * multiplies the two. Measured off a screenshot: Bollinger at `opacity: 0.25`
 * composited to 5% of its colour — exactly 0.25 x the `"33"` suffix's 0.2 —
 * while the same band with a fill token set composited to 25%, because naming a
 * colour was the one branch that skipped the suffix.
 *
 * Only a band carrying no opacity of its own still needs the colour to supply
 * one. Nothing in the catalog is in that state; `addBandBetween` mints one.
 */
function bandFills(
  // Structural, not `SeriesPlan`: the two callers are handed different types —
  // construction gets the whole plan, `restyleSeries` only the style half — and
  // naming either one here is what would keep them from sharing this.
  sp: { fill?: string; opacity?: number },
  stroke: string,
  paired: { up: string; down: string } | undefined,
): { fill: string; fillY1: string } {
  const up = paired?.up ?? sp.fill ?? stroke;
  const down = paired?.down ?? sp.fill ?? stroke;
  const carriesOwnAlpha = sp.opacity === undefined;
  return {
    fill: carriesOwnAlpha ? (translucent(up) ?? up) : up,
    fillY1: carriesOwnAlpha ? (translucent(down) ?? down) : down,
  };
}

export interface SciChartBuilderOptions {
  /** Active theme for token→color resolution. Defaults to the built-in dark theme. */
  theme?: Theme;
  /**
   * Resolves an indicator's declared axis — RSI's 30/70, MACD's zero — by the
   * name `Plot.indicatorRef` carries.
   *
   * Injected rather than imported because this package cannot fetch, and
   * **omitting it draws no reference lines rather than guessed ones**: the same
   * degradation `screener-grid` makes with no injected DSL catalog. The lines
   * are synthesised on every build and never stored, so a convention corrected
   * server-side reaches charts that already exist (D8).
   */
  axisSpecs?: AxisSpecLookup;
  /**
   * How each signal output reads — `single` or `dual` — by family and alias.
   * Same bargain as `axisSpecs`: omitted, every signal is `single`.
   */
  eventKinds?: EventKindLookup;
  /**
   * How the time axis writes a bar — dates for daily and coarser, the
   * exchange's clock for intraday. Defaults to dates. A constructor option
   * rather than a setter because the interval is structural: changing it
   * already rebuilds, and a rebuild makes a new builder.
   */
  timeLabels?: TimeAxisLabels;
  /**
   * Shared modifier group so rollover cursors line up across panes.
   *
   * **Defaults to a name no other builder can be using**, and a constant is the
   * wrong default however tempting: SciChart fans a grouped pointer event out to
   * every *other surface on the page* that names the same group, not just to the
   * sub-charts of this one. Panes line up either way — they go through
   * `processSubChartEvent`, which stays inside one parent surface — so a shared
   * name buys nothing and costs the page a crosshair on every chart at once,
   * mapped through another chart's viewport.
   *
   * It is worse than cosmetic once a chart is rebuilt. The surface a symbol or
   * interval change disposes stays in that fan-out, so hovering any chart maps
   * coordinates through a viewport that no longer exists — and the *other*
   * chart's crosshair stops moving and never clears, without an error.
   *
   * Pass one explicitly only to link charts on purpose. Note that crosshair sync
   * across charts is deliberately not a contract feature (see charts.md): it is
   * a pointer coordinate sixty times a second, not workspace data.
   */
  modifierGroup?: string;
  /** Gridline pattern for every axis in the group. Defaults to solid. */
  gridLines?: GridLineStyle;
  /** Axis drag and zoom direction. Defaults to drag on, zoom along X. */
  interactions?: InteractionPlan;
  /**
   * Fixed Y-axis label-area width (px) applied to every pane, so their plot
   * areas — and therefore bars — align to the pixel across panes. Must fit the
   * widest label; raise it for very large numbers. See
   * {@link DEFAULT_Y_AXIS_THICKNESS} for the measurement behind the default.
   */
  yAxisThickness?: number;
}

/**
 * The crosshair, plus the bar it is standing on.
 *
 * A subclass rather than a `pointermove` listener on the container, for the
 * reason `drawing-modifier.ts` gives at length: a modifier is inside SciChart's
 * own event ordering and reads the same `seriesViewRect` and coordinate
 * calculator the crosshair does, so the index reported is the bar the crosshair
 * is drawn on rather than one that agrees with it by coincidence.
 *
 * **It takes two flags, and either alone is wrong.** Panes share a parent
 * surface, and one pointer move reaches every pane's modifier by two different
 * routes with a different flag false on each:
 *
 * | route | `isMaster` | `isActiveSubChartEvent` |
 * |---|---|---|
 * | the pane the pointer is in | true | true |
 * | every other pane, dispatched directly | true | **false** |
 * | every other pane, fanned out by group | **false** | true |
 *
 * `updateSubCharts` walks all sub-charts and dispatches to each, spreading the
 * original args — so `isMaster` survives as `true` on panes the pointer is
 * nowhere near — and marks them `isActiveSubChartEvent: false`. Then
 * `processSubChartEvent` fans the *active* pane's event out through the modifier
 * group, and `copyForSubChart` stamps those `isMaster: false` while leaving
 * `isActiveSubChartEvent` alone.
 *
 * Checking only `isMaster` was the first version and is why the legend appeared
 * only over the bottom pane: every pane reported, the last dispatched won, and
 * its `translateFromCanvasToSeriesViewRect` returned nothing for a pointer
 * outside its own rect — so hovering anywhere else reported `null`.
 */
/**
 * The axis drag, reporting that a person scaled the Y axis by hand.
 *
 * The fit runs on every pan, zoom and push, so a dragged range lasted until the
 * next tick (owner, 2026-10-16). The report puts the pane in manual, which the
 * fit skips — see `SciChartJsChartBuilder.setYManual`.
 */
class ManualYAxisDragModifier extends YAxisDragModifier {
  /** Set by the builder. */
  scaled: (() => void) | null = null;

  protected override doScaling(pointTo: Parameters<YAxisDragModifier["doScaling"]>[0]): void {
    super.doScaling(pointTo);
    this.scaled?.();
  }
}

/** The wheel zoom, reporting a Y zoom for the same reason — X alone is not one. */
class ManualWheelZoomModifier extends MouseWheelZoomModifier {
  scaled: (() => void) | null = null;

  override modifierMouseWheel(args: ModifierMouseArgs): void {
    super.modifierMouseWheel(args);
    if (this.isEnabled && this.xyDirection !== EXyDirection.XDirection) this.scaled?.();
  }
}

class HoverCursorModifier extends CursorModifier {
  /** Set by the builder. Null while nobody is listening, which is the default. */
  report: ((time: number | null) => void) | null = null;
  /**
   * Set by the builder: which line the pointer is on. Handed the pane, the
   * snapped bar's time and the pointer's canvas point, or `null`s when the
   * pointer is on no bar. See {@link SciChartJsChartBuilder.onHoverPlot}.
   */
  probe: ((surface: SciChartSubSurface | null, time: number | null, point: Point | null) => void) | null =
    null;

  constructor(options: ICursorModifierOptions) {
    super(options);
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!args.isMaster || !args.isActiveSubChartEvent) return;
    this.report?.(this.barAt(args));
    if (this.probe) {
      const surface = this.parentSurface as SciChartSubSurface | undefined;
      const point = args.mousePoint;
      const snap = point ? this.snapped(point) : undefined;
      // The crosshair's point: the snapped bar's x, the pointer's y.
      if (surface && point && snap) this.probe(surface, snap.time, new Point(snap.x, point.y));
      else this.probe(null, null, null);
    }
  }

  /**
   * **Snap the crosshair to the nearest bar** before SciChart places it (owner,
   * 2026-10-05: always). `update()` draws both lines and the X axis label from
   * `this.mousePoint`, so moving that point's x onto the bar's pixel moves the
   * vertical line *and* the date under it together; the horizontal line keeps
   * the pointer's own y, since it reads a price rather than a bar.
   *
   * Each pane snaps from its own series, so the fanned-out move to a lower pane
   * lands on the same bar the price pane chose — they share the X axis range.
   */
  protected override update(): void {
    const point = this.mousePoint;
    if (point) {
      const x = this.snapped(point)?.x;
      if (x !== undefined && x !== point.x) this.mousePoint = new Point(x, point.y);
    }
    super.update();
  }

  /**
   * The bar nearest `point`: its time, and its canvas x. `undefined` off the
   * plot or with no bars.
   */
  private snapped(point: Point): { x: number; time: number } | undefined {
    const surface = this.parentSurface as SciChartSubSurface | undefined;
    if (!surface) return undefined;
    const rect = surface.seriesViewRect;
    const local = translateFromCanvasToSeriesViewRect(point, rect);
    if (!local) return undefined;
    const calc = surface.xAxes.get(0)?.getCurrentCoordinateCalculator();
    if (!calc) return undefined;

    // The longest series holds every bar the axis shows, a projection's
    // forward bars included; the price series usually is it.
    let xs: { get(i: number): number; size(): number } | undefined;
    for (const rs of surface.renderableSeries.asArray()) {
      const values = rs.dataSeries?.getNativeXValues();
      if (values && values.size() > (xs?.size() ?? 0)) xs = values;
    }
    if (!xs) return undefined;

    // Outside the bars — the right margin every chart keeps — the line stays
    // free, as `indexAtTime` gives the values readout no bar there either.
    // Clamped, the line would sit on the last bar while the readout went blank.
    const pointer = calc.getDataValue(local.x);
    if (pointer < xs.get(0) || pointer > xs.get(xs.size() - 1)) return undefined;
    const time = nearestBarTime((i) => xs!.get(i), xs.size(), pointer);
    if (time === undefined) return undefined;
    // `local` is relative to the series rect; the point SciChart reads is canvas.
    return { x: point.x + (calc.getCoordinate(time) - local.x), time };
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    this.probe?.(null, null, null);
    // Unconditional, and not gated on `isMaster`: a leave arrives at the pane
    // the pointer is departing, which by then is nobody's master. Gating it
    // would leave the last reading frozen on screen after the pointer is gone.
    this.report?.(null);
  }

  /**
   * The **time** under the pointer, or `null` off the plot area.
   *
   * It reported a rounded bar index while the axis was a `CategoryAxis`, where a
   * data value was a fractional index and rounding was what "which bar" meant.
   * On a `DiscontinuousDateAxis` the data value is a timestamp, and rounding one
   * to the nearest second is meaningless — so the raw value goes out and
   * whoever holds the bar times does the lookup. That is the host, which already
   * has them.
   *
   * `null` rather than a clamp: over an axis, or before the calculators exist on
   * the first frame, there is no honest answer and the nearest edge is a reading
   * of a bar nobody pointed at.
   */
  private barAt(args: ModifierMouseArgs): number | null {
    const surface = this.parentSurface as SciChartSubSurface | undefined;
    const point = args.mousePoint;
    if (!surface || !point) return null;
    const local = translateFromCanvasToSeriesViewRect(point, surface.seriesViewRect);
    if (!local) return null;
    const calc = surface.xAxes.get(0)?.getCurrentCoordinateCalculator();
    if (!calc) return null;
    const time = calc.getDataValue(local.x);
    return Number.isFinite(time) ? time : null;
  }
}

/**
 * The axis every pane-anchored signal in a pane shares.
 *
 * One axis rather than one per signal: lanes are already numbered across the
 * pane, so they share a coordinate space by construction, and an axis each would
 * be N invisible axes competing for the same strip of pane.
 */
const SIGNAL_AXIS_ID = "qf-signal-lanes";

/**
 * Prefix for the axis a **bar-anchored** signal actually draws on, one per
 * anchor axis (`qf-signal-mirror:<anchorAxisId>`).
 *
 * A mirror rather than the anchor's own axis, and this is not tidiness — it is
 * the only thing that makes the offset safe. A bar-anchored marker sits
 * *outside* its bar, below the low or above the high, and the price axis is
 * `EAutoRange.Always`. Put the markers on it and they widen the range they were
 * measured against; the offset is a fraction of that range, so it grows; so the
 * markers move further out; so the range widens again. The chart zooms itself
 * out a percent and a half at a time, forever.
 *
 * The mirror is `EAutoRange.Never` and is handed the anchor's range whenever it
 * changes, so the markers live in the anchor's coordinates and contribute
 * nothing back to them. The loop is not damped — it cannot form.
 *
 * `isVisible: false` here, unlike the lane axis: a hidden axis is given no
 * width, which is exactly right when a *visible* axis already insets the pane.
 */
const SIGNAL_MIRROR_PREFIX = "qf-signal-mirror:";

/**
 * A down-pointing triangle, which SciChart does not ship.
 *
 * The available markers are Triangle (up only), Ellipse, Square, Cross and X.
 * Pairing a triangle with an X would say "up" and "bad" rather than "up" and
 * "down", so the up triangle is drawn rotated instead — direction has to be
 * legible without colour, for a reader who cannot distinguish the two and for a
 * chart that gets printed.
 */
class DownTrianglePointMarker extends TrianglePointMarker {
  override drawSprite(
    context: CanvasRenderingContext2D,
    spriteWidth: number,
    spriteHeight: number,
    stroke: string,
    strokeThickness: number,
    fill: string,
  ): void {
    // **About the canvas centre, not the sprite box centre.** They are not the
    // same point: SciChart allocates the sprite texture padded for the stroke
    // (`PIXEL_RATIO * (height + strokeThickness) + 1`) and then draws into it
    // centred on `context.canvas.height / 2` — so rotating about
    // `spriteHeight / 2` is a rotation about a point the shape is not centred
    // on, and a π rotation about the wrong centre *translates* by twice the
    // error. At a 2× device pixel ratio that put the down triangle four pixels
    // above the up one on a nine-pixel marker, which reads as the two
    // directions sitting on different values — the one thing a shared lane must
    // never look like.
    //
    // It also has to be read off the context rather than computed, because the
    // same `drawSprite` is called three times with three different sprite sizes
    // (the fill mask is inset by 3px) against canvases of one size.
    const { width, height } = context.canvas;
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(Math.PI);
    context.translate(-width / 2, -height / 2);
    super.drawSprite(context, spriteWidth, spriteHeight, stroke, strokeThickness, fill);
    context.restore();
  }
}

/**
 * A square turned 45 degrees, which SciChart does not ship either.
 *
 * Rotated about the canvas centre for the reason {@link DownTrianglePointMarker}
 * gives: the sprite box and the canvas are not centred on the same point.
 */
class DiamondPointMarker extends SquarePointMarker {
  override drawSprite(
    context: CanvasRenderingContext2D,
    spriteWidth: number,
    spriteHeight: number,
    stroke: string,
    strokeThickness: number,
    fill: string,
  ): void {
    const { width, height } = context.canvas;
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(Math.PI / 4);
    // A rotated square's corners reach sqrt(2) further out; shrink it back so
    // a diamond reads the same size as the circle beside it.
    context.scale(Math.SQRT1_2, Math.SQRT1_2);
    context.translate(-width / 2, -height / 2);
    super.drawSprite(context, spriteWidth, spriteHeight, stroke, strokeThickness, fill);
    context.restore();
  }
}

/**
 * An arrow — a head on a shaft — pointing up; `markerShape: "arrowUp"`.
 *
 * SciChart ships no arrow. Drawn as one closed path about the canvas centre,
 * for the reason {@link DownTrianglePointMarker} gives: the sprite box and the
 * canvas are not centred on the same point. The head is the top 55% at full
 * width, the shaft a third of the width. Owner, 2026-10-14 (the Titanic mark).
 */
class ArrowUpPointMarker extends TrianglePointMarker {
  override drawSprite(
    context: CanvasRenderingContext2D,
    spriteWidth: number,
    spriteHeight: number,
    stroke: string,
    strokeThickness: number,
    fill: string,
  ): void {
    const cx = context.canvas.width / 2;
    const cy = context.canvas.height / 2;
    const s = strokeThickness / 2;
    const hw = spriteWidth / 2 - s;
    const hh = spriteHeight / 2 - s;
    const top = cy - hh;
    const bottom = cy + hh;
    const neck = top + 2 * hh * 0.55;
    const shaft = Math.max(1, hw / 3);
    context.fillStyle = fill;
    context.beginPath();
    context.moveTo(cx, top);
    context.lineTo(cx + hw, neck);
    context.lineTo(cx + shaft, neck);
    context.lineTo(cx + shaft, bottom);
    context.lineTo(cx - shaft, bottom);
    context.lineTo(cx - shaft, neck);
    context.lineTo(cx - hw, neck);
    context.closePath();
    context.fill();
    if (strokeThickness > 0) {
      context.strokeStyle = stroke;
      context.lineWidth = strokeThickness;
      context.stroke();
    }
  }
}

/** {@link ArrowUpPointMarker} turned about the canvas centre; `markerShape: "arrowDown"`. */
class ArrowDownPointMarker extends ArrowUpPointMarker {
  override drawSprite(
    context: CanvasRenderingContext2D,
    spriteWidth: number,
    spriteHeight: number,
    stroke: string,
    strokeThickness: number,
    fill: string,
  ): void {
    const { width, height } = context.canvas;
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(Math.PI);
    context.translate(-width / 2, -height / 2);
    super.drawSprite(context, spriteWidth, spriteHeight, stroke, strokeThickness, fill);
    context.restore();
  }
}

/**
 * A short horizontal bar — `markerShape: "line"`.
 *
 * Full marker width and a quarter of its height, so markers on neighbouring
 * bars touch and a run of firings reads as one continuous strip: the reading a
 * state (EMS, Squeeze On) wants, where a row of dots reads as separate events.
 */
class LinePointMarker extends SquarePointMarker {
  override drawSprite(
    context: CanvasRenderingContext2D,
    spriteWidth: number,
    spriteHeight: number,
    stroke: string,
    strokeThickness: number,
    fill: string,
  ): void {
    const { width, height } = context.canvas;
    context.save();
    context.translate(width / 2, height / 2);
    context.scale(1, 0.25);
    context.translate(-width / 2, -height / 2);
    super.drawSprite(context, spriteWidth, spriteHeight, stroke, strokeThickness, fill);
    context.restore();
  }
}

/** Constructor shape shared by every SciChart point marker used below. */
type SignalMarkerCtor = new (
  wasmContext: TSciChart,
  options: {
    width: number;
    height: number;
    fill: string;
    stroke: string;
    strokeThickness: number;
  },
) => IPointMarker;

/**
 * The glyph pair for each `PlotStyle.markerShape`. Only `triangle` differs
 * between its two halves — every other shape is symmetric, so direction is the
 * up/down colour alone. The default is `circle` (`DEFAULT_MARKER_SHAPE`), by the
 * owner's choice; it used to be a constant here, triangle, with no control.
 */
const SIGNAL_MARKERS: Record<Exclude<GlyphShape, "text">, { up: SignalMarkerCtor; down: SignalMarkerCtor }> = {
  circle: { up: EllipsePointMarker, down: EllipsePointMarker },
  triangle: { up: TrianglePointMarker, down: DownTrianglePointMarker },
  // Both halves down: a signal row that should only ever point down, and the
  // sell side of a scatter pair.
  triangleDown: { up: DownTrianglePointMarker, down: DownTrianglePointMarker },
  // One way each, whatever the bar's sign — like `triangleDown`.
  arrowUp: { up: ArrowUpPointMarker, down: ArrowUpPointMarker },
  arrowDown: { up: ArrowDownPointMarker, down: ArrowDownPointMarker },
  square: { up: SquarePointMarker, down: SquarePointMarker },
  diamond: { up: DiamondPointMarker, down: DiamondPointMarker },
  cross: { up: CrossPointMarker, down: CrossPointMarker },
  x: { up: XPointMarker, down: XPointMarker },
  line: { up: LinePointMarker, down: LinePointMarker },
};

/** What a `text` marker reads when its plot names nothing. */
const DEFAULT_MARKER_TEXT = "TXT";
/** A `text` marker's type size when its plot names none: the `E` glyph's 11 px. */
const TEXT_MARKER_PX = 11;
const TEXT_MARKER_FONT = "Arial, sans-serif";

let textMeasure: CanvasRenderingContext2D | null | undefined;

/** The drawn width of `text` in bold at `px`, or an estimate where no canvas exists. */
function textWidth(text: string, px: number): number {
  if (textMeasure === undefined) {
    textMeasure = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  }
  if (!textMeasure) return text.length * px * 0.65;
  textMeasure.font = `bold ${px}px ${TEXT_MARKER_FONT}`;
  return textMeasure.measureText(text).width;
}

/**
 * A point marker that is a word — `markerShape: "text"`, `FTD` under a bar
 * (owner, 2026-10-28). A class per text, closing over it: SciChart may draw the
 * sprite from inside the base constructor, before any field of a subclass is
 * set, so the text cannot be an instance property. Drawn in the fill colour
 * with no outline; the sprite is as wide as the word (`markerBox`).
 */
function textMarkerKind(text: string): SignalMarkerCtor {
  return class TextPointMarker extends SquarePointMarker {
    override drawSprite(
      context: CanvasRenderingContext2D,
      _spriteWidth: number,
      spriteHeight: number,
      _stroke: string,
      _strokeThickness: number,
      fill: string,
    ): void {
      const { width, height } = context.canvas;
      context.save();
      context.fillStyle = fill;
      context.font = `bold ${Math.max(6, Math.round(spriteHeight * 0.85))}px ${TEXT_MARKER_FONT}`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(text, width / 2, height / 2);
      context.restore();
    }
  };
}

/** The marker class for a glyph and half; a `text` one is built for its words. */
function markerKind(shape: GlyphShape, half: SignalDirection, text: string | undefined): SignalMarkerCtor {
  return shape === "text" ? textMarkerKind(text || DEFAULT_MARKER_TEXT) : SIGNAL_MARKERS[shape][half];
}

/**
 * A marker's box in px. A shape is square at `size` (the type's default when
 * unset); a word is `size` tall — its type size — and as wide as it reads.
 */
function markerBox(
  shape: MarkerShape,
  size: number | undefined,
  fallback: number,
  text: string | undefined,
): { width: number; height: number } {
  if (shape !== "text") return { width: size ?? fallback, height: size ?? fallback };
  const px = size ?? TEXT_MARKER_PX;
  return { width: Math.ceil(textWidth(text || DEFAULT_MARKER_TEXT, px)) + 2, height: px + 2 };
}

/**
 * A signal `column`'s width as a fraction of one bar's slot — the gap between
 * neighbouring columns is the rest. Wider than a volume bar's 0.7: a run of
 * firing bars should read as one band with hairline seams, the TC2000 strip.
 */
const SIGNAL_COLUMN_WIDTH = 0.85;

/** One firing bar, kept so a range change can re-place it without re-deriving. */
interface SignalMark {
  /** Category-axis x, as the data arrived with it. */
  x: number;
  /** Bar index into the group's columns — how the anchor's value is found. */
  index: number;
  direction: SignalDirection;
}

/** The second half of a signal plot: one plot draws as two series, split by sign. */
interface SignalRender {
  lane: number;
  /** `pane` or `series`, as planned — decides whether a `column` is drawable. */
  anchor: "pane" | "series";
  /**
   * `single` or `dual`, as planned from the catalog. Held because the catalog
   * can arrive after the data: `refreshReferenceLines` re-plans then, and a
   * changed kind re-reads every mark from `values`.
   */
  eventKind: EventKind;
  /**
   * The shape the two halves were built with — already through
   * `effectiveSignalShape`, so a bar-anchored `column` is recorded as the
   * `square` it draws. A restyle compares against it to tell a repaint from a
   * marker swap, and a swap to or from `column` from both: that one changes the
   * renderable class.
   */
  markerShape: MarkerShape;
  /** The words a `text` marker was built with, so retyping them swaps the marker. */
  markerText?: string;
  /** `PlotRender.series` / `.dataSeries` are the up half; these are the down. */
  series: IRenderableSeries;
  dataSeries: XyDataSeries;
  /**
   * The plot this rides, for `anchor: "series"`. Absent means a pane lane.
   *
   * Held as an id rather than a reference because the anchor may not exist yet
   * when this is built — plots are created in order and a signal can precede the
   * thing it hangs off.
   */
  anchorPlotId?: string;
  /**
   * Every fired bar. A bar-anchored signal is re-placed whenever its axis'
   * range moves, and re-deriving from the column each time would mean keeping
   * the column *and* walking it per frame of a pan.
   */
  marks: SignalMark[];
  /**
   * Every bar's x, fired or not — the spine the two data series are written
   * against.
   *
   * Held because both series must be **dense**: see {@link placeSignal}. The
   * marks alone cannot rebuild them, since a bar that did not fire still needs
   * a slot.
   */
  xs: number[];
}

/** One level's fill: a band from the axis's line to the level. */
interface LevelFillRender {
  series: FastBandRenderableSeries;
  data: XyyDataSeries;
  axisId: string | undefined;
  /** The level as drawn — through `toSignedLog` on a signed-log axis. */
  level: number;
}

/** Per-plot render state, keyed within a pane. */
interface PlotRender {
  plotId: Ulid;
  /** The glyph a `scatter` was built with; absent means the default. */
  markerShape?: MarkerShape;
  /** The words a `text` scatter was built with. */
  markerText?: string;
  alias?: string;
  series: IRenderableSeries;
  dataSeries: AnyDataSeries;
  dataKind: SeriesPlan["dataKind"];
  /** Kept so a re-theme knows which color properties this series actually has. */
  seriesKind: SeriesPlan["seriesKind"];
  palette?: PlanPaletteProvider;
  /** Present on a candlestick while anything recolours it — see {@link repaintCandles}. */
  candlePalette?: CandlePaletteProvider;
  /** A candlestick's rule and colours, kept so a paint arriving later can repaint it. */
  candleStyle?: "standard" | "hollow";
  candleArgb?: CandleArgb;
  /**
   * Present on a `paint` plot (HCD/LCD): its two body colours. Such a plot's
   * series is a placeholder that is **never visible** — the values colour the
   * price candles instead — so it is skipped by the Y fit and has no marker.
   */
  paint?: { up: number; down: number };
  /**
   * The last value, printed on this series' own Y axis.
   *
   * Every series gets one, not just price: an indicator's current reading is the
   * number you actually want, and reading it off gridlines is guesswork. Coloured
   * to match the series, because several markers on one axis are otherwise
   * unattributable.
   */
  marker?: AxisMarkerAnnotation;
  /**
   * A dotted line across the pane at the last close — price (OHLC) plots only,
   * so the close can be read against bars far to the left of it. Moves with
   * the marker; hidden with the plot.
   */
  closeLine?: HorizontalLineAnnotation;
  /**
   * Whether this plot is currently hidden.
   *
   * Kept here rather than read back off `series.isVisible`, because the marker
   * is created **lazily on the first bar** — so a plot hidden in a saved
   * workspace had no marker to hide at build time, and gained a visible one the
   * moment data arrived: a price label on the axis for a series that is not
   * drawn, which on a crowded axis reads as belonging to whatever is nearest.
   */
  hidden?: boolean;
  /** Tracked y-values (drives palette recompute; also lets us patch the last bar). */
  values: number[];
  /**
   * Bar times, for an `ohlc` plot only, index-aligned with `values`.
   *
   * Only Volume-by-Price's `visible` period reads them, to turn the axis' time
   * window into a bar range. Held here rather than read back off the DataSeries
   * because nothing else in this file reaches into WASM for x values, and a
   * second way of getting the same numbers is a second way to get them wrong.
   */
  xs?: number[];
  /**
   * The bar extremes, for an `ohlc` plot only — what a bar-anchored signal reads
   * to sit under a candle's low or over its high.
   *
   * `values` already holds the close, which is the wrong number for this: a
   * marker at the close sits inside the candle body rather than clear of it.
   */
  lows?: number[];
  highs?: number[];
  /** Present for `type: "signal"` only — see {@link SignalRender}. */
  signal?: SignalRender;
}

/**
 * The bar a marker hangs off, as `{low, high}`.
 *
 * An OHLC plot has real extremes; anything else has one value per bar, so both
 * collapse to it and the direction only picks a side. That collapse is what lets
 * a signal ride a MACD line with exactly the same code as a candlestick.
 */
function barExtremes(anchor: PlotRender, index: number): { low: number; high: number } {
  const low = anchor.lows?.[index];
  const high = anchor.highs?.[index];
  if (low !== undefined && high !== undefined) return { low, high };
  const value = anchor.values[index] ?? NaN;
  return { low: value, high: value };
}

/** Per-pane render state — one SubChart sub-surface. */
interface PaneRender {
  sub: SciChartSubSurface;
  byId: Map<Ulid, PlotRender>;
  /**
   * Alias → **every** plot fed by it, not one.
   *
   * An alias names a *column*, and more than one plot can read the same column:
   * a catalog band is drawn under the lines it spans, so Bollinger's fill and
   * its upper edge both feed from `bbandsUpper_20_2`, and Chandelier's fill and
   * its long stop both feed from `ceLong_22_3_1`. While this was a
   * `Map<string, PlotRender>` the second registration replaced the first, so the
   * fill — always registered first, since it is ordered *below* its lines — was
   * silently dropped and never received a bar. It rendered as nothing at all,
   * which reads as "bands are not implemented" rather than as a bug.
   */
  byAlias: Map<string, PlotRender[]>;
  /**
   * The modifiers whose behaviour is a *setting*, retained so changing one is a
   * property write rather than a rebuild — the same bargain as `applyTheme` and
   * `setGridLines`. They are created once and always added; turning axis drag
   * off flips `isEnabled` rather than removing and re-adding, because a modifier
   * removed mid-gesture leaves the surface holding a pointer capture nobody owns.
   */
  interaction: {
    zoomPan: ZoomPanModifier;
    wheelZoom: ManualWheelZoomModifier;
    xAxisDrag: XAxisDragModifier;
    yAxisDrag: ManualYAxisDragModifier;
  };
}

/**
 * One PaletteProvider for the solid/paired coloring tiers. Colors are computed
 * by the pure {@link resolveBarColorsArgb} and refreshed whenever data changes.
 */
class PlanPaletteProvider extends DefaultPaletteProvider {
  private colors: number[] = [];
  private readonly fallback: number;

  constructor(private readonly plan: PalettePlan) {
    super();
    this.strokePaletteMode = EStrokePaletteMode.SOLID;
    this.fillPaletteMode = EFillPaletteMode.SOLID;
    this.fallback =
      plan.kind === "solid"
        ? parseColorToArgb(plan.color)
        : plan.kind === "paired"
          ? parseColorToArgb(plan.up)
          : 0xffffffff;
  }

  setValues(values: readonly number[]): void {
    this.colors = resolveBarColorsArgb(values, this.plan);
  }

  override overrideFillArgb(_x: number, _y: number, index: number): number {
    return this.colors[index] ?? this.fallback;
  }

  override overrideStrokeArgb(_x: number, _y: number, index: number): number {
    return this.colors[index] ?? this.fallback;
  }
}

/**
 * Hollow candles (`PlotPalette.candleStyle: "hollow"`) — the per-bar outline
 * and body from the pure {@link hollowCandleColors}, recomputed from the
 * candlestick's own open and close whenever its bars change.
 *
 * Read off the DataSeries rather than kept beside it: `PlotRender.values` holds
 * the close only, and the rule needs the open too. A refresh is O(bars) and
 * runs on a data update, never per frame.
 */
class CandlePaletteProvider extends DefaultPaletteProvider {
  private colors: CandleColors = { stroke: [], fill: [] };

  constructor(private readonly argb: CandleArgb) {
    super();
    this.strokePaletteMode = EStrokePaletteMode.SOLID;
    this.fillPaletteMode = EFillPaletteMode.SOLID;
  }

  refresh(ds: OhlcDataSeries, style: "standard" | "hollow", paints: readonly CandlePaint[]): void {
    const open = ds.getNativeOpenValues();
    const close = ds.getNativeCloseValues();
    this.colors = candleColors((i) => open.get(i), (i) => close.get(i), ds.count(), this.argb, style, paints);
  }

  override overrideFillArgb(_x: number, _y: number, index: number): number | undefined {
    return this.colors.fill[index];
  }

  override overrideStrokeArgb(_x: number, _y: number, index: number): number | undefined {
    return this.colors.stroke[index];
  }
}

/**
 * Ticks for a signed log axis: zero and the decades, from the pure
 * {@link signedLogTicks}. No minor ticks — the decades are the scale.
 */
class SignedLogTickProvider extends NumericTickProvider {
  override getMajorTicks(_minorDelta: number, _majorDelta: number, visibleRange: NumberRange): number[] {
    return signedLogTicks(visibleRange.min, visibleRange.max);
  }

  override getMinorTicks(): number[] {
    return [];
  }
}

/** The candle colours as ARGB, bodies at the same alpha the standard brushes use. */
function candleArgb(up: string, down: string): CandleArgb {
  return {
    up: parseColorToArgb(up),
    down: parseColorToArgb(down),
    upFill: withOpacityArgb(up, 0xaa / 0xff),
    downFill: withOpacityArgb(down, 0xaa / 0xff),
  };
}

/**
 * The Y label area every pane is pinned to, in CSS pixels.
 *
 * **A floor, not a width.** SciChart takes
 * `max(measuredLabels, axisThickness × dpr) + tickSize`, so a pane whose labels
 * are wider grows past it — and a pane that grows alone is a pane whose bars no
 * longer line up with the ones above. That is the whole reason the number is
 * shared rather than left to each axis.
 *
 * **50, the tightest value that still clears every label.** `12345.67` measures
 * 42px at the default 10px font, and the tick adds 7 — so the floor has to be
 * at least 49 or the widest-labelled pane grows past it alone.
 *
 * This was 5 for one revision, chosen as the tightest possible right edge and
 * accepting the cost named above. **The cost arrived**: on a five-pane chart
 * every plot area had the same left edge and five different widths — 1996,
 * 2016, 2016, 2041, 2055 — because each axis had sized to its own text, from a
 * label-less lane axis at nearly nothing to a price axis at 59 device px more.
 * Bars stretch by the difference, so nothing lines up between panes and the
 * error is worst at the newest bar, which is the one being read. That is the
 * same failure the signal strip had at 7px, reached deliberately rather than by
 * accident, and it is not worth 45px of margin.
 *
 * **Going below this needs a measured width, not a smaller guess.** Every pane
 * would have to be built with one thickness computed from the widest label
 * across all of them — which means measuring text before the axes exist, and
 * rebuilding when a zoom changes the digit count. Worth doing; not a constant.
 */
const DEFAULT_Y_AXIS_THICKNESS = 50;

/**
 * Padding above and below each pane's plot, in CSS px — half the seam.
 *
 * SciChart's own default is 10, which put **20px of dead band between two
 * adjacent panes** (one's bottom plus the next one's top) and inset the top and
 * bottom of the stack by 10 more. Set to 3 by the owner, 2026-09-19: the seams
 * are where a multi-pane chart spends its height, and six pixels is enough to
 * keep a candle off the divider.
 *
 * It is not the whole of the whitespace a reader sees at the top and bottom of a
 * pane — `Y_FIT_PADDING` in `plan/y-fit.ts` keeps a further 5% of the *data's*
 * extent clear at either end, which is the part that scales with the pane
 * rather than with the screen.
 */
const PANE_PADDING_Y = 3;

/** Space past the Y axis labels at each pane's right edge, in CSS px. */
const PANE_PADDING_RIGHT = 2;

/**
 * How many times {@link SciChartJsChartBuilder.equalizeYAxisWidths} may re-run
 * before giving up on this fit.
 *
 * Three is generous for a job that normally finishes in one and occasionally
 * needs two — the second pass exists for a pane whose labels were already wider
 * than the thickness it was handed. The cap is not for the arithmetic, which
 * converges; it is so a container being resized under us cannot keep scheduling
 * frames forever.
 */
const MAX_EQUALIZE_PASSES = 3;

export class SciChartJsChartBuilder implements ChartBuilder {
  /** Mutable: a theme switch restyles in place (D13), so this is not readonly. */
  private theme: Theme;
  private readonly modifierGroup: string;
  /**
   * Every pane's crosshair, so the hover handler can reach all of them.
   *
   * A list rather than one, because only the pane the pointer is genuinely in
   * reports (see {@link HoverCursorModifier}) — so a handler on one modifier is
   * a legend that answers for one pane and goes silent over the others.
   */
  private readonly hoverCursors: HoverCursorModifier[] = [];
  /**
   * Held as well as pushed, because panes are built one at a time and the host
   * subscribes once. A modifier created after the call would otherwise never be
   * told, which on a two-pane chart is the volume pane silently not reporting.
   */
  private hoverHandler: ((time: number | null) => void) | null = null;
  /** Told which line the pointer is on — {@link onHoverPlot}. */
  private plotHoverHandler: ((hit: PlotHit | null) => void) | null = null;
  /**
   * What the last hit test over a pane considered, for `__qfHit()` in the
   * owner's browser (`apps/web/src/lib/chart-debug.ts`). Overwritten per move
   * over a pane and kept when the pointer leaves, so it can be read from the
   * console afterwards. Debug only; nothing reads it.
   */
  lastHitProbe: unknown = null;
  /** The line currently drawn +1 wide, and the width it had before. */
  private emphasised: { pr: PlotRender; base: number } | null = null;
  private readonly yAxisThickness: number;
  /**
   * What the price axis is scaled to — see {@link setPriceFit}.
   *
   * Mutable and not readonly, because changing it is a re-range of axes that
   * already exist rather than a rebuild, the same bargain as the theme.
   */
  private priceFit: PriceFit = "all";
  /** The pending {@link scheduleAxisEqualize} frame, so a burst coalesces to one. */
  private equalizePending: number | undefined;
  /** Passes spent on the current convergence, reset by each new fit. */
  private equalizePasses = 0;

  private readonly plans: Map<Ulid, PanePlan> = new Map();
  private readonly panes: Map<Ulid, PaneRender> = new Map();
  /**
   * Y axes {@link fitYAxes} must not touch — the ones the contract pinned with
   * `autoRange: "never"`/`"once"` or its own `visibleRange`. Held by axis id
   * because the fit walks SciChart's axes, which carry the id and not the plan.
   */
  private readonly pinnedYAxes: Set<string> = new Set();
  /**
   * Y axes on the signed log scale (`plan/signed-log.ts`). Every y written onto
   * one goes through `toSignedLog` first — data, patches, reference lines — and
   * its labels invert it. Held by axis id for `pinnedYAxes`' reason.
   */
  private readonly signedLogAxes: Set<string> = new Set();
  /**
   * Y axes a person scaled by hand — {@link fitYAxes} skips them until
   * {@link setYManual}`(false)`. Not saved, and gone with the builder on a
   * rebuild: a new symbol has a different price range (owner, 2026-10-16).
   */
  private readonly manualYAxes: Set<string> = new Set();
  private yManualHandler: ((manual: boolean) => void) | null = null;
  /**
   * The panes as the model gave them, kept so {@link applyTheme} can re-resolve
   * their colors. `plans` holds colors already resolved against the *old* theme,
   * so it cannot be re-resolved from — the model can.
   */
  private paneModels: Pane[] = [];

  /**
   * The company events along the foot of the price pane, and what was drawn for
   * them.
   *
   * Held apart from `annotationsByPane` on purpose. That registry carries the
   * selection across a re-render, guards what may be written back into the
   * contract, and reports drags — none of which applies to a glyph nobody drew,
   * nobody may move, and nothing persists.
   */
  private markers: readonly EventMarker[] = [];
  private markerAnnotations: unknown[] = [];
  /** Which glyph is which, for the hover callback. Rebuilt with the glyphs. */
  private markersByAnnotation = new Map<unknown, EventMarker>();
  /** The notes above the glyphs, with their bar, for `layoutMarkerNotes`. */
  private markerNotes: { annotation: TextAnnotation; time: number }[] = [];
  /** The glyphs and notes of `pinned` markers, re-placed by `layoutPinnedMarkers`. */
  private pinnedAnnotations: { annotation: TextAnnotation; time: number; text: string }[] = [];
  private markerHover:
    | ((marker: EventMarker | null, at: { x: number; y: number } | null) => void)
    | null = null;
  private markerHoverModifier: unknown = null;
  /** Derived drawings that carry popup lines, and those lines. Rebuilt with them. */
  private readonly derivedHoverLines = new Map<unknown, readonly string[]>();
  private derivedHover:
    | ((lines: readonly string[] | null, at: { x: number; y: number } | null) => void)
    | null = null;
  private derivedHoverModifier: unknown = null;

  /**
   * The boxes drawn for each Volume-by-Price plot — **created once, then written
   * to**, never rebuilt.
   *
   * Two per zone, and the set only grows: it is allocated up to the zone count
   * the current plan needs and never shrinks back. That is what lets every
   * repaint be a property write — the zones are recomputed whenever the bars
   * change, which on a live chart is every tick, and with ATR sizing the *count*
   * can change too, on any pan of a `visible` period. Allocating and freeing
   * WebGL annotations per tick to move four numbers on each would be the one
   * shape this file spends its whole length avoiding. A surplus box is hidden by
   * the painter and waits.
   *
   * Held apart from `annotationsByPane` for `markerAnnotations`' reason: nobody
   * drew these, nobody may move them, and nothing persists them — so they must
   * not travel the registry that guards what is written back into the contract.
   */
  private readonly volumeByPriceBoxes = new Map<Ulid, BoxAnnotation[]>();

  /**
   * One text label per zone, holding that zone's up minus down volume.
   *
   * **A third map, and `clearPlacedDrawings` had to be told about it** — that
   * method identifies what is "ours" by exclusion, so an annotation this
   * builder adds and does not track is one it deletes on the next "remove every
   * drawing". Volume-by-Price's boxes already taught that lesson once; this is
   * the same shape and the doc above `clearPlacedDrawings` predicted it.
   *
   * Held apart from `annotationsByPane` for the boxes' reason: nobody drew
   * these, nobody may move them, and nothing persists them, so they must not
   * travel the registry that guards what is written back into the contract.
   *
   * Pooled and grown exactly as the boxes are, and for a sharper version of the
   * same reason — a label is an SVG node rather than a WebGL quad, so re-minting
   * sixty of them on a pan frame is DOM churn as well as allocation.
   */
  private readonly volumeByPriceLabels = new Map<Ulid, TextAnnotation[]>();

  /**
   * Every annotation this builder put on a surface itself — what
   * `clearPlacedDrawings` must never remove.
   *
   * **Recorded at the add, not gathered from the maps afterwards.** The clear
   * used to collect "ours" from each map that tracks annotations, and every
   * annotation added outside those maps was deleted as if the user had placed
   * it: the last-value axis markers, the close line, reference lines, the
   * extended-hours marker, the forming-bar band, the event glyphs. Four maps
   * later it was still missing six kinds. Every `annotations.add` in this file
   * goes through {@link own}, so a new kind is protected the day it is written.
   *
   * Weak because the builder never iterates it and must not keep a removed
   * annotation alive.
   */
  private readonly owned = new WeakSet<object>();

  private own<T>(annotation: T): T {
    this.owned.add(annotation as object);
    return annotation;
  }

  /**
   * The bar timestamps the current data carried, for placing annotations.
   *
   * A drawing is stored against a *time* and drawn at an *index* — see
   * `plan/annotation-x.ts` for why neither can be the other. The conversion
   * needs the loaded timestamps, and this is the only thing that has them.
   *
   * Kept rather than passed per call because a data load and an annotation
   * change arrive separately, and either one has to be able to re-place the
   * drawings on its own.
   */
  private barTimes: number[] = [];
  /**
   * How many of {@link barTimes} are real bars.
   *
   * See {@link setBarTimes}. Read only by the Y fit, which is about the price
   * and not about anything forecast beyond it.
   */
  private realBars = 0;
  /**
   * The wasm context, kept so the base-value series can be built after the fact.
   *
   * `buildGroup` is the only place it arrives, and the base values are not known
   * then — the panes exist before a single bar has been fetched.
   */
  private wasmContext?: TSciChart;
  /**
   * The bar timestamps as a `DataSeries`, which is the shape
   * `DiscontinuousDateAxis.setBaseXValues` takes.
   *
   * **One for the whole group, not one per pane.** Every pane draws the same
   * bars, and sub-surfaces share the parent's wasm context — so a series each
   * would be N copies of one array that must never disagree, and the way they
   * would disagree is a pane whose bars sit half a session off the pane above.
   */
  private baseXValues?: XyDataSeries;

  /** Rendered annotations by pane, so a repaint can remove exactly its own. */
  /**
   * What is drawn, with the id it was drawn for.
   *
   * The id has to travel with the object: SciChart's annotation carries no
   * notion of ours, so "which of my drawings is selected" is unanswerable
   * without this — and that is the question the Delete key asks.
   */
  private readonly annotationsByPane: Map<
    Ulid,
    { id: string; origin: string; annotation: unknown }[]
  > = new Map();

  /**
   * Every **derived** level drawn, and the price it sits at.
   *
   * Kept so a right-click can be answered without asking SciChart which
   * annotation was hit: its click routing is built for left-click selection and
   * a context menu must not depend on whether a right-press happens to travel
   * the same path. The prices are what a hit test needs anyway — a level is a
   * horizontal line, so "which one did I click" is one subtraction per level in
   * pixel space.
   */
  private readonly levelsByPane: Map<Ulid, { id: string; y: number }[]> = new Map();

  /** One per pane, attached at build and armed by {@link setDrawingTool}. */
  private readonly drawingModifiers: Map<Ulid, DrawingModifier> = new Map();

  /**
   * The financial-tools placement modifier, one per pane.
   *
   * Its own documentation calls `startPlacement` the cleanest approach for a
   * toolbar-driven app, and that is exactly this: created once, kept, and told
   * which of the twenty-six types to place when a tool is picked. Creating one
   * per drawing would mean constructing a modifier during the click that is
   * meant to start the gesture.
   */
  private readonly placementModifiers: Map<Ulid, MultiPointAnnotationPlacementModifier> =
    new Map();

  /**
   * Freehand's own modifier, one per pane.
   *
   * **A stroke is not a placement.** `MultiPointAnnotationPlacementModifier`
   * commits a point per click, which for a freehand annotation gives exactly two
   * points and draws a straight line — which is what it did. The library ships
   * `FreehandDrawingModifier` for this and its own docs say most applications
   * use it: it samples the pointer while the button is down, which is the
   * gesture a stroke actually is.
   */
  private readonly freehandModifiers: Map<Ulid, FreehandDrawingModifier> = new Map();

  /**
   * **Polled, because the library reports nothing.**
   *
   * `MultiPointAnnotationPlacementModifier` has no completion callback — no
   * event, no handler option, and `completePlacement` is private — so there is
   * no moment to subscribe to. The alternatives were worse: patching a private
   * method, or adding a modifier after it in the chain and inferring completion
   * from event order, both of which break silently on a version bump.
   *
   * The cost is bounded to the seconds a palette tool is armed: it starts on
   * arming, stops on the drawing it adopts, and never runs at all on a chart
   * nobody is drawing on. Only the built-in tools and pan are live otherwise.
   */
  private placementPoll: ReturnType<typeof setInterval> | null = null;

  /** Where a finished drawing is reported. Null until a host asks for them. */
  private drawnHandler: ((paneId: Ulid, annotation: Annotation) => void) | null = null;

  /** Where a dragged derived line reports its new price. */
  private editedHandler: ((paneId: Ulid, annotationId: string, y: number) => void) | null = null;

  /**
   * Told when the selection changes. Null until a host asks, so an annotation's
   * `selectedChanged` costs nothing on a chart nobody is editing.
   */
  private selectedHandler: ((ids: readonly Ulid[]) => void) | null = null;

  /** Told when a first point is placed, or taken back. @see onPlacingChanged */
  private placingHandler: ((placing: boolean) => void) | null = null;

  /**
   * The drawing just committed, to be selected when it comes back.
   *
   * A drawing is reported to the host, stored, and reaches the surface again
   * through `setAnnotations` as a *different object* — so "select what was just
   * drawn" cannot be done at the moment of drawing. The id is remembered instead
   * and claimed by the next render, which is the first moment the thing to select
   * exists.
   */
  private selectOnArrival: string | null = null;

  /** Where a drawn trade plan reports its two prices and where it was drawn. */
  private planHandler: ((entry: number, stop: number, at: number) => void) | null = null;

  /** What is armed, kept so a rebuild can restore it. */
  private drawingTool: AnnotationKind | "plan" | null = null;

  /** The rubber band under the pointer — one annotation, replaced per move. */
  private preview: { paneId: Ulid; annotation: unknown } | null = null;
  /** Mutable for the same reason as `theme`: changing it restyles, never rebuilds. */
  private gridLines: GridLineStyle = DEFAULT_GRID_LINES;
  private readonly axisSpecs?: AxisSpecLookup;
  private readonly eventKinds?: EventKindLookup;
  private readonly timeLabels: TimeAxisLabels;
  /** The pale band behind the bar still forming — see {@link setFormingBar}. */
  private formingBand: BoxAnnotation | null = null;
  private formingBar: { start: number; end: number } | null = null;
  /** The columns behind highlighted bars — see {@link setBarHighlights}. */
  private readonly barHighlightLayers = new Map<
    string,
    { list: BarHighlight[]; boxes: BoxAnnotation[] }
  >();
  /** Time-range backgrounds, per layer — see {@link setSpans}. */
  private readonly spanLayers = new Map<string, { list: TimeSpan[]; boxes: BoxAnnotation[] }>();
  /** The pre-/post-market price label on the price axis — see `setExtendedPrice`. */
  private extMarker: HorizontalLineAnnotation | null = null;
  /** Text appended to a plot's last-value label — the bar countdown. */
  private readonly labelSuffix = new Map<Ulid, string>();
  /** Told whenever the X window moves — a pan, a wheel zoom, or a window we set. */
  private barWindowListener: ((window: { min: number; max: number }) => void) | null = null;
  /** Reference-line annotations per pane, so a rebuild replaces rather than stacks. */
  private readonly referenceLines = new Map<string, HorizontalLineAnnotation[]>();
  /** Per pane, the bands that shade a line past a level — {@link syncLevelFills}. */
  private readonly levelFills = new Map<string, LevelFillRender[]>();
  /** Mutable for the same reason again — changing a modifier is a property write. */
  private interactions: InteractionPlan = resolveInteractions();
  private parentSurface?: SciChartSurface;
  private lastPaneId?: Ulid;
  /**
   * The host element, kept only to measure it.
   *
   * How many gridlines a pane should draw falls out of how tall it is rendered,
   * and that is a pixel count no model carries — so the builder measures and the
   * pure {@link maxMajorGridLines} decides.
   */
  private container?: HTMLElement;
  private resizeObserver?: ResizeObserver;

  constructor(options: SciChartBuilderOptions = {}) {
    this.theme = options.theme ?? DEFAULT_THEME;
    this.gridLines = options.gridLines ?? DEFAULT_GRID_LINES;
    this.interactions = options.interactions ?? resolveInteractions();
    // Per instance, not per app, and per *instance* rather than per chart group:
    // a rebuild mints a new builder, so the group it joins cannot be one the
    // surface it replaced is still a member of.
    this.modifierGroup = options.modifierGroup ?? `qufolio-${++modifierGroupSeq}`;
    this.yAxisThickness = options.yAxisThickness ?? DEFAULT_Y_AXIS_THICKNESS;
    this.axisSpecs = options.axisSpecs;
    this.eventKinds = options.eventKinds;
    this.timeLabels = options.timeLabels ?? DATE_LABELS;
  }

  /**
   * Be told when the X window moves, in axis units (bar times, epoch seconds).
   *
   * Fires through a drag, so a listener must be cheap or coalesce. `null`
   * unsubscribes. What an intraday chart uses to fetch older bars before the
   * reader reaches the first one.
   */
  onBarWindowChanged(listener: ((window: { min: number; max: number }) => void) | null): void {
    this.barWindowListener = listener;
  }

  /**
   * Build a chart group's panes into one container: a parent SciChartSurface
   * subdivided into a vertically-stacked SubChart per pane (heights from
   * pane.height). Feed data afterwards via {@link updateSeries}.
   */
  async buildGroup(
    panes: Pane[],
    container: HTMLDivElement,
    gridLines: GridLineStyle = this.gridLines,
    interactions: InteractionPlan = this.interactions,
    // **Passed in for `gridLines`' reason, not defaulted in the constructor.**
    // A rebuild makes a *new* builder — a symbol change is structural, so the
    // surface is thrown away and this class with it — and anything the host set
    // through a setter afterwards is gone with the old instance. The host's
    // effect for it is keyed on the value, which has not changed, so it does not
    // re-fire and the setting silently reverts while its menu still shows a tick
    // against it. Everything that must survive a rebuild arrives here.
    priceFit: PriceFit = this.priceFit,
    // Same rule, same reason: derived from a fetch rather than from the model,
    // so nothing in `panes` would put them back after a rebuild.
    markers: readonly EventMarker[] = this.markers,
  ): Promise<void> {
    this.gridLines = gridLines;
    this.interactions = interactions;
    this.priceFit = priceFit;
    this.markers = markers;
    const { sciChartSurface: parent, wasmContext } = await SciChartSurface.create(container, {
      theme: this.surfaceTheme(),
    });
    this.parentSurface = parent;
    this.wasmContext = wasmContext;
    this.container = container;
    this.paneModels = [...panes];

    // SciChartSurface defaults to Thickness.fromNumber(10). Sub-charts are laid
    // out inside the padded viewRect, so the panes would not reach the container
    // edges: dead space along the bottom, and every seam ~10px above where its
    // fraction says it is — which also puts any overlay drawn at `fraction *
    // height` below the seam it is meant to mark. The panes ARE the chrome here,
    // so the parent needs no padding of its own.
    parent.padding = Thickness.fromNumber(0);

    for (const [pane, position] of stackPanes(panes)) {
      this.buildPaneSubChart(parent, wasmContext, pane, position);
    }

    // After the panes, because they are what it draws into.
    this.drawEventMarkers();

    // **The axes are not themed by anything above this line.** `theme:` on
    // `SciChartSurface.create` applies a provider to the *surface*, and a 2D
    // surface does not re-theme an axis added to it afterwards (only the 3D one
    // does). Every axis is instead born holding `SciChartSurfaceBase.DEFAULT_THEME`
    // — SciChart's dark default — so on a fresh load the tick labels came out
    // `#A6A7AC`, the axis bands `#20212333` and the gridlines `#AAAAAA37`,
    // whatever the workspace's theme said. Measured on the White theme: bands
    // rendered `#d2d3d3` (and `#aeafb0` where an X band crossed a Y band) with
    // tick text a pale grey on white.
    //
    // It was invisible on the dark themes, where SciChart's defaults happen to
    // look about right, and it healed itself the moment anyone switched theme —
    // which is exactly what this call is: the *switch* path, run once at build,
    // so the first paint matches the one after a switch instead of being a
    // slightly different chart. Cheap, and it cannot drift from `applyTheme`
    // because it *is* `applyTheme`.
    this.applyTheme(this.theme);

    this.syncXAxes();
    this.applyGridDensity();

    // The gridline count follows the *rendered* height, so it has to follow the
    // container too — dragging the layout's own column/row divider, or opening
    // the screener editor, resizes every pane without touching a pane height.
    // Recomputing is a few property writes and only when the number changes, so
    // it is affordable at observer frequency; a rebuild here would not be.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.applyGridDensity());
      this.resizeObserver.observe(container);
    }
  }

  /**
   * Give every Y axis a gridline count its pane's height can carry.
   *
   * Measured rather than stored: the model says a pane is `1fr`, and only the
   * browser knows what that is in pixels. Deliberately **not** part of the render
   * plan's structural key — `maxAutoTicks` is a property write on an axis that
   * already exists, and folding a measurement into the key would rebuild the
   * WebGL surface (and refetch the data) sixty times a second during a drag.
   */
  private applyGridDensity(): void {
    const total = this.container?.clientHeight ?? 0;
    if (total <= 0) return;

    const fractions = stackFractions(paneWeights(this.paneModels));
    const renders = [...this.panes.values()];
    renders.forEach((render, i) => {
      const f = fractions[i];
      if (!f) return;
      const ticks = maxMajorGridLines(total * f.height);
      for (const axis of render.sub.yAxes.asArray()) {
        // Guarded: the setter invalidates the parent surface, so writing an
        // unchanged value would repaint the whole group on every observer tick.
        if (axis.maxAutoTicks !== ticks) axis.maxAutoTicks = ticks;
      }
    });
  }

  /**
   * Re-stack the panes to new relative heights, without rebuilding.
   *
   * A SubChart's `subPosition` is settable, so a divider drag is a reposition
   * rather than a teardown — which matters because rebuilding would drop the
   * loaded series, the zoom window and the X-axis sync wiring on every pointer
   * move.
   *
   * `heights` is positional, matching the `panes` passed to {@link buildGroup};
   * a length mismatch is ignored rather than partially applied, since a
   * half-stacked chart is worse than an unchanged one.
   */
  setPaneHeights(heights: readonly string[]): void {
    const renders = [...this.panes.values()];
    if (renders.length !== heights.length) return;

    // Written back so `applyGridDensity` — which measures against the *current*
    // stacking — does not keep dividing the container by the heights the group
    // was built with. A drag would otherwise thin the gridlines of the pane that
    // grew and crowd the one that shrank, backwards.
    this.paneModels = this.paneModels.map((pane, i) => ({ ...pane, height: heights[i]! }));

    const fractions = stackFractions(heights.map(paneWeight));
    renders.forEach((render, i) => {
      const f = fractions[i]!;
      // A plain {x,y,width,height} object, the same shape `buildGroup` passes to
      // `createSubSurface`. `subPosition` is a union discriminated by which
      // fields are present, and SciChart tests xywh before ltrb — so a `Rect`
      // would also work, since it exposes both. One representation on both paths
      // simply leaves nothing to reason about.
      render.sub.subPosition = { x: 0, y: f.y, width: 1, height: f.height };
    });
    // A pane that just lost half its height must lose gridlines with it, or the
    // count stays right only until the first drag.
    this.applyGridDensity();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Re-stack the panes into a new display order, without rebuilding.
   *
   * A reorder is a reposition for {@link setPaneHeights}' reason and then one
   * more: it changes nothing about the *request*, so paying a WebGL teardown
   * plus a full refetch for it would reload the chart once per click of a move
   * button — and the zoom window would snap back each time.
   *
   * **It cannot be spelled as `setPaneHeights` with the heights permuted.**
   * `panes` is a `Map` whose *insertion* order is the display order, and every
   * positional walk in this class — that method and {@link applyGridDensity} —
   * reads `[...this.panes.values()]`. `Map.set` on a key that already exists
   * keeps its original position, so the Map has to be emptied and refilled;
   * permuting the heights alone would move the bands and leave the panes.
   *
   * `paneModels` is reordered with it, or the two disagree in a way that shows
   * up nowhere until later: `applyGridDensity` zips the models' weights against
   * the Map by index, so a half-applied reorder thins the gridlines of the
   * wrong pane, and the next divider drag writes the wrong pane's height.
   *
   * Anything that is not exactly the current pane set is ignored rather than
   * partially applied — a half-stacked chart is worse than an unchanged one,
   * the same guard `setPaneHeights` makes on its length.
   */
  setPaneOrder(paneIds: readonly string[]): void {
    if (paneIds.length !== this.panes.size) return;

    const renders: PaneRender[] = [];
    const models: Pane[] = [];
    const byId = new Map(this.paneModels.map((pane) => [pane.id, pane] as const));
    for (const id of paneIds) {
      const render = this.panes.get(id);
      const model = byId.get(id);
      // An id that was never built, or a duplicate: both leave a hole, and both
      // are a caller bug rather than something to render half of.
      if (!render || !model) return;
      renders.push(render);
      models.push(model);
    }
    if (models.length !== this.paneModels.length) return;

    this.panes.clear();
    this.linkedLines.clear();
    paneIds.forEach((id, i) => this.panes.set(id, renders[i]!));
    this.paneModels = models;

    const fractions = stackFractions(paneWeights(this.paneModels));
    renders.forEach((render, i) => {
      // The same plain {x,y,width,height} `setPaneHeights` and `buildGroup`
      // write, for the reason given there: one representation on every path.
      render.sub.subPosition = { x: 0, y: fractions[i]!.y, width: 1, height: fractions[i]!.height };
    });
    // A pane that moved into a taller band carries a different gridline count,
    // exactly as one that was dragged into it does.
    this.applyGridDensity();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Where the glyphs sit in the price pane, as a fraction of its height.
   *
   * Relative rather than a price, so they stay on the floor of the pane whatever
   * the scale does — a y in data coordinates would drift up and down the pane
   * every time the visible range changed, which is most of what a chart does.
   *
   * 0.985 rather than 1: flush with the floor a glyph straddles the axis line
   * and reads as belonging to the axis rather than to the bars. It started at
   * 0.955, which left a visible band of empty chart between the markers and the
   * date labels — the span presets (`1Y`, `YTD`, `>|`) sit in that band, and the
   * markers are deliberately **below** them: the presets are chrome that belongs
   * to the chart, and a marker belongs to the bar above it, so the marker is the
   * one that should be nearer its bar's own axis.
   */
  private static readonly MARKER_Y = 0.985;
  /**
   * The glyph's size. 11 px bold since the YoY note arrived (owner,
   * 2026-10-08: "E small, the number bold") — the number is the reading now,
   * and the letter only says what kind of event it is.
   */
  private static readonly MARKER_FONT_PX = 11;
  /** How far apart two markers on one bar sit: an 11 px bold glyph and a gap. */
  private static readonly MARKER_STEP_PX = 14;
  /** From the top glyph's centre to its note's: half a glyph, a gap, half an 11 px line. */
  private static readonly NOTE_STEP_PX = 13;
  /** The note's type size (owner, 2026-10-07: 10 was too small). */
  private static readonly NOTE_FONT_PX = 11;
  /**
   * The closest two markers may sit, in CSS pixels, and still carry notes. A
   * note is at most `Q<-999%` — seven characters of 11 px bold, ~50 px — so 56
   * leaves a gap between neighbours. Below it every note hides (owner's
   * option B): one hidden and its neighbour shown would read as a quarter
   * that has no YoY.
   */
  private static readonly NOTE_MIN_GAP_PX = 56;
  /** The text line a note takes, which the price axis keeps clear of candles. */
  static readonly NOTE_LINE_PX = 15;

  /** What {@link setEstimateLines} was last handed, re-drawn on a theme switch. */
  private estimateInput: readonly EstimateLine[] = [];
  /** The drawn estimate lines, re-placed by `layoutPinnedMarkers`. */
  private estimateLines: { time: number; annotation: HorizontalLineAnnotation; sub: SciChartSubSurface }[] = [];

  setEstimateLines(lines: readonly EstimateLine[]): void {
    for (const drawn of this.estimateLines) {
      drawn.sub.annotations.remove(drawn.annotation as never);
      drawn.annotation.delete();
    }
    this.estimateLines = [];
    this.estimateInput = lines;
    const warn = this.theme.tokens.warn ?? "#e3b341";
    for (const line of lines) {
      for (const render of this.panes.values()) {
        const pr = render.byId.get(line.plotId);
        if (!pr || pr.hidden) continue;
        const annotation = new HorizontalLineAnnotation({
          // From `x1` to the right edge: the estimate holds from its report on.
          x1: line.time,
          y1: this.drawnY(pr, line.value),
          ...(pr.series.yAxisId ? { yAxisId: pr.series.yAxisId } : {}),
          stroke: warn,
          strokeThickness: 1.5,
          strokeDashArray: [4, 3],
          showLabel: true,
          labelPlacement: ELabelPlacement.Axis,
          labelValue: line.label,
          axisLabelFill: warn,
          axisLabelStroke: this.theme.tokens.background ?? "#0e1116",
          axisFontSize: 11,
          isEditable: false,
        });
        render.sub.annotations.add(this.own(annotation) as never);
        this.estimateLines.push({ time: line.time, annotation, sub: render.sub });
      }
    }
    this.layoutPinnedMarkers();
    this.parentSurface?.invalidateElement();
  }

  setEventMarkers(markers: readonly EventMarker[]): void {
    const hadNotes = this.markers.some((m) => m.note);
    this.markers = markers;
    this.drawEventMarkers();
    // Notes arriving or leaving change the price axis's foot (`withNoteRoom`).
    if (hadNotes !== markers.some((m) => m.note)) this.fitYAxes();
    this.parentSurface?.invalidateElement();
  }

  /**
   * The pane the markers belong to: the price one, by **role**.
   *
   * Never index 0 — the panes can be reordered, and the events belong to the
   * bars rather than to whatever is on top.
   */
  /**
   * A pale band behind the bar that is still forming — or none (`null`).
   *
   * **Yellow, the colour of "not final".** The same theme token the projected
   * lines use (`warn`): a projection is not final and neither is this bar, so it
   * is one meaning, told apart by shape — lines past the last bar there, a
   * background behind it here (owner, 2026-09-29). Faint, so the candle and any
   * indicator over it read unchanged, and on the price pane only.
   *
   * `start`/`end` are X values — the bar's time plus and minus half a bar, so
   * the band sits centred on its candle. A repaint, never a rebuild: it moves
   * with every update.
   */
  setFormingBar(bar: { start: number; end: number } | null): void {
    this.formingBar = bar;
    const pane = this.pricePane();
    if (!bar || !pane) {
      if (this.formingBand && pane) pane.sub.annotations.remove(this.formingBand as never);
      this.formingBand = null;
      return;
    }
    const fill = SciChartJsChartBuilder.faint(this.theme.tokens.warn ?? "#e3b341");
    if (!this.formingBand) {
      this.formingBand = new BoxAnnotation({
        x1: bar.start,
        x2: bar.end,
        y1: 0,
        y2: 1,
        yCoordinateMode: ECoordinateMode.Relative,
        // Named, never defaulted — see `priceYAxisId`.
        yAxisId: this.priceYAxisId(),
        fill,
        strokeThickness: 0,
        annotationLayer: EAnnotationLayer.BelowChart,
        isEditable: false,
        isSelected: false,
      });
      pane.sub.annotations.add(this.own(this.formingBand) as never);
    } else {
      this.formingBand.x1 = bar.start;
      this.formingBand.x2 = bar.end;
      this.formingBand.fill = fill;
    }
  }

  /**
   * A pale full-height column behind each listed bar of the price pane — the
   * forming band generalised (`docs/features/charts.md`, "Highlighting a bar").
   * `color` is a theme token name or a hex; default `accent`. A time that is not
   * a loaded bar draws nothing. A repaint, never a rebuild: boxes are re-pointed
   * and the extras removed, and `setBarTimes` re-places them on a new frame.
   *
   * Builder state, so the host re-applies it after a rebuild — its effect must
   * be keyed on `structure`, as the forming band's is.
   *
   * **One list per `layer`**, so an indicator's highlights and the user's own
   * never overwrite each other; each caller owns its layer and replaces it whole.
   */
  setBarHighlights(bars: readonly BarHighlight[], layer = "default"): void {
    let entry = this.barHighlightLayers.get(layer);
    if (!entry) this.barHighlightLayers.set(layer, (entry = { list: [], boxes: [] }));
    entry.list = [...bars];
    const boxes = entry.boxes;
    const pane = this.pricePane();
    if (!pane) return;
    const placed = bars.flatMap((bar) => {
      const slot = barSlot(this.barTimes, bar.t);
      return slot
        ? [{ slot, color: bar.color ?? "accent", low: bar.low, high: bar.high, opacity: bar.opacity }]
        : [];
    });
    while (boxes.length > placed.length) {
      pane.sub.annotations.remove(boxes.pop() as never);
    }
    placed.forEach(({ slot, color, low, high, opacity }, i) => {
      // A token the way a plot's is resolved (`accent.orange` included), else a hex.
      const solid = resolveColorRef(color, this.theme) ?? color;
      const fill = opacity === undefined ? SciChartJsChartBuilder.faint(solid) : solid;
      const alpha = opacity ?? 1;
      // A price span hugs the candle; without one the column is the pane's height.
      const priced = low !== undefined && high !== undefined;
      const y = priced
        ? { y1: low, y2: high, yCoordinateMode: ECoordinateMode.DataValue }
        : { y1: 0, y2: 1, yCoordinateMode: ECoordinateMode.Relative };
      const box = boxes[i];
      if (box) {
        box.x1 = slot.start;
        box.x2 = slot.end;
        box.yCoordinateMode = y.yCoordinateMode;
        box.y1 = y.y1;
        box.y2 = y.y2;
        box.fill = fill;
        box.opacity = alpha;
        return;
      }
      const made = new BoxAnnotation({
        x1: slot.start,
        x2: slot.end,
        ...y,
        // Named, never defaulted — see `priceYAxisId`.
        yAxisId: this.priceYAxisId(),
        fill,
        opacity: alpha,
        strokeThickness: 0,
        annotationLayer: EAnnotationLayer.BelowChart,
        isEditable: false,
        isSelected: false,
      });
      boxes.push(made);
      pane.sub.annotations.add(this.own(made) as never);
    });
  }

  /**
   * Full-height background boxes over **time ranges** on the price pane —
   * `seasBands`' windows. `x1`/`x2` are axis times, so a range may run past the
   * last bar: the caller places those ends with `timeAtOffset`, as a trendline
   * stub does. One list per `layer`, replaced whole, `setBarHighlights`' rule;
   * a repaint, never a rebuild, and builder state the host re-applies after one.
   */
  setSpans(spans: readonly TimeSpan[], layer = "spans"): void {
    let entry = this.spanLayers.get(layer);
    if (!entry) this.spanLayers.set(layer, (entry = { list: [], boxes: [] }));
    entry.list = [...spans];
    const boxes = entry.boxes;
    const pane = this.pricePane();
    if (!pane) return;
    while (boxes.length > spans.length) {
      pane.sub.annotations.remove(boxes.pop() as never);
    }
    spans.forEach((span, i) => {
      const solid = resolveColorRef(span.color, this.theme) ?? span.color;
      const fill = span.opacity === undefined ? SciChartJsChartBuilder.faint(solid) : solid;
      const alpha = span.opacity ?? 1;
      const box = boxes[i];
      if (box) {
        box.x1 = span.x1;
        box.x2 = span.x2;
        box.fill = fill;
        box.opacity = alpha;
        return;
      }
      const made = new BoxAnnotation({
        x1: span.x1,
        x2: span.x2,
        y1: 0,
        y2: 1,
        yCoordinateMode: ECoordinateMode.Relative,
        yAxisId: this.priceYAxisId(),
        fill,
        opacity: alpha,
        strokeThickness: 0,
        annotationLayer: EAnnotationLayer.BelowChart,
        isEditable: false,
        isSelected: false,
      });
      boxes.push(made);
      pane.sub.annotations.add(this.own(made) as never);
    });
  }

  /** A theme colour at ~13% opacity, for a background that must not compete. */
  private static faint(color: string): string {
    const hex = /^#([0-9a-fA-F]{6})$/.exec(color.trim());
    return hex ? `#${hex[1]}22` : color;
  }

  /**
   * Text after a plot's last-value label on the price axis — the forming bar's
   * countdown (`"02:41"`), or `null` to remove it. A property write on a label
   * that exists; nothing is rebuilt, so it can change every second.
   */
  setExtendedPrice(price: number | null, phase: "pre" | "post" = "post"): void {
    // A label on the price axis at the last pre- or post-market print, in the
    // theme's yellow — this app's "not final" (owner, 2026-09-29). A rectangle
    // reading `P:765.13` (`Pre:` before the bell), not an arrow, so it cannot be
    // mistaken for a plot's last value. It is a horizontal line's axis label
    // with the line itself transparent. A property write once it exists, never
    // a rebuild; `null` takes it off.
    const pane = this.pricePane();
    if (price === null || !Number.isFinite(price) || !pane) {
      if (this.extMarker && pane) pane.sub.annotations.remove(this.extMarker as never);
      this.extMarker = null;
      return;
    }
    const axis = pane.sub.yAxes.asArray().find((a) => a.id === this.priceYAxisId());
    const text = `${phase === "pre" ? "Pre" : "P"}:${
      axis ? axis.labelProvider.formatCursorLabel(price) : price.toFixed(2)
    }`;
    if (!this.extMarker) {
      this.extMarker = new HorizontalLineAnnotation({
        y1: price,
        // Named, never defaulted — see `priceYAxisId`.
        yAxisId: this.priceYAxisId(),
        stroke: "#00000000",
        strokeThickness: 0,
        showLabel: true,
        labelPlacement: ELabelPlacement.Axis,
        labelValue: text,
        axisLabelFill: this.theme.tokens.warn ?? "#e3b341",
        axisLabelStroke: this.theme.tokens.background ?? "#0e1116",
        axisFontSize: 11,
        isEditable: false,
      });
      pane.sub.annotations.add(this.own(this.extMarker) as never);
      this.raisePriceMarker(pane.sub);
    } else {
      if (this.extMarker.y1 === price && this.extMarker.labelValue === text) return;
      this.extMarker.y1 = price;
      this.extMarker.labelValue = text;
    }
    // **Asked for, not assumed.** After the close the bars stop changing, so
    // no series update repaints the surface — the label's new value was set
    // and never drawn (owner: "P: label shows … don't see moving").
    this.parentSurface?.invalidateElement();
  }

  setLastLabelSuffix(plotId: Ulid, suffix: string | null): void {
    if (suffix) this.labelSuffix.set(plotId, suffix);
    else this.labelSuffix.delete(plotId);
    for (const pane of this.panes.values()) {
      const pr = pane.byId.get(plotId);
      if (pr) this.updateMarker(pr);
    }
  }

  private pricePane(): PaneRender | undefined {
    const model = this.paneModels.find((pane) => pane.role === "price");
    return model ? this.panes.get(model.id) : undefined;
  }

  /**
   * The Y axis a marker must name, and it is **not** the default one.
   *
   * Every axis here is born with the contract's own ULID for an id, and a series
   * says so explicitly (`yAxisId: sp.axisRef ?? plan.axes[0].id`). An annotation
   * that names none gets SciChart's `DefaultAxisId`, which matches no axis on
   * this pane — and the annotation then simply does not draw. Nothing throws and
   * nothing is logged, which is exactly how a chart ends up with no markers on
   * it and no reason why.
   *
   * `Relative` y does not excuse it: the mode decides how the number is read,
   * and the annotation still resolves an axis to find the rect to read it
   * against.
   */
  private priceYAxisId(): string {
    const model = this.paneModels.find((pane) => pane.role === "price");
    const plan = model ? this.plans.get(model.id) : undefined;
    return plan?.axes[0]?.id ?? DEFAULT_Y_AXIS_ID;
  }

  private markerColor(tone: EventMarker["tone"]): string {
    const tokens = this.theme.tokens;
    if (tone === "positive") return tokens.positive ?? "#26a69a";
    if (tone === "negative") return tokens.negative ?? "#ef5350";
    if (tone === "pending") return tokens.warn ?? "#e3b341";
    return tokens.textSecondary ?? "#8a8f98";
  }

  /**
   * Replace every glyph. Cheap enough to do wholesale: there are a few dozen at
   * most, and a diff would have to key on something a caller does not promise.
   */
  private drawEventMarkers(): void {
    const pane = this.pricePane();
    for (const annotation of this.markerAnnotations) {
      pane?.sub.annotations.remove(annotation as never);
    }
    this.markerAnnotations = [];
    this.markersByAnnotation = new Map();
    this.markerNotes = [];
    this.pinnedAnnotations = [];
    if (!pane) return;
    this.attachMarkerHover(pane);

    const yAxisId = this.priceYAxisId();
    // **Stacked when they share a bar** (owner, 2026-09-29): earnings and news
    // both land on the last bar, and at one point they were one smudge whose
    // hover found only the topmost. Each later marker on a bar goes one glyph
    // higher. Relative y, so the step is the glyph's pixels over the pane's —
    // measured when laid out, a fixed guess before.
    const paneHeight = pane.sub.seriesViewRect?.height ?? 0;
    const step = paneHeight > 0 ? SciChartJsChartBuilder.MARKER_STEP_PX / paneHeight : 0.06;
    const slotAt = new Map<number, number>();
    for (const marker of this.markers) {
      const slot = slotAt.get(marker.time) ?? 0;
      slotAt.set(marker.time, slot + 1);
      // **`TextAnnotation`, not `NativeTextAnnotation`.** The native one draws
      // through the WebGL font atlas and carries only `fontSize` and
      // `fontFamily` — there is no `fontWeight` on it at all, so a bold glyph is
      // not expressible. These are one or two characters that have to read at a
      // glance against candles, which is exactly what weight buys; the SVG one
      // costs a DOM node per marker and there are a few dozen.
      const annotation = new TextAnnotation({
        x1: marker.time,
        y1: SciChartJsChartBuilder.MARKER_Y - slot * step,
        yCoordinateMode: ECoordinateMode.Relative,
        // Named, never defaulted — see `priceYAxisId`. The X axis is the one
        // axis here built without an id, so it genuinely is the default and is
        // deliberately left unset.
        yAxisId,
        text: marker.label,
        textColor: this.markerColor(marker.tone),
        fontSize: SciChartJsChartBuilder.MARKER_FONT_PX,
        fontWeight: "bold",
        // Centred on the bar it belongs to rather than starting at it, or every
        // glyph would sit a few pixels to the right of its own candle.
        horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
        verticalAnchorPoint: EVerticalAnchorPoint.Center,
        // Above the series, or a dense candle body paints over it.
        annotationLayer: EAnnotationLayer.AboveChart,
        // Nobody drew these and nobody may move them: a grip here would offer to
        // edit a fact.
        isEditable: false,
        isSelected: false,
      });
      pane.sub.annotations.add(this.own(annotation) as never);
      this.markerAnnotations.push(annotation);
      this.markersByAnnotation.set(annotation, marker);
      if (marker.pinned) this.pinnedAnnotations.push({ annotation, time: marker.time, text: marker.label });
    }

    // **The note goes above every glyph on its bar**, so an `E` and an `N`
    // stacked on the last bar keep the reading clear of both. Hovering it opens
    // the same popup as its glyph: it is part of the marker, not a second one.
    const noteStep = paneHeight > 0 ? SciChartJsChartBuilder.NOTE_STEP_PX / paneHeight : 0.05;
    for (const marker of this.markers) {
      if (!marker.note) continue;
      const glyphs = slotAt.get(marker.time) ?? 1;
      const note = new TextAnnotation({
        x1: marker.time,
        y1: SciChartJsChartBuilder.MARKER_Y - (glyphs - 1) * step - noteStep,
        yCoordinateMode: ECoordinateMode.Relative,
        yAxisId,
        text: marker.note,
        textColor:
          marker.noteTone === "negative" ? this.markerColor("negative") : this.markerColor("neutral"),
        fontSize: SciChartJsChartBuilder.NOTE_FONT_PX,
        // Bold: the number is the reading (owner, 2026-10-08).
        fontWeight: "bold",
        horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
        verticalAnchorPoint: EVerticalAnchorPoint.Center,
        annotationLayer: EAnnotationLayer.AboveChart,
        isEditable: false,
        isSelected: false,
      });
      pane.sub.annotations.add(this.own(note) as never);
      this.markerAnnotations.push(note);
      this.markersByAnnotation.set(note, marker);
      this.markerNotes.push({ annotation: note, time: marker.time });
      if (marker.pinned) this.pinnedAnnotations.push({ annotation: note, time: marker.time, text: marker.note });
    }
    this.layoutMarkerNotes();
  }

  /** How far inside the right edge a pinned glyph is held: half a glyph and a gap. */
  private static readonly PIN_INSET_PX = 12;

  /**
   * A `pinned` marker sits on **its own date** while that is on screen, and at
   * the view's right edge as `E›` while it is further out (owner, 2026-10-28:
   * AAPL reporting tomorrow belongs on tomorrow, not at the edge two months
   * on). The "reported twice" look it once had came from a stale next date
   * beside a report just made; `alreadyReported` drops that marker instead.
   * Its note moves with it.
   *
   * Called wherever `layoutMarkerNotes` is, for its reason: the edge is the
   * visible range, and a pan or a zoom moves it.
   */
  private layoutPinnedMarkers(): void {
    if (this.pinnedAnnotations.length === 0 && this.estimateLines.length === 0) return;
    const xAxis = this.pricePane()?.sub.xAxes.get(0);
    const calc = xAxis?.getCurrentCoordinateCalculator();
    const visible = xAxis?.visibleRange;
    if (!calc || !visible) return;
    const inset = SciChartJsChartBuilder.PIN_INSET_PX * (DpiHelper.PIXEL_RATIO || 1);
    const edge = calc.getDataValue(calc.getCoordinate(visible.max) - inset);
    for (const pinned of this.pinnedAnnotations) {
      const held = pinned.time > edge;
      const x1 = held ? edge : pinned.time;
      if (pinned.annotation.x1 !== x1) pinned.annotation.x1 = x1;
      // Only the glyph gains the arrow; its note keeps its own text.
      const glyph = this.markerNotes.every((n) => n.annotation !== pinned.annotation);
      const text = held && glyph ? `${pinned.text}›` : pinned.text;
      if (pinned.annotation.text !== text) pinned.annotation.text = text;
    }
    // The estimate lines by the same rule: on their date while it is on
    // screen, a stub at the right edge while it is further out.
    for (const line of this.estimateLines) {
      const x1 = line.time > edge ? edge : line.time;
      if (line.annotation.x1 !== x1) line.annotation.x1 = x1;
    }
  }

  /**
   * Show the notes only while markers sit far enough apart for them to fit
   * (`NOTE_MIN_GAP_PX`), measured on screen — so zooming in brings them back and
   * zooming out to monthly bars hides them. All or none, never a few.
   *
   * Called after every redraw and on every visible-range change: the gap is
   * pixels, and both change it.
   */
  private layoutMarkerNotes(): void {
    this.layoutPinnedMarkers();
    if (this.markerNotes.length === 0) return;
    const xAxis = this.pricePane()?.sub.xAxes.get(0);
    const calc = xAxis?.getCurrentCoordinateCalculator();
    const visible = xAxis?.visibleRange;
    if (!calc || !visible) return;
    const ratio = DpiHelper.PIXEL_RATIO || 1;
    // Only the markers on screen — see `notesFit` for the TSLA case.
    const hide = !notesFit(
      this.markerNotes.map((n) => n.time),
      { min: visible.min, max: visible.max },
      (t) => calc.getCoordinate(t) / ratio,
      SciChartJsChartBuilder.NOTE_MIN_GAP_PX,
    );
    for (const note of this.markerNotes) {
      if (note.annotation.isHidden !== hide) note.annotation.isHidden = hide;
    }
  }

  onEventMarkerHover(
    handler: ((marker: EventMarker | null, at: { x: number; y: number } | null) => void) | null,
  ): void {
    this.markerHover = handler;
  }

  /**
   * Attach the hover detector to the price pane, once.
   *
   * `targets` is a **getter**, not the array: the annotations are replaced
   * wholesale every time the markers change, and a modifier holding the list it
   * was built with would go on hit-testing objects that are no longer on the
   * chart — hovering nothing and reporting the marker that used to be there.
   *
   * `notifyOutEvent` is what closes the popup. Without it the callback fires
   * only on the way *in*, so the panel would open on the first glyph and never
   * shut.
   */
  private attachMarkerHover(pane: PaneRender): void {
    if (this.markerHoverModifier) return;
    const modifier = new AnnotationHoverModifier({
      targets: () => this.markerAnnotations as never[],
      notifyOutEvent: true,
      hoverMode: EHoverMode.AbsoluteTopmost,
      onHover: (args) => {
        const hovered = args.hoveredEntities[0];
        const marker = hovered ? this.markersByAnnotation.get(hovered) : undefined;
        if (!marker) {
          this.markerHover?.(null, null);
          return;
        }
        // Canvas -> client, so the host can place a popup without knowing
        // anything about the surface it is over. `mousePoint` is in device
        // pixels (`offsetX * PIXEL_RATIO`), so it is divided back before being
        // added to a CSS box — unscaled, the popup opened too far right and
        // down at 150% display scaling.
        const box = this.container?.getBoundingClientRect();
        const point = args.mouseArgs?.mousePoint;
        const ratio = DpiHelper.PIXEL_RATIO || 1;
        const at =
          box && point ? { x: box.left + point.x / ratio, y: box.top + point.y / ratio } : null;
        this.markerHover?.(marker, at);
      },
    });
    pane.sub.chartModifiers.add(modifier as never);
    this.markerHoverModifier = modifier;
  }

  onDerivedHover(
    handler: ((lines: readonly string[] | null, at: { x: number; y: number } | null) => void) | null,
  ): void {
    this.derivedHover = handler;
  }

  /**
   * The hover detector for derived drawings that carry lines, once per pane.
   *
   * `attachMarkerHover`'s shape and its reasons: `targets` is a getter because
   * the drawings are replaced on every render, `notifyOutEvent` closes the
   * popup, and the point is divided back out of device pixels.
   */
  private attachDerivedHover(pane: PaneRender): void {
    if (this.derivedHoverModifier) return;
    const modifier = new AnnotationHoverModifier({
      targets: () => [...this.derivedHoverLines.keys()] as never[],
      notifyOutEvent: true,
      hoverMode: EHoverMode.AbsoluteTopmost,
      onHover: (args) => {
        const hovered = args.hoveredEntities[0];
        const lines = hovered ? this.derivedHoverLines.get(hovered) : undefined;
        if (!lines) {
          this.derivedHover?.(null, null);
          return;
        }
        const box = this.container?.getBoundingClientRect();
        const point = args.mouseArgs?.mousePoint;
        const ratio = DpiHelper.PIXEL_RATIO || 1;
        const at =
          box && point ? { x: box.left + point.x / ratio, y: box.top + point.y / ratio } : null;
        this.derivedHover?.(lines, at);
      },
    });
    pane.sub.chartModifiers.add(modifier as never);
    this.derivedHoverModifier = modifier;
  }

  /**
   * Draw every Volume-by-Price plot: horizontal volume bars down the left edge
   * of the pane they sit in.
   *
   * **Annotations, not a renderable series, and the choice is forced.** Every
   * renderable here plots one y per bar against a shared x, so its geometry is a
   * function of x — and this indicator's is not. Its bars run the other way:
   * positioned by *price*, sized by volume, and anchored to the pane's left edge
   * rather than to any bar. The nearest renderable is
   * `FastColumnRenderableSeries` rotated, which SciChart does not offer on a 2D
   * surface, and the nearest thing that does draw a rectangle takes its corners
   * in data coordinates on both axes — so "twenty per cent of the pane" would
   * have to be converted into a span of *time* on a `DiscontinuousDateAxis`, and
   * recomputed on every frame of a pan to stay twenty per cent. A
   * `BoxAnnotation` takes its two axes' coordinate modes separately, which is
   * exactly the mixed shape this needs and the reason there is nothing to
   * recompute on a pan:
   *
   *  - **x is `Relative`** — 0 is the left edge of the plot area and 1 the
   *    right, so the bars are pinned to the edge and a zoom or a pan does not
   *    touch them, which is what makes them readable as levels;
   *  - **y is `DataValue`** — a zone *is* a price band, so it must track the
   *    price axis, and it does so for free.
   *
   * `BelowChart`, or the bars would paint over the candles they exist to be read
   * against. That is the same call the trade-plan zone makes, for the same
   * reason.
   *
   * **This is the only place a Volume-by-Price bar is styled.** There is
   * deliberately no construction-time half to drift from a restyle half: the
   * boxes are born blank and every colour, width and opacity on them is written
   * here, from the retained model, on every one of the four paths that can
   * change what they should look like — the build, a data change, `applyStyle`
   * and `applyTheme`. The band fill was the cautionary tale: the same decision
   * made in two places, only one of them corrected, and the restyle path winning
   * because the host calls `applyTheme` from its own mount effect.
   *
   * **Recomputed when the data changes, and deliberately not on a pan.** The
   * period is a fixed `lookback` from the last bar, not the visible window, so
   * it does not move when the window does — a support level that slides while
   * you scroll is not a level. `visibleRangeChanged` is already subscribed a few
   * methods down and subscribing to it here is the obvious thing to do; it would
   * re-bin two hundred and fifty bars sixty times a second to produce the same
   * twelve numbers.
   */
  private drawVolumeByPrice(): void {
    for (const pane of this.paneModels) {
      for (const plot of pane.plots ?? []) {
        if (plot.type !== "volumeByPrice") continue;
        this.paintVolumeByPrice(pane, plot);
      }
    }
  }

  /**
   * One Volume-by-Price plot, repainted in place.
   *
   * Reads the *model* rather than the `SeriesPlan`, because this plot has no
   * series: `plan/series.ts` maps a `PlotType` to a renderable and a DataSeries
   * shape, and this type has neither. The model carries everything that is
   * actually needed — the style, the palette, the axis and whether it is hidden
   * — and reading it is also what keeps `applyStyle` and `applyTheme` correct,
   * since both write the model before asking for a repaint.
   */
  private paintVolumeByPrice(pane: Pane, plot: Plot): void {
    const render = this.panes.get(pane.id);
    if (!render) return;

    const bars = this.volumeByPriceBars(pane.id);
    // Binned by the pure layer, which is where the whole indicator lives and the
    // only part of it that can be unit-tested. It is handed the **whole** loaded
    // series and a lookback rather than a slice, because the first bar of the
    // period takes its direction from the bar before the period — which exists,
    // since the chart loads far more bars than the period asks for.
    // **Off the plot's own alias, never the defaults.** The catalog declares
    // three editable parameters and the picker draws a spinner for each; binding
    // the constants here is what made all three inert — Apply re-minted the
    // alias, the chart tore down and rebuilt, and the picture never changed.
    // `volumeByPriceParams` lives in the plan layer because the shell cannot be
    // unit-tested, so a parameter parsed here is one nobody can prove works.
    const params = volumeByPriceParams(plot.outputAlias);
    // **`visible` re-bins what is on screen; `lookback` holds still.** The two
    // answer different questions — "where has business happened *here*" against
    // "where are this year's levels" — and the only difference in the maths is
    // which bars go in, so both arrive at the binner as a range.
    //
    // The search is `visibleBarRange` in the plan layer, deliberately: an
    // off-by-one at either edge drops or doubles a day's volume and the picture
    // stays entirely plausible, which is the kind of wrong this file cannot
    // test for itself.
    const range =
      params.period === "visible"
        ? visibleBarRange(this.volumeByPriceTimes(pane.id), this.barWindow())
        : undefined;
    // An undefined range in `visible` mode means the window could not be
    // resolved — no bars yet, or an axis with no range. Draw nothing rather than
    // silently falling back to the lookback, which would show a profile of a
    // period nobody asked for.
    const unresolved = params.period === "visible" && !range;
    const plan: VolumeByPricePlan | undefined =
      bars.length && !unresolved
        ? volumeByPricePlan(bars, {
            lookback: params.lookback,
            bins: params.bins,
            // **The zone count is an output in `atr` mode, not an input.** The
            // width comes from the symbol's own volatility and the grid is
            // absolute, so how many zones that makes is a fact about the period
            // rather than a number anybody chose. `bins` still arrives because
            // it is the `fixed` mode's count and the warm-up fallback's.
            bands: params.bands,
            ...(range ? { range } : {}),
          })
        : undefined;

    // Sized from the PLAN, never from the parameters. In `fixed` mode the two
    // agree; in `atr` mode `bins` is the fallback count and would allocate
    // twelve boxes for a forty-zone grid, which draws the bottom third of the
    // profile and nothing above it — the same defect as the hardcoded twelve
    // this replaced, one level further in.
    const boxes = this.volumeByPriceBoxesFor(plot, pane, render, plan?.bins.length ?? 0);

    // **Paired, and `PlotStyle.stroke` deliberately does not override it** — the
    // same call `restyleSignal` makes and for the same reason: up and down
    // volume in one colour would leave the segment boundary as the only thing
    // telling them apart, which is no distinction at all. A `PlotPalette` is
    // how a user changes these two, exactly as for candles.
    // A named palette wins; with none, the two greys above rather than the
    // candles' own pair — see `VOLUME_BY_PRICE_TONES`.
    const [up, down] = plot.palette
      ? this.pairedColors(resolvePalettePlan(plot.palette, this.theme))
      : (VOLUME_BY_PRICE_TONES.map(
          (tone, i) => resolveColorRef(tone, this.theme) ?? (i === 0 ? "#6b7280" : "#aab3c0"),
        ) as [string, string]);
    // Straight off the style, so the panel can change it without a deploy —
    // which the design asks for by name, expecting 0.30 to need one correction
    // once it has been seen against real candles.
    const opacity = plot.style?.opacity ?? VOLUME_BY_PRICE_OPACITY;
    // The column's bargain, and the same default: a bar that never asked for an
    // outline does not gain one, and the edge is only drawn when a width was
    // named. A zone edge drawn at every boundary would fence the pane.
    const thickness = plot.style?.thickness ?? 0;
    const hidden = plot.hidden ?? false;

    const labels = this.volumeByPriceLabelsFor(plot, pane, render, plan?.bins.length ?? 0);
    // **One x for every label, and it is the edge the bars are anchored to.**
    // The zones grow inward from that edge, so the edge itself is the one
    // column that is inside every bar however short it is — and it keeps the
    // numbers out of the part of the pane the candles are read in, which is
    // the whole point of putting them here rather than at each bar's tip.
    // `volumeBarX(0, 0, side)` is the same collapse the up segment starts from,
    // so the two cannot come to disagree about which edge was chosen.
    const labelX = volumeBarX(0, 0, params.side).x1;
    // The de-emphasised text this file already uses for a glyph nobody drew —
    // `markerColor`'s neutral case reads the same token. The zones are
    // furniture and so is their arithmetic; a label competing with the candles
    // for attention would be worse than no label.
    const labelColor = this.theme.tokens.textSecondary ?? "#9aa4b2";

    // **Every box in the pool, not every zone in the plan** — the pool only ever
    // grows, so a period that needs fewer zones than the last one leaves boxes
    // behind it, and a box left holding the geometry it had is a zone from the
    // previous grid still painted on the pane. The `!bin` branch below hides
    // them, which is the same branch a chart with no bars yet takes.
    for (let i = 0; i < boxes.length >> 1; i++) {
      const upBox = boxes[i * 2]!;
      const downBox = boxes[i * 2 + 1]!;
      const label = labels[i];
      const bin = plan?.bins[i];
      // No bars yet, a peak of zero (nothing traded), or a zone the binner did
      // not produce: hide rather than draw a degenerate box. A chart is built
      // long before its first bar arrives, so this is the state every one of
      // these starts in.
      if (!plan || !bin || !(plan.peak > 0)) {
        upBox.isHidden = true;
        downBox.isHidden = true;
        if (label) label.isHidden = true;
        continue;
      }

      this.placeVolumeLabel(label, bin, labelX, params.side, labelColor, hidden);

      const scale = params.width / plan.peak;
      const upWidth = bin.up * scale;
      const downWidth = bin.down * scale;

      // **Up first, from the edge outwards** — and the offsets are measured
      // from whichever edge was chosen, so `volumeBarX` mirrors and this stacks
      // them once. The two segments are one bar read outwards, not two bars
      // sharing a row: the down volume starts where the up volume ended, and the
      // pair's total length is what the eye compares between zones.
      const upX = volumeBarX(0, upWidth, params.side);
      const downX = volumeBarX(upWidth, upWidth + downWidth, params.side);
      this.placeVolumeBar(upBox, upX.x1, upX.x2, bin, up, opacity, thickness, hidden);
      this.placeVolumeBar(
        downBox,
        downX.x1,
        downX.x2,
        bin,
        down,
        opacity,
        thickness,
        hidden,
      );
    }
  }

  /**
   * This plot's zone labels, created on first use and grown, never re-minted.
   *
   * One per zone rather than two: the label describes the whole bar, not either
   * segment. Everything about the geometry is written by
   * {@link placeVolumeLabel}, so these are born blank for the boxes' reason —
   * one styling path rather than two that can disagree.
   *
   * **`TextAnnotation`, not `NativeTextAnnotation`**, the same choice
   * `drawEventMarkers` made and for the same reason: the native one draws
   * through the WebGL font atlas and carries no `fontWeight`, and a delta that
   * cannot be set apart from the axis labels behind it is a number nobody
   * reads. The cost is a DOM node per zone, which is why the pool matters.
   */
  private volumeByPriceLabelsFor(
    plot: Plot,
    pane: Pane,
    render: PaneRender,
    zones: number,
  ): TextAnnotation[] {
    const wanted = Math.max(0, Math.floor(zones));
    const existing = this.volumeByPriceLabels.get(plot.id) ?? [];
    if (existing.length >= wanted) return existing;

    // Named explicitly, by the rule the boxes and the event glyphs both follow:
    // an annotation that names no axis gets SciChart's `DefaultAxisId`, matches
    // no axis built here, and then simply does not draw — nothing thrown and
    // nothing logged.
    const yAxisId =
      plot.axisRef ?? this.plans.get(pane.id)?.axes[0]?.id ?? DEFAULT_Y_AXIS_ID;
    const labels: TextAnnotation[] = existing;
    for (let i = existing.length; i < wanted; i++) {
      const label = new TextAnnotation({
        yAxisId,
        // The boxes' two modes, for the boxes' reason: x is a share of the plot
        // area so the column stays pinned to the edge through any pan or zoom,
        // y is a price so each label tracks the zone it describes.
        xCoordinateMode: ECoordinateMode.Relative,
        yCoordinateMode: ECoordinateMode.DataValue,
        // **Above the chart, where the bars are below it.** The zones are drawn
        // under the candles deliberately; their labels are not, or a candle
        // would erase the number describing the ground it stands on.
        annotationLayer: EAnnotationLayer.AboveChart,
        verticalAnchorPoint: EVerticalAnchorPoint.Center,
        fontSize: VOLUME_BY_PRICE_LABEL_SIZE,
        fontWeight: "bold",
        isEditable: false,
        isSelected: false,
        isHidden: true,
      });
      render.sub.annotations.add(this.own(label) as never);
      labels.push(label);
    }
    this.volumeByPriceLabels.set(plot.id, labels);
    return labels;
  }

  /**
   * Write one zone's delta onto the label that already draws it.
   *
   * **The delta is `up - down`, in shares, signed** — the number the two
   * segments are already drawn to compare, said once instead of left to the eye
   * to estimate from two lengths that share an edge. A positive sign is written
   * explicitly because the interesting reading is the *direction* of the
   * imbalance, and a bare `12.40M` beside a `-3.21M` reads as a magnitude.
   *
   * A zone that saw no volume gets no label rather than a `0`: the grid spans
   * the period's whole close range, so empty zones are ordinary — the profile
   * is mostly gaps at the extremes — and a column of zeros down the edge of the
   * pane is noise that looks like data.
   */
  private placeVolumeLabel(
    label: TextAnnotation | undefined,
    bin: VolumeByPricePlan["bins"][number],
    x: number,
    side: VolumeByPriceSide,
    color: string,
    hidden: boolean,
  ): void {
    if (!label) return;
    const delta = bin.up - bin.down;
    const mid = (bin.low + bin.high) / 2;
    if (hidden || bin.volume <= 0 || !Number.isFinite(delta) || !Number.isFinite(mid)) {
      label.isHidden = true;
      return;
    }
    // Anchored on the side the profile is, so the text runs *into* the band and
    // never past the edge it is measured from. Right-anchored at the right
    // edge; left-anchored at the left one.
    label.horizontalAnchorPoint =
      side === "right" ? EHorizontalAnchorPoint.Right : EHorizontalAnchorPoint.Left;
    label.x1 = x;
    label.y1 = mid;
    // `legendValue` is what the legend prints a volume cell with, so a zone's
    // delta abbreviates exactly as the volume pane's own reading does. One
    // rounding rule for the same quantity in two places.
    label.text = `${delta > 0 ? "+" : ""}${legendValue(delta)}`;
    label.textColor = color;
    label.isHidden = false;
  }

  /**
   * Write one segment's geometry and colour onto the box that already draws it.
   *
   * A zero-width segment is hidden rather than drawn: a zone that saw no down
   * volume at all is common, and a box whose two x coordinates are equal is a
   * hairline at the pane edge that reads as a tick nobody asked for. The
   * finiteness guard is for the same class of thing from the other direction —
   * a `NaN` volume in the period (a symbol that did not trade) would otherwise
   * be handed to the renderer as a coordinate.
   */
  private placeVolumeBar(
    box: BoxAnnotation,
    x1: number,
    x2: number,
    bin: VolumeByPricePlan["bins"][number],
    color: string,
    opacity: number,
    thickness: number,
    hidden: boolean,
  ): void {
    if (hidden || !Number.isFinite(x1) || !Number.isFinite(x2) || x2 <= x1) {
      box.isHidden = true;
      return;
    }
    box.x1 = x1;
    box.x2 = x2;
    // The zone's own edges, drawn edge to edge with no gap between zones. A gap
    // would very likely read better — StockCharts leaves one — but the *size* of
    // one is precisely the kind of number this feature has already learned not
    // to invent. `low` and `high` are what the binner says the zone is, so that
    // is what gets drawn until somebody has looked at it on screen and said.
    box.y1 = bin.low;
    box.y2 = bin.high;
    box.fill = color;
    box.stroke = thickness > 0 ? color : "transparent";
    box.strokeThickness = thickness;
    box.opacity = opacity;
    box.isHidden = false;
  }

  /**
   * This plot's boxes, created on first use and reused forever after.
   *
   * Two per zone and a fixed count, because the bin count is a constant — see
   * {@link volumeByPriceBoxes}. They are born hidden and blank; everything about
   * their appearance is written by {@link paintVolumeByPrice}, so there is one
   * styling path rather than two that can disagree.
   *
   * The Y axis is named explicitly and by the same rule a series uses. An
   * annotation that names none gets SciChart's `DefaultAxisId`, which matches no
   * axis built here — and it then simply does not draw, with nothing thrown and
   * nothing logged.
   *
   * **The set is sized from the count the caller asks for, and only ever grows.**
   * Trusting the rebuild was the first design here and it was wrong twice over:
   * the allocation was hardcoded to twelve while the painter looped the
   * parameter, so `bins: 20` indexed past the end of the array; and relying on a
   * teardown to fix a cache is relying on something this class cannot see.
   *
   * Growing rather than re-sizing is what ATR sizing added, and it is about the
   * `visible` period: the zone count there is a consequence of where the window
   * is, so it changes as you pan. Re-minting on every change would destroy and
   * rebuild annotations on pointer-move frames. A surplus box costs nothing —
   * the painter hides it — and a pan back to a wider window reuses what is
   * already there.
   */
  private volumeByPriceBoxesFor(
    plot: Plot,
    pane: Pane,
    render: PaneRender,
    zones: number,
  ): BoxAnnotation[] {
    // Two boxes per zone — the up segment and the down one.
    const wanted = Math.max(0, Math.floor(zones)) * 2;
    const existing = this.volumeByPriceBoxes.get(plot.id) ?? [];
    if (existing.length >= wanted) return existing;

    const yAxisId =
      plot.axisRef ?? this.plans.get(pane.id)?.axes[0]?.id ?? DEFAULT_Y_AXIS_ID;
    const boxes: BoxAnnotation[] = existing;
    for (let i = existing.length; i < wanted; i++) {
      const box = new BoxAnnotation({
        yAxisId,
        // **The two axes do not share a coordinate mode, and that is the whole
        // mechanism.** x is a share of the plot area, so the bars stay pinned to
        // its left edge through any pan or zoom; y is a price, so each zone
        // tracks the axis it describes.
        xCoordinateMode: ECoordinateMode.Relative,
        yCoordinateMode: ECoordinateMode.DataValue,
        // Behind the candles. These are the ground the bars are read against,
        // and painted over them they would hide what they are describing.
        annotationLayer: EAnnotationLayer.BelowChart,
        // Nobody drew these and nobody may move them: a grip here would offer to
        // edit a measurement.
        isEditable: false,
        isSelected: false,
        isHidden: true,
      });
      render.sub.annotations.add(this.own(box) as never);
      boxes.push(box);
    }
    this.volumeByPriceBoxes.set(plot.id, boxes);
    return boxes;
  }

  /**
   * The loaded bars as `(close, volume)` pairs, oldest first.
   *
   * **Assembled from two plots, because no delta is addressed to this one.** The
   * closes are the pane's own candles — `PlotRender.values` is the close for an
   * OHLC-fed plot — and the volumes come from whatever plot is drawing the
   * `volume` column, which lives in a different pane. Both are appended from the
   * same `x` array bar for bar, so index `i` is the same session in each, and
   * the shorter one ends the pair: a candle series is trimmed to the last real
   * bar while an indicator column keeps its projected `NaN` tail, so the closes
   * are normally what runs out first.
   *
   * Empty when either side is missing, which draws nothing. That is the honest
   * degradation and the one this codebase keeps choosing — a Volume-by-Price
   * computed off closes alone would be a picture of the price range with no
   * volume in it at all, and it would look entirely plausible.
   */
  /**
   * Bar times for this pane's candles, index-aligned with the bars above.
   *
   * Same source and same discriminator as `volumeByPriceBars` — the `ohlc`-fed
   * plot — so a range found in these indexes the array that one returns.
   */
  private volumeByPriceTimes(paneId: Ulid): number[] {
    const render = this.panes.get(paneId);
    if (!render) return [];
    const candles = [...render.byId.values()].find((pr) => pr.dataKind === "ohlc");
    return candles?.xs ?? [];
  }

  private volumeByPriceBars(paneId: Ulid): VolumeByPriceBar[] {
    const render = this.panes.get(paneId);
    if (!render) return [];
    // By **data kind**, not by name or position: OHLC-fed is what "the bars"
    // means here, the same discriminator `fitYAxes` uses to tell a price series
    // from an overlay drawn on the price axis.
    const candles = [...render.byId.values()].find((pr) => pr.dataKind === "ohlc");
    const volume = this.volumeValues();
    if (!candles || !volume) return [];

    const n = Math.min(candles.values.length, volume.length);
    const bars: VolumeByPriceBar[] = [];
    for (let i = 0; i < n; i++) {
      // **The extremes are for the ATR sizing and nothing else.** The binning is
      // defined on closes and stays defined on closes; a true range needs the
      // high and the low, and they are already here because a bar-anchored
      // signal needs them too. Each is passed only when it is a real number —
      // the plan layer degrades a bar with no extremes to its close, and an
      // `undefined` is how it is told to.
      const high = candles.highs?.[i];
      const low = candles.lows?.[i];
      bars.push({
        close: candles.values[i]!,
        volume: volume[i]!,
        ...(Number.isFinite(high) ? { high: high! } : {}),
        ...(Number.isFinite(low) ? { low: low! } : {}),
      });
    }
    return bars;
  }

  /**
   * The `volume` column, wherever on this chart it is being drawn.
   *
   * Every pane, because the volume histogram is in a pane of its own and the
   * Volume-by-Price plot is in the price pane. The first plot fed by the alias
   * is as good as any — they all read the same column, which is exactly why
   * `byAlias` holds a list.
   */
  private volumeValues(): number[] | undefined {
    for (const pane of this.panes.values()) {
      const fed = pane.byAlias.get(VOLUME_BY_PRICE_SOURCE_ALIAS);
      if (fed?.length) return fed[0]!.values;
    }
    return undefined;
  }

  /**
   * Switch themes **without rebuilding** — the whole point of resolving colors
   * at render time (D13).
   *
   * A rebuild would work and be far less code, but it destroys the WebGL
   * surface, drops every loaded series, refetches the data and resets the zoom
   * window. A theme switch is a restyle: the model did not change, so the data
   * must not move.
   *
   * Two halves, because SciChart splits ownership:
   *  - chart *chrome* (canvas, gridlines, tick labels, cursor) belongs to the
   *    surface's theme provider, applied to the parent **and every sub-surface**
   *    — `applyTheme` walks a surface's own children, and sub-surfaces are not
   *    among the parent's, so skipping them leaves every pane on the old canvas;
   *  - *series* colors come from the model, so each pane's plan is re-resolved
   *    against the new theme and reapplied per series.
   *
   * Safe to call before {@link buildGroup}: the theme is stored and the build
   * picks it up.
   */
  applyTheme(theme: Theme): void {
    this.theme = theme;
    // The forming-bar band takes the new theme's `warn` like the projections do.
    if (this.formingBar) this.setFormingBar(this.formingBar);
    for (const [layer, { list }] of this.barHighlightLayers) this.setBarHighlights(list, layer);
    for (const [layer, { list }] of this.spanLayers) this.setSpans(list, layer);
    if (this.estimateInput.length > 0) this.setEstimateLines(this.estimateInput);
    if (this.extMarker) {
      this.extMarker.axisLabelFill = this.theme.tokens.warn ?? "#e3b341";
      this.extMarker.axisLabelStroke = this.theme.tokens.background ?? "#0e1116";
    }
    const provider = this.surfaceTheme();

    /**
     * **The application-wide default, which is where SciChart's own defaults
     * come from.**
     *
     * A theme provider handed to a surface themes what that surface *applies*.
     * It does not reach anything SciChart constructs, because a constructed
     * object reads `SciChartSurfaceBase.DEFAULT_THEME` — the static the library
     * documents as "the application-wide default theme" — and copies the value
     * onto itself there and then. Roughly two dozen properties work that way:
     * `tickTextBrush` and `axisBandsFill` on an axis, `annotationSelectionStroke`
     * on an annotation, `majorGridLineBrush`, `cursorLineBrush`,
     * `lineSeriesColor`, the wick and body colours, and more.
     *
     * Both bugs that came from this were found one at a time and fixed one at a
     * time — the axes wearing SciChart's dark tick labels on a white canvas, and
     * a selected drawing tinted red over whatever colour the user chose. Setting
     * the static closes the rest of the list at once, including the parts nothing
     * here constructs: the drawings the financial-tools palette places for
     * itself now take this theme too, where before they could only ever look
     * like the library's idea of a chart.
     *
     * Assigned on every theme change and before anything else, so an object
     * constructed later in this same call already sees the new one.
     *
     * It is application-wide, and that is safe here for a reason worth stating:
     * the theme is a property of the *workspace*, so every chart on screen is
     * always showing the same one. Two surfaces wearing different themes would
     * need this to move somewhere per-surface, and there is nowhere for it to go.
     */
    SciChartSurfaceBase.DEFAULT_THEME = provider;

    const parent = this.parentSurface;
    if (!parent) return;
    parent.applyTheme(provider);

    for (const pane of this.paneModels) {
      const render = this.panes.get(pane.id);
      if (!render) continue;
      render.sub.applyTheme(provider);

      const plan = buildPanePlan(pane, theme, this.gridLines, this.axisSpecs, this.eventKinds);
      this.plans.set(pane.id, plan);
      for (const sp of plan.series) {
        const pr = render.byId.get(sp.plotId);
        if (pr) this.restyleSeries(pr, sp);
      }

      // Their stroke is a theme token, so they have to be re-resolved with
      // everything else — and replaced rather than added to, or each toggle
      // would leave the previous set underneath.
      this.syncReferenceLines(pane.id, plan);

      // The crosshair's stroke is a theme token too, and nothing else here
      // reaches it: `applyTheme` on a sub-surface restyles axes and series but
      // leaves modifiers alone. Without this a switch out of White kept the
      // light grey crosshair over a dark chart.
      for (const modifier of render.sub.chartModifiers.asArray()) {
        if (modifier instanceof CursorModifier) modifier.crosshairStroke = this.crosshairColor();
      }
    }

    // `applyTheme` on a surface rewrites its axes' gridline styles from the
    // theme provider, which has no notion of a dash — so a theme switch would
    // silently turn dotted gridlines solid. Re-apply after, not before.
    this.setGridLines(this.gridLines);

    // The glyphs hold a resolved colour, not a token, so they are re-drawn for
    // the same reason every series is: `tone` means positive, and what positive
    // *looks like* is the new theme's answer (D13).
    this.drawEventMarkers();

    // And the Volume-by-Price bars, for exactly that reason: their up and down
    // segments are the `positive`/`negative` tokens, and a token that survived a
    // theme change unchanged is the one thing a token must not do (D13). They
    // are not series, so the loop above never reaches them — and this is also
    // the call that paints them at build time, since `buildGroup` ends by
    // running this very method.
    this.drawVolumeByPrice();

    parent.invalidateElement();
  }

  /**
   * Keep every pane's X window in lock-step, and refit Y whenever it moves.
   *
   * Panes share one X range (the same bars, so the same times), so we mirror the
   * visibleRange directly. A guard flag breaks the change→change feedback loop.
   *
   * **The Y refit is subscribed even on a single pane**, which is why the
   * early return that used to sit here is now only around the mirroring. Zoom
   * and pan reach the axis without passing through any method of ours, so this
   * subscription is the only place that can notice — and a one-pane chart is
   * the common case, so returning early would have left the fit running
   * everywhere except the default layout.
   */
  private syncXAxes(): void {
    const xAxes = [...this.panes.values()].map((p) => p.sub.xAxes.get(0)).filter(Boolean);
    if (xAxes.length === 0) return;

    let syncing = false;
    for (const src of xAxes) {
      src.visibleRangeChanged.subscribe((args) => {
        if (syncing || !args) return;
        if (xAxes.length > 1) {
          syncing = true;
          for (const other of xAxes) {
            if (other !== src) other.visibleRange = args.visibleRange;
          }
          syncing = false;
        }
        // The window moved, so the bars in it did: refit. This is the path a
        // wheel zoom and a pan both take, and the one `EAutoRange.Always` used
        // to serve.
        this.fitYAxes();
        // A zoom changes how far apart the `E`s are, which decides whether
        // their YoY notes fit.
        this.layoutMarkerNotes();
        // And re-bin any Volume-by-Price plot on its `visible` period, whose
        // whole answer is "which bars are on screen". Coalesced to a frame:
        // this fires continuously through a drag, and a profile recomputed
        // twice within one paint is a profile computed once too often.
        this.scheduleVolumeByPriceRepaint();
        // Once per change, not once per pane: a mirrored axis fires while
        // `syncing` is set and returned above.
        this.barWindowListener?.({ min: args.visibleRange.min, max: args.visibleRange.max });
      });
    }
  }

  /**
   * Re-bin the `visible` profiles, at most once per animation frame.
   *
   * **Only the `visible` period needs this, and only it pays for it.**
   * `drawVolumeByPrice` re-reads each plot's own parameters, so a chart holding
   * only `lookback` profiles recomputes the same answer — wasteful per frame,
   * which is why the schedule collapses a drag's worth of events into one paint
   * rather than letting each through.
   *
   * `requestAnimationFrame` rather than a timer: the work exists to be seen, so
   * the right cadence is the one the browser is already painting at. Falls
   * through to a direct call where there is none, which is every test
   * environment and no browser.
   */
  private volumeByPriceFrame?: number;

  private scheduleVolumeByPriceRepaint(): void {
    if (this.volumeByPriceFrame !== undefined) return;
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : undefined;
    if (!raf) {
      this.drawVolumeByPrice();
      return;
    }
    this.volumeByPriceFrame = raf(() => {
      this.volumeByPriceFrame = undefined;
      this.drawVolumeByPrice();
      this.parentSurface?.invalidateElement();
    });
  }

  /**
   * Draw the reference lines an indicator declares — RSI's 30/70, MACD's zero.
   *
   * **Replaces rather than appends.** It runs on build *and* on every theme
   * switch (their stroke is a token, so it has to re-resolve), and a theme
   * change that added a second set on top of the first would darken the lines a
   * little more each time the user toggled — visible only after several.
   *
   * They are annotations on the surface and nothing else: never written back to
   * `Pane.annotations`, so they cannot go stale against a corrected catalog and
   * no workspace grows rows for them. The consequence to accept is that they
   * are not individually draggable or deletable, which is the right trade for
   * something that follows from the indicator rather than from the user.
   */
  private syncReferenceLines(paneId: string, plan: PanePlan): void {
    const render = this.panes.get(paneId);
    if (!render) return;

    for (const existing of this.referenceLines.get(paneId) ?? []) {
      render.sub.annotations.remove(existing as never);
      existing.delete();
    }
    this.referenceLines.delete(paneId);
    if (plan.referenceLines.length === 0) return;

    const drawn = plan.referenceLines.map((line: ReferenceLinePlan) => {
      const axisId = line.axisRef ?? plan.axes[0]?.id;
      const annotation = new HorizontalLineAnnotation({
        y1: axisId !== undefined && this.signedLogAxes.has(axisId) ? toSignedLog(line.value) : line.value,
        stroke: line.stroke,
        strokeThickness: 1,
        strokeDashArray: line.dash,
        // A reference line is scenery: it must not intercept the pointer, or it
        // would swallow clicks meant for the plot area behind it.
        isEditable: false,
        showLabel: Boolean(line.label),
        axisLabelFill: line.stroke,
        ...(line.axisRef ? { yAxisId: line.axisRef } : {}),
      });
      render.sub.annotations.add(this.own(annotation) as never);
      return annotation;
    });
    this.referenceLines.set(paneId, drawn);
    this.syncLevelFills(paneId, plan);
  }

  /**
   * Shade a line past a level — RSI above 70, below 30 (`AxisLevel.fill`,
   * owner 2026-11-02).
   *
   * **A band between the line and the level**, so SciChart interpolates the
   * crossing and the fill meets the line exactly there: `y` is the line, `y1`
   * the level held flat, and of the band's two fills only the side asked for
   * is coloured — `fill` is drawn where y < y1, `fillY1` where y > y1. Inserted
   * at the bottom of the pane so the line draws over it.
   *
   * Replaces like {@link syncReferenceLines}, which calls it, for the same
   * reason; the data is rebuilt from the line by {@link refreshLevelFills}.
   */
  private syncLevelFills(paneId: string, plan: PanePlan): void {
    const render = this.panes.get(paneId);
    if (!render) return;
    for (const old of this.levelFills.get(paneId) ?? []) {
      render.sub.renderableSeries.remove(old.series);
      old.series.delete();
      old.data.delete();
    }
    this.levelFills.delete(paneId);
    const wasm = this.wasmContext;
    if (!wasm) return;

    const fills: LevelFillRender[] = [];
    for (const line of plan.referenceLines) {
      if (!line.fill) continue;
      const axisId = line.axisRef ?? plan.axes[0]?.id;
      const data = new XyyDataSeries(wasm);
      const color = atOpacity(line.fill.color, line.fill.opacity);
      const clear = "#00000000";
      const series = new FastBandRenderableSeries(wasm, {
        dataSeries: data,
        ...(axisId ? { yAxisId: axisId } : {}),
        stroke: clear,
        strokeY1: clear,
        strokeThickness: 0,
        // SciChart: `fill` where y < y1, `fillY1` where y > y1
        // (`BaseBandRenderableSeries.d.ts`). y is the line, y1 the level.
        fill: line.fill.side === "below" ? color : clear,
        fillY1: line.fill.side === "above" ? color : clear,
      });
      render.sub.renderableSeries.insert(0, series);
      fills.push({
        series,
        data,
        axisId,
        level: axisId !== undefined && this.signedLogAxes.has(axisId) ? toSignedLog(line.value) : line.value,
      });
    }
    if (fills.length === 0) return;
    this.levelFills.set(paneId, fills);
    this.refreshLevelFills(paneId);
  }

  /**
   * Copy each fill's line into its band: the first visible, ordinary plot on
   * the level's axis — RSI's one line. Rebuilt whole, which is a few thousand
   * numbers and only for panes that have a fill.
   */
  private refreshLevelFills(paneId: string): void {
    const fills = this.levelFills.get(paneId);
    const pane = this.panes.get(paneId);
    if (!fills || !pane) return;
    for (const fill of fills) {
      const source = [...pane.byId.values()].find(
        (pr) =>
          !pr.signal &&
          !pr.paint &&
          pr.dataKind === "xy" &&
          (fill.axisId === undefined || pr.series.yAxisId === fill.axisId),
      );
      fill.data.clear();
      fill.series.isVisible = Boolean(source) && !source?.hidden;
      if (!source) continue;
      const xs = source.dataSeries.getNativeXValues();
      const n = Math.min(xs.size(), source.values.length);
      const x: number[] = new Array(n);
      const y: number[] = new Array(n);
      const y1: number[] = new Array(n).fill(fill.level);
      for (let i = 0; i < n; i++) {
        x[i] = xs.get(i);
        y[i] = source.values[i]!;
      }
      fill.data.appendRange(x, y, y1);
    }
  }

  /**
   * Re-resolve every pane's reference lines against the injected lookup.
   *
   * Exists because of an ordering fact rather than a preference: the surface is
   * built as soon as the container exists, and the catalog that declares RSI's
   * 30/70 arrives over HTTP some time after. A snapshot taken at construction
   * would be empty forever, so the lookup reads through to a live cache and the
   * host calls this once the fetch resolves.
   *
   * A repaint, never a rebuild — the lines are annotations on a surface that
   * already holds its data and its zoom window.
   */
  refreshReferenceLines(): void {
    for (const pane of this.paneModels) {
      const plan = buildPanePlan(pane, this.theme, this.gridLines, this.axisSpecs, this.eventKinds);
      this.plans.set(pane.id, plan);
      this.syncReferenceLines(pane.id, plan);
      this.syncEventKinds(pane.id, plan);
    }
    this.parentSurface?.invalidateElement();
  }

  /**
   * Re-read a signal whose `eventKind` the catalog has just changed.
   *
   * The catalog is fetched once per page and can land after the first bars, so
   * JAS Timing would otherwise stay drawn as `single` — only its 1s — until the
   * next rebuild. Its marks are re-derived from `values`, which holds the whole
   * column, and re-placed; nothing is refetched.
   */
  private syncEventKinds(paneId: string, plan: PanePlan): void {
    const pane = this.panes.get(paneId);
    if (!pane) return;
    for (const sp of plan.series) {
      const pr = pane.byId.get(sp.plotId);
      const signal = pr?.signal;
      if (!pr || !signal || signal.eventKind === sp.eventKind) continue;
      signal.eventKind = sp.eventKind;
      signal.marks = [];
      for (const point of signalPoints(pr.values, sp.eventKind)) {
        const x = signal.xs[point.index];
        if (x !== undefined) signal.marks.push({ x, index: point.index, direction: point.direction });
      }
      this.placeSignal(pr);
    }
  }

  private buildPaneSubChart(
    parent: SciChartSurface,
    wasmContext: TSciChart,
    pane: Pane,
    position: { x: number; y: number; width: number; height: number },
  ): void {
    const plan = buildPanePlan(pane, this.theme, this.gridLines, this.axisSpecs, this.eventKinds);
    this.plans.set(pane.id, plan);

    const sub = SciChartSubSurface.createSubSurface(parent, { position });

    // **Every sub-surface carries its own padding, and it is what the gap
    // between two panes is made of.** `buildGroup` zeroes the *parent*'s, which
    // is a different thickness — a sub-surface is a `SciChartSurface` and so is
    // born with the same `Thickness.fromNumber(10)` default. Panes are stacked
    // edge to edge by `stackPanes`, so the empty band a reader sees at a seam is
    // one pane's bottom 10px plus the next one's top 10px, DPI-scaled, with a
    // pane's own plot inset from its band by the same amount at either end.
    //
    // Vertical only. The left and right sides are where the Y axis and its
    // labels are laid out — `adjustedPadding` is added to the axis's required
    // size — and narrowing those moves the plot area sideways for a complaint
    // that was about the horizontal seams.
    //
    // **Except the right, which is 2** (owner, 2026-10-13): it is empty space
    // past the Y labels, the same on every pane, so trimming it moves every plot
    // area by the same amount and nothing goes out of line.
    sub.padding = new Thickness(PANE_PADDING_Y, PANE_PADDING_RIGHT, PANE_PADDING_Y, 10);

    // **X axis — `DiscontinuousDateAxis`: gaps collapsed, and x is a time.**
    //
    // It was a `CategoryAxis`, which also collapses non-trading gaps and is why
    // it was chosen — but a `CategoryAxis` positions by a point's *index* and
    // reads its x value only to label it. So every x on this axis was a bar
    // number, and a translation layer grew to bridge that: a drawing stored
    // against a timestamp, drawn at an index, binary-searched between the two,
    // and re-placed on every load because a wider fetch moves every index while
    // leaving every time alone.
    //
    // This axis is SciChart's own answer to the same shape — its docs say
    // "discontinuous timeseries data, such as weekday only financial data",
    // plotted evenly by index but converting *from data values to indexes*
    // itself, from base values it is handed. Same picture, and x is the
    // timestamp the series already carried.
    //
    // Not `DateTimeNumericAxis`, which would put the weekends back.
    // See `docs/plans/x-axis-time-not-index.md`.
    const xAxis = new DiscontinuousDateAxis(wasmContext, {
      autoRange: EAutoRange.Once,
      drawMinorGridLines: false,
    });
    // The base values decide where a time lands, and they arrive with the bars —
    // which is after this. Fed here as well for the rebuild case, where a load
    // has already happened and no further one is coming.
    if (this.baseXValues) xAxis.setBaseXValues(this.baseXValues);
    xAxis.drawLabels = plan.showXLabels;
    // **A label-less axis still reserved a strip, and it was invisible.** Every
    // pane carries its own date axis and only the price pane draws the labels,
    // but SciChart measures a horizontal axis as
    // `max(labelSize, axisThickness × dpr) + (drawTicks ? ticksSize : 0)` —
    // so an indicator pane with no labels still gave up its major tick size
    // (5px, DPI-scaled) at the foot for ticks against dates nobody can read.
    //
    // **Both flags, because `drawTicks` is their OR** (`AxisBase2D.measure`):
    // clearing only the major one leaves the minor one holding the strip open,
    // and the size taken is `getTicksMaxSize()` — the larger of the two — so
    // the pane would look exactly as it did.
    if (!plan.showXLabels) {
      xAxis.drawMajorTickLines = false;
      xAxis.drawMinorTickLines = false;
    } else {
      // **The labelled axis has no tick marks either** (owner, 2026-10-07): the
      // vertical gridlines already mark the same dates, so the ticks only put
      // ~7 px between the `E` markers and the dates. The label padding drops to
      // 2 px above for the same reason; the strip is now the text and little
      // else.
      xAxis.drawMajorTickLines = false;
      xAxis.drawMinorTickLines = false;
      xAxis.labelStyle = { ...xAxis.labelStyle, padding: new Thickness(2, 4, 2, 4) };
    }
    // Both, because they are two properties over one bug: the axis labels and
    // the crosshair readout each format through the provider's own function, so
    // fixing one leaves the other a day out and disagreeing with it.
    const format = timeAxisFormatters(this.timeLabels);
    xAxis.labelProvider.formatLabel = format.axis;
    xAxis.labelProvider.formatCursorLabel = format.cursor;
    this.styleGrid(xAxis, plan.xGrid);
    sub.xAxes.add(xAxis);

    // Y axes — one per plan axis, or a single default if none were specified.
    if (plan.axes.length > 0) {
      for (const ap of plan.axes) sub.yAxes.add(this.createYAxis(wasmContext, ap));
    } else if (plan.signalLanes === 0) {
      // `Never` for `createYAxis`'s reason — `fitYAxes` ranges this one too.
      const fallback = new NumericAxis(wasmContext, {
        autoRange: EAutoRange.Never,
        // **The same 72px every other pane's axis is pinned to.** Without it
        // this axis took its natural width — whatever its widest label happened
        // to need — so the pane's plot area started at a different x from the
        // ones above it and every bar in it sat a few pixels off. On a pane
        // carrying markers that is exactly the failure a marker must not have:
        // the triangle no longer points at the candle it belongs to.
        //
        // It has to be a constructor option. `axisThickness`'s setter writes
        // `axisLengthProperty` while the getter and the layout both read
        // `axisRenderer.axisThickness`, so assigning it does nothing at all —
        // the same SciChart bug the lane axis below documents.
        axisThickness: this.yAxisThickness,
      });
      // A pane with no declared axis still shows gridlines, so it has to be
      // styled too — otherwise "dotted" would apply to some panes and not others.
      this.styleGrid(fallback, { draw: this.gridLines !== "none", minorsPerMajor: 2,
        majorDash: plan.xGrid.majorDash });
      sub.yAxes.add(fallback);
    }
    // …and no `else`: a pane that declares no axis but does carry signals has
    // the lane axis below as its only one. A fallback beside it would print
    // `0.00` labels against a scale whose numbers are lane indices, on the one
    // kind of pane where the axis is meant to be invisible.

    // The lane axis, when the pane carries a signal that actually uses lanes.
    // Fixed range and no labels: the numbers are lane indices, so they mean
    // nothing to a reader, and an axis that fitted them would move every lane
    // the first time one fired — or have no range at all in a pane where
    // nothing has fired yet.
    //
    // Only for *pane*-anchored signals. One riding another plot draws in that
    // plot's units, on that plot's axis, so a lane axis for it would be an
    // invisible axis taking real width for nothing.
    const needsLaneAxis = plan.series.some((sp) => sp.signal?.anchor !== "series" && sp.signal);
    if (needsLaneAxis) {
      const lanes = paneLaneRange(plan.signalLanes);
      const laneAxis = new NumericAxis(wasmContext, {
        id: SIGNAL_AXIS_ID,
        autoRange: EAutoRange.Never,
        visibleRange: new NumberRange(lanes.min, lanes.max),
        // **Flipped, so lane 1 is the TOP row.** Lanes are numbered in plot
        // order — the order the settings list shows — and an unflipped axis
        // puts 1 at the bottom, so the strip read upside down against its own
        // settings and against the value legend, which lists plots top to
        // bottom. Reported on DOTS, where Squeeze On sat four rows from where
        // the list said it was. Hidden axis, so flipping moves no label.
        flippedCoordinates: true,
        drawLabels: false,
        // **Ticks stay ON, drawn in nothing.** Turning them off is what made
        // this pane seven pixels wider than every other one — measured on a
        // five-pane chart: four panes 1971 wide, the signal strip 1978, same
        // left edge, so its bars stretched and every marker drifted right,
        // worst at the newest bar.
        //
        // `axisThickness` does not cover it. SciChart's own layout is
        // `max(labelSize, axisThickness × dpr) + (drawTicks ? ticksSize : 0)`,
        // and `drawTicks` is `drawMinorTickLines || drawMajorTickLines` — so an
        // axis with its ticks off loses the tick allowance outright, whatever
        // its thickness says. Reserving it by adding a magic 7 to the thickness
        // would be guessing at a default that is theirs to change; letting the
        // same code path run and painting the result transparent cannot drift.
        drawMajorTickLines: true,
        drawMinorTickLines: false,
        drawMajorGridLines: false,
        drawMinorGridLines: false,
        // **Both of these must be constructor options, not assignments.**
        // `axisThickness`'s setter is broken in SciChart 5.2.55: it writes
        // `axisLengthProperty` while the getter and the layout both read
        // `axisRenderer.axisThickness`, so `axis.axisThickness = 72` silently
        // does nothing. Only the constructor writes the renderer's field.
        //
        // Assigned rather than passed, this axis took zero width while every
        // other pane's took 72 — same left edge, a plot area 72px wider, so the
        // strip's bars were stretched against the panes above and every marker
        // drifted right, worst at the newest bar. It is invisible on a chart
        // with nothing to compare against, which is what a signal strip
        // finally provided.
        axisAlignment: EAxisAlignment.Right,
        axisThickness: this.yAxisThickness,
        isVisible: !plan.axes.some((a) => a.visible),
      });
      // Now paint those ticks in nothing, **keeping their size**.
      //
      // The size has to survive: `getTicksMaxSize` reads `tickSize` off this
      // very object — `max(major.tickSize, minor.tickSize) × dpr` — and returns
      // 0 for a style that omits it. Passing `{color, strokeThickness}` as a
      // constructor option would therefore replace the default wholesale and
      // reserve nothing, which is the bug this is fixing, arrived at from the
      // other side. Spreading the default keeps whatever SciChart chose.
      //
      // Assignment is safe here, unlike `axisThickness`: this property has a
      // working setter that invalidates the parent.
      laneAxis.majorTickLineStyle = {
        ...laneAxis.majorTickLineStyle,
        color: "transparent",
        strokeThickness: 0,
      };
      // Not `isVisible: false`. A hidden axis is given no width, so on a strip
      // that carries *only* signals the plot area would sit flush against the
      // pane edge while every other pane is inset by its axis — the markers
      // would be a few pixels out of line with the candles above them, which is
      // the one thing a marker must not be.
      //
      // **Hidden (`isVisible` above) when the pane already has a visible axis**, which then does
      // the inset (owner, 2026-10-13). Visible beside it, this label-less axis
      // stacked a second ~57px strip on the right — Pressure's pane, whose
      // value axis carries -1..1 — and `equalizeYAxisWidths` then grew every
      // other pane's axis to match, so the whole chart lost the width.
      sub.yAxes.add(laneAxis);
    }

    const defaultYAxisId = plan.axes[0]?.id ?? DEFAULT_Y_AXIS_ID;

    // Series — eager: sub-surfaces share one context, so creating one can't tear
    // down another's data (the failure mode of separate top-level surfaces).
    const state: PaneRender = {
      sub,
      byId: new Map(),
      byAlias: new Map(),
      interaction: {
        zoomPan: new ZoomPanModifier(),
        wheelZoom: new ManualWheelZoomModifier(),
        // Scaling, not panning: dragging an axis to *stretch* it is the gesture
        // people arrive with, and panning is already what dragging the plot area
        // does — two ways to pan and no way to scale would be the wrong pair.
        xAxisDrag: new XAxisDragModifier({ dragMode: EDragMode.Scaling }),
        yAxisDrag: new ManualYAxisDragModifier({ dragMode: EDragMode.Scaling }),
      },
    };
    // A hand-scaled Y axis stays where it was put — `setYManual`.
    state.interaction.yAxisDrag.scaled = () => this.markPaneManual(state);
    state.interaction.wheelZoom.scaled = () => this.markPaneManual(state);
    for (const sp of plan.series) {
      const pr = this.createSeries(wasmContext, sp, defaultYAxisId);
      sub.renderableSeries.add(pr.series);
      // A signal is one plot and two renderables, so its second half is added
      // here rather than inside `createSeries` — which owns one PlotRender and
      // has no surface to add anything to.
      if (pr.signal) sub.renderableSeries.add(pr.signal.series);
      state.byId.set(pr.plotId, pr);
      if (pr.alias) {
        const fed = state.byAlias.get(pr.alias);
        if (fed) fed.push(pr);
        else state.byAlias.set(pr.alias, [pr]);
      }
    }

    // **Registered before the anchors are attached, not after.** `anchorFor`,
    // `placeSignal` and `rangeOf` all resolve through `this.panes` rather than
    // through the pane they were handed, so a pane that is not in the map yet
    // has no plots as far as any of them is concerned. Attaching first meant
    // every bar-anchored signal missed its anchor and hit the `continue`: no
    // mirror axis, no `visibleRangeChanged` subscription, and a `yAxisId` left
    // on a lane axis whose range is 0.5–1.5 while the y written to it is a
    // price. Registering here costs nothing — the pane is fully populated by
    // this line, and the modifiers added below are not read by any of the three.
    this.panes.set(pane.id, state);

    // After every plot exists, because a signal can be created before the plot
    // it rides and a yAxisId written now would name an axis nothing draws on.
    this.attachSignalAnchors(wasmContext, state);

    this.syncReferenceLines(pane.id, plan);

    // The drawing tool, attached whether or not one is ever armed. Adding it on
    // demand would mean creating a modifier during the mouse-down that is meant
    // to start the gesture, which arrives after the surface has already routed
    // that event. It is inert until `setDrawingTool` gives it a kind.
    const drawing = new DrawingModifier({
      onPreview: (kind, start, current, path) =>
        this.previewDrawing(pane.id, kind, start, current, path),
      onCommit: (kind, start, end, path) =>
        this.commitDrawing(pane.id, kind, start, end, path),
      // Straight through. Only one pane can hold a placed point at a time — the
      // press that placed it landed in exactly one — so there is nothing to
      // aggregate, and a per-pane id would tell the host something it cannot use.
      onPlacingChange: (placing) => this.placingHandler?.(placing),
    });
    // A rebuild must not silently disarm. `buildGroup` runs again on any
    // structural change — a symbol swap, a new indicator — and a tool armed
    // before it would otherwise be lit in the palette while the pointer did
    // nothing, which reads as the feature being broken.
    drawing.tool = this.drawingTool;
    this.drawingModifiers.set(pane.id, drawing);

    const placement = new MultiPointAnnotationPlacementModifier({});
    this.placementModifiers.set(pane.id, placement);

    const freehand = new FreehandDrawingModifier({});
    this.freehandModifiers.set(pane.id, freehand);

    sub.chartModifiers.add(
      // The financial-tools modifiers go first for the same reason the drawing
      // one does: a click that is placing a point, or a drag that is a stroke,
      // must not also pan.
      placement,
      freehand,
      // First, so an armed tool takes the press before zoom and pan see it —
      // modifiers are offered an event in the order they were added, and a drag
      // that both draws a trendline and pans the chart draws it against bars
      // that moved underneath it.
      drawing,
      state.interaction.zoomPan,
      // Direction comes from `ChartGroup.zoomAxis`, defaulting to X. Zooming both
      // axes fights `EAutoRange.Always`: the wheel sets a Y range and the next
      // render refits it, so the chart appears to resist the gesture. X alone
      // lets Y do what it is for — fit whatever is now visible to the pane's
      // height. That is the default rather than the law because an axis pinned to
      // a fixed range has no refit to fight.
      state.interaction.wheelZoom,
      state.interaction.xAxisDrag,
      state.interaction.yAxisDrag,
      new ZoomExtentsModifier(),
      // **The crosshair alone, and no tooltip on it.**
      //
      // `RolloverModifier` used to run alongside this, drawing a vertical line
      // and a tooltip box per series. Two modifiers meant two vertical lines on
      // one pointer — the rollover's solid one and the cursor's dashed one, a
      // pixel or two apart because they round the same x differently — which
      // read as the chart being out of alignment with itself.
      //
      // The tooltip went with it rather than being turned off in place: a box
      // that follows the pointer covers the candles it is describing, and the
      // axis labels below already give the two numbers under the crosshair
      // without occluding anything.
      this.trackHover(
        new HoverCursorModifier({
          modifierGroup: this.modifierGroup,
          showTooltip: false,
          showAxisLabels: true,
          // **`text.secondary`, not `grid`.** It was the gridline colour, which
          // is the one colour on the chart guaranteed to hide it: `#20242e` on
          // Classic and `#e6eaf0` on White are chosen to sit just above the
          // background and be ignorable, and a crosshair drawn in them vanishes
          // into the grid it crosses. `text.secondary` is the readable-but-
          // subordinate neutral in every theme — `#9aa4b2`, `#a8a8a8`, `#55606f`
          // — so it reads on a dark ground and a light one without inverting.
          //
          // Not `accent`: that is a plot colour here, and a blue crosshair over
          // a chart of blue indicator lines is one more line to disambiguate.
          crosshairStroke: this.crosshairColor(),
          crosshairStrokeDashArray: [3, 3],
          // **The horizontal line only in the pane the pointer is actually in.**
          //
          // Panes share one parent surface, and SciChart's `processSubChartEvent`
          // fans a pointer move from the pane under the cursor out to every other
          // pane in that surface — remapping the point *proportionally* between
          // the two `seriesViewRect`s (`(y - master.y) * slave.h / master.h`).
          // Each pane then drew a full crosshair. The vertical line is the point
          // of that fan-out: same bar, every pane, which is what lines them up.
          // The horizontal one is not, and it is worse than redundant — hover a
          // candle at 152 and the RSI pane draws a line at *the same fraction of
          // its own height*, so a reader gets 43.7 on the axis for a level nobody
          // pointed at and no series crosses. A pane an eighth the height of the
          // price pane makes the error eight times smaller and no less wrong.
          //
          // `showOnOthersInGroup` is SciChart's own switch for this, and it is
          // the only safe one: `update()` gates the *un-hide* of each line on
          // `isMasterEvent || showOnOthersInGroup.<line>`, so a non-master pane
          // simply never reveals its Y line, and the `Leave` it gets on the way
          // out hides both as before. Flipping `showYLine` per move instead would
          // work and cost far more — its setter constructs and `deleteSafe`s the
          // annotation on every change, i.e. an allocation and a free per pane
          // per pointer move.
          showOnOthersInGroup: { xLine: true, yLine: false, tooltip: false },
        }),
      ),
    );

    this.applyInteractions(state);
    this.lastPaneId = pane.id;
  }

  /**
   * The crosshair stroke, resolved from the current theme.
   *
   * One method rather than two call sites, because it is read once when the
   * modifier is built and again on every theme switch — and the two drifting
   * apart is invisible until somebody changes theme with the pointer over the
   * chart, which is not a thing anyone does on purpose.
   */
  private crosshairColor(): string {
    return this.theme.tokens.textSecondary ?? "#8a8f98";
  }

  /**
   * Change axis drag / zoom direction without rebuilding.
   *
   * Both are persisted `ChartGroup` settings, and both are property writes on
   * modifiers that already exist — so the same bargain as {@link applyTheme} and
   * {@link setGridLines}: a rebuild would destroy the WebGL surface, drop the
   * loaded bars and reset the zoom window to change a boolean.
   *
   * Safe before {@link buildGroup}: the plan is stored and the build picks it up.
   */
  private trackHover(modifier: HoverCursorModifier): HoverCursorModifier {
    modifier.report = this.hoverHandler;
    modifier.probe = (surface, time, point) => this.probePlot(surface, time, point);
    this.hoverCursors.push(modifier);
    return modifier;
  }

  /**
   * Be told what time the pointer is on, or `null` when it is on none.
   *
   * A **time**, not a value: reading a series is the host's, which already holds
   * the decoded columns the chart was fed and would otherwise be asking the
   * renderer to hand back what it was given. This only knows the one thing the
   * renderer knows and the host cannot — where the pointer is on the axis.
   *
   * Safe before {@link buildGroup} and safe across a rebuild: the handler is
   * retained and re-attached to each pane's modifier as it is created. Without
   * that a symbol change would leave the legend frozen on the bar it last read,
   * with no error and a chart that otherwise works.
   */
  onHoverTime(handler: ((time: number | null) => void) | null): void {
    this.hoverHandler = handler;
    for (const cursor of this.hoverCursors) cursor.report = handler;
  }

  /**
   * Be told which line the pointer is on, or `null` when it is on none
   * (charts.md, "The line under the pointer"). The line itself is drawn one
   * pixel wider while it is the one.
   *
   * Only the plot id and where to put a chip: naming the plot and formatting
   * its value are the host's, through the same `legendRows` the legend uses.
   * `null` turns the whole thing off, emphasis included.
   */
  onHoverPlot(handler: ((hit: PlotHit | null) => void) | null): void {
    this.plotHoverHandler = handler;
    if (!handler) this.emphasise(null);
  }

  /** The hit test behind {@link onHoverPlot}, run on each pointer move. */
  private probePlot(surface: SciChartSubSurface | null, time: number | null, point: Point | null): void {
    if (!this.plotHoverHandler) return;
    const hit = surface && time !== null && point ? this.plotAt(surface, time, point) : undefined;
    this.emphasise(hit?.pr ?? null);
    if (!hit || !point) {
      this.plotHoverHandler(null);
      return;
    }
    let paneId: Ulid | undefined;
    for (const [id, pane] of this.panes) if (pane.sub === surface) paneId = id;
    // Canvas -> client. **SciChart's points are device pixels**: it builds
    // `mousePoint` as `offsetX * DpiHelper.PIXEL_RATIO`, and `seriesViewRect`
    // is in the same space. Added to a CSS-pixel box unscaled, the chip landed
    // 1.5x too far right and down at 150% display scaling (owner, 2026-10-07).
    const box = this.container?.getBoundingClientRect();
    const rect = surface!.seriesViewRect;
    const ratio = DpiHelper.PIXEL_RATIO || 1;
    const left = box?.left ?? 0;
    this.plotHoverHandler({
      plotId: hit.pr.plotId,
      paneId: paneId ?? "",
      x: left + point.x / ratio,
      y: (box?.top ?? 0) + point.y / ratio,
      left: left + rect.left / ratio,
      right: left + rect.right / ratio,
    });
  }

  /**
   * The line nearest the pointer at the snapped bar, within
   * {@link PLOT_HIT_TOLERANCE} pixels. Vertical distance only: the value
   * reported is the one at the bar the crosshair stands on.
   */
  private plotAt(surface: SciChartSubSurface, time: number, point: Point): { pr: PlotRender } | undefined {
    let pane: PaneRender | undefined;
    for (const p of this.panes.values()) if (p.sub === surface) pane = p;
    if (!pane) return undefined;
    const local = translateFromCanvasToSeriesViewRect(point, surface.seriesViewRect);
    if (!local) return undefined;
    const candidates: { pr: PlotRender; y: number }[] = [];
    const seen: Record<string, unknown>[] = [];
    this.lastHitProbe = { time, pointerY: local.y, seen };
    for (const pr of pane.byId.values()) {
      seen.push({
        alias: pr.alias,
        kind: pr.seriesKind,
        hidden: pr.hidden ?? false,
        paint: !!pr.paint,
        signal: !!pr.signal,
        hittable: isHittableKind(pr.seriesKind),
      });
      if (pr.hidden || pr.paint || !isHittableKind(pr.seriesKind)) continue;
      // A signal draws as two series split by sign; whichever holds a marker
      // at this bar is the one on screen. Both are dense, NaN where unfired.
      const halves: [IRenderableSeries, AnyDataSeries][] = [[pr.series, pr.dataSeries]];
      if (pr.signal) halves.push([pr.signal.series, pr.signal.dataSeries]);
      for (const [series, data] of halves) {
        const row = seen[seen.length - 1]!;
        if (!series.isVisible) {
          row.invisible = true;
          continue;
        }
        const xs = data.getNativeXValues();
        const i = indexOfTime((k) => xs.get(k), xs.size(), time);
        const value = i < 0 ? NaN : (data as XyDataSeries).getNativeYValues().get(i);
        const calc = series.yAxis?.getCurrentCoordinateCalculator();
        (row.halves ??= [] as unknown[]) as unknown;
        (row.halves as unknown[]).push({
          axis: series.yAxisId,
          hasAxis: !!series.yAxis,
          points: xs.size(),
          index: i,
          value,
          y: calc && Number.isFinite(value) ? calc.getCoordinate(value) : null,
        });
        if (i < 0 || !calc || !Number.isFinite(value)) continue;
        candidates.push({ pr, y: calc.getCoordinate(value) });
      }
    }
    // The 6 px is CSS pixels; these coordinates are device pixels.
    return nearestPlot(candidates, local.y, PLOT_HIT_TOLERANCE * (DpiHelper.PIXEL_RATIO || 1));
  }

  /**
   * Draw `pr` one pixel wider and put the previous one back.
   *
   * The restore writes the base width back only while the series still carries
   * the +1 it was given: a restyle that landed in between (the ⚙ width spinner)
   * is the user's width now, and is left alone.
   */
  private emphasise(pr: PlotRender | null): void {
    const was = this.emphasised;
    if (was?.pr === pr) return;
    if (was) {
      if (was.pr.series.strokeThickness === was.base + 1) was.pr.series.strokeThickness = was.base;
      this.emphasised = null;
    }
    // Markers are not thickened: a scatter's stroke is its outline, and +1 on
    // a dot reads as a different marker rather than as emphasis.
    if (pr && pr.seriesKind !== "scatter") {
      const base = pr.series.strokeThickness;
      pr.series.strokeThickness = base + 1;
      this.emphasised = { pr, base };
    }
  }

  /** A paint plot's body colours, from its palette or the WPF app's orange and light blue. */
  private paintArgb(plan: PalettePlan): { up: number; down: number } {
    const up = plan.kind === "paired" ? plan.up : (resolveColorRef("accent.orange", this.theme) ?? "#f4c042");
    const down = plan.kind === "paired" ? plan.down : (resolveColorRef("accent.blue", this.theme) ?? "#4fc3f7");
    return { up: parseColorToArgb(up), down: parseColorToArgb(down) };
  }

  /** The pane a render belongs to. */
  private paneOf(pr: PlotRender): PaneRender | undefined {
    for (const pane of this.panes.values()) if (pane.byId.get(pr.plotId) === pr) return pane;
    return undefined;
  }

  /**
   * Colour a pane's candles: the rule (standard or hollow), then every shown
   * paint plot over the bodies, in plot order (`plan/candle-colors.ts`).
   *
   * **No provider at all for standard with nothing painting** — SciChart's own
   * up/down brushes already draw that, and a provider would cost a pass over
   * every bar on each update for the same picture. One is added the moment a
   * rule or a paint needs it and removed when neither does.
   */
  private repaintCandles(pane: PaneRender | undefined): void {
    if (!pane) return;
    const renders = [...pane.byId.values()];
    const candle = renders.find((pr) => pr.seriesKind === "candlestick");
    if (!candle?.candleArgb) return;
    const paints: CandlePaint[] = renders
      .filter((pr) => pr.paint && !pr.hidden)
      .map((pr) => ({ values: pr.values, up: pr.paint!.up, down: pr.paint!.down }));
    const style = candle.candleStyle ?? "standard";
    const series = candle.series as FastCandlestickRenderableSeries;
    if (style === "standard" && paints.length === 0) {
      if (candle.candlePalette) {
        candle.candlePalette = undefined;
        series.paletteProvider = new DefaultPaletteProvider();
      }
      return;
    }
    // Rebuilt rather than reused when the colours changed — they are baked in.
    const provider = new CandlePaletteProvider(candle.candleArgb);
    provider.refresh(candle.dataSeries as OhlcDataSeries, style, paints);
    candle.candlePalette = provider;
    series.paletteProvider = provider;
    this.parentSurface?.invalidateElement();
  }

  /** The linked crosshair's line, one per pane, made on first use. */
  private readonly linkedLines = new Map<SciChartSubSurface, VerticalLineAnnotation>();

  /**
   * Draw another chart's pointer date here, or hide it with `null` — the
   * crosshair link (`SyncFlags.crosshair`, owner 2026-10-05).
   *
   * **On the bar that contains the date**, not the nearest: a weekly bar is
   * stamped with its first day and covers the week, so a daily chart pointing at
   * Thursday must land on that week's bar, never the next one (`containingIndex`
   * gives the argument). A date before the first bar, or more than one bar's
   * length past the last, draws nothing — this chart has no bar for it.
   *
   * **The vertical line only**, dashed like the crosshair. A horizontal line
   * here would mark a price nobody pointed at — the reason the panes of one
   * chart already show it only where the pointer is. The date label rides on
   * the pane that draws the X axis labels.
   */
  showLinkedTime(time: number | null): void {
    const times = this.barTimes;
    let at: number | undefined;
    if (time !== null && times.length > 0) {
      const i = containingIndex(times, time);
      const last = times.length - 1;
      const span = last > 0 ? times[last]! - times[last - 1]! : 0;
      if (i !== undefined && (i < last || time - times[last]! <= span)) at = times[i];
    }
    const stroke = this.crosshairColor();
    for (const pane of this.panes.values()) {
      let line = this.linkedLines.get(pane.sub);
      if (at === undefined) {
        if (line) line.isHidden = true;
        continue;
      }
      if (!line) {
        line = new VerticalLineAnnotation({
          x1: at,
          // A pane's Y axes are named, never the default id — an annotation
          // left on the default would find no axis. Any of the pane's will do
          // for a line that spans it top to bottom.
          ...(pane.sub.yAxes.get(0) ? { yAxisId: pane.sub.yAxes.get(0)!.id } : {}),
          stroke,
          strokeThickness: 1,
          strokeDashArray: [3, 3],
          showLabel: pane.sub.xAxes.get(0)?.drawLabels === true,
          labelPlacement: ELabelPlacement.Axis,
          axisLabelFill: stroke,
          axisLabelStroke: this.theme.tokens.background ?? "#0e1116",
          isEditable: false,
        });
        pane.sub.annotations.add(this.own(line) as never);
        this.linkedLines.set(pane.sub, line);
      } else {
        line.x1 = at;
        line.stroke = stroke;
        line.axisLabelFill = stroke;
        line.isHidden = false;
      }
    }
  }

  setInteractions(interactions: InteractionPlan): void {
    this.interactions = interactions;
    for (const render of this.panes.values()) this.applyInteractions(render);
    this.parentSurface?.invalidateElement();
  }

  /**
   * Move the X window, in bar indices — a property write, never a rebuild.
   *
   * **`autoRange` is turned off as the range goes on**, and that is the whole
   * subtlety. The X axis is built with `EAutoRange.Once` so a chart with no
   * window yet still fits its data; that fit is queued against the *next* render
   * pass, so a range written immediately after the bulk load — which is exactly
   * when a window is first applied — was silently overwritten by it. Clearing
   * the flag as part of writing the range is what stops the two from racing;
   * `ZoomExtentsModifier` still works, since `zoomExtents()` computes from the
   * data rather than from this setting.
   *
   * Written to every pane so the panes cannot disagree for a frame.
   * {@link syncXAxes} would carry it across anyway, but only after a change
   * event, and a volume pane one frame behind its candles reads as a flicker.
   */

  /**
   * Draw the annotations, replacing whatever was drawn before.
   *
   * **A repaint, never a rebuild.** Adding a trendline must not tear down a
   * WebGL surface and refetch the data — the same bargain as `applyTheme`,
   * `setGridLines` and `setPaneHeights`, and the reason annotations are
   * excluded from `structuralKey`. Someone drawing five lines would otherwise
   * pay five refetches.
   *
   * Both sources arrive here and only the first is ever collected back out:
   * `user` is `Pane.annotations` and persists, `derived` is computed per render
   * — the trading plan's levels, the fills as marks — and must not be written
   * into the contract tree. `plan/annotation.ts` marks the difference and
   * `persistable()` is the guard on the way back.
   */
  setAnnotations(
    paneId: Ulid,
    user: readonly Annotation[] | undefined,
    derived: readonly DerivedAnnotation[] | undefined,
    editable?: ReadonlySet<string>,
  ): void {
    const pane = this.panes.get(paneId);
    if (!pane) return;
    // Hover targets are rebuilt with the drawings: the old objects are about to
    // be removed, and a modifier hit-testing them would report a stale label.
    for (const [annotation] of this.derivedHoverLines) {
      if ((this.annotationsByPane.get(paneId) ?? []).some((d) => d.annotation === annotation)) {
        this.derivedHoverLines.delete(annotation);
      }
    }

    // **The selection is carried across, by id.** These objects are replaced
    // wholesale on every render, and SciChart's `isSelected` lives on the object
    // — so a naive rebuild drops the selection every time anything changes. That
    // is not cosmetic: restyling a drawing *is* a change, so the style bar closed
    // on its own first edit, and holding the width spinner shut it on every tick.
    //
    // The id survives, which is the whole reason a drawing can be re-selected at
    // all. Anything gone from the workspace simply does not match and is dropped.
    const carried = new Set(this.selectedAnnotationIds());
    if (this.selectOnArrival) carried.add(this.selectOnArrival);
    const hadSelection = carried.size > 0;
    for (const existing of this.annotationsByPane.get(paneId) ?? []) {
      pane.sub.annotations.remove(existing.annotation as never);
    }

    const drawn: { id: string; origin: string; annotation: unknown }[] = [];
    const levels: { id: string; y: number }[] = [];
    // **Off the price pane, a drawing names the pane's own axis.** Price-pane
    // drawings have always resolved to the default id and keep doing so; an
    // indicator pane's axis has an id of its own, and a line left on the
    // default would be attached to an axis that pane does not have and draw
    // nothing (Seasonality hits, 2026-10-22 — the first shapes not on price).
    const model = this.paneModels.find((m) => m.id === paneId);
    const ownAxis = model && model.role !== "price" ? this.plans.get(paneId)?.axes[0]?.id : undefined;
    for (const plan of resolveAnnotations(user, derived, this.theme, editable)) {
      const annotation = this.buildAnnotation(plan, ownAxis);
      if (!annotation) continue;
      pane.sub.annotations.add(this.own(annotation) as never);
      drawn.push({ id: plan.id, origin: plan.origin, annotation });
      if (plan.origin === "derived" && plan.hover?.length) {
        this.derivedHoverLines.set(annotation, plan.hover);
        this.attachDerivedHover(pane);
      }

      // **A price on the axis, for a derived level.**
      //
      // The label sits out on the chart where the line is; the axis is where
      // someone looks to compare a level against what the market is doing right
      // now, beside the last-price tag that is already there. Two readings of
      // one number, in the two places it is asked for.
      //
      // Levels only — a zone has two prices and neither is the level, and a
      // fill mark is an event rather than a price to watch.
      const marker = this.buildAxisMarker(plan);
      if (marker) {
        pane.sub.annotations.add(this.own(marker) as never);
        drawn.push({ id: `axis|${plan.id}`, origin: plan.origin, annotation: marker });
        this.watchAxisTags(pane.sub);
      }

      // A dragged **derived** line has to report where it landed, or the drag is
      // undone by the next render — which is the reason these carried no grips
      // at all until there was somewhere to send the number. A user's own
      // drawing needs no subscription: it *is* the stored thing, and the
      // writeback for those is the drawing path's business.
      if (plan.origin === "derived" && typeof plan.y1 === "number") {
        // Every derived level, not only the draggable ones: a frozen entry
        // cannot be moved and can still be right-clicked to add a target to the
        // plan it belongs to.
        levels.push({ id: plan.id, y: plan.y1 });
      }

      // **The user's own drawings report their selection.** Derived ones do not:
      // a plan level is not the user's to restyle, and the next render would put
      // the plan's own colour back over anything written here.
      if (plan.origin === "user") {
        const selectable = annotation as {
          selectedChanged?: { subscribe(fn: () => void): void };
          isSelected?: boolean;
        };
        // Restored **before** subscribing, so putting the selection back does not
        // fire the handler and announce a selection nobody changed — the render
        // announces once at the end, with the whole set.
        if (carried.has(plan.id)) selectable.isSelected = true;
        selectable.selectedChanged?.subscribe(() => this.announceSelection());
      }

      if (plan.editable && plan.origin === "derived") {
        const handle = annotation as { dragEnded?: { subscribe(fn: () => void): void }; y1?: number };
        handle.dragEnded?.subscribe(() => {
          const y = handle.y1;
          if (typeof y === "number" && Number.isFinite(y)) {
            this.editedHandler?.(paneId, plan.id, y);
          }
        });
      }
    }
    this.annotationsByPane.set(paneId, drawn);
    this.levelsByPane.set(paneId, levels);
    // The level tags were just re-added, so they sit above the close's — raise
    // it again (owner, 2026-10-16: the close's label always on top).
    this.raisePriceMarker(pane.sub);

    // Announced once, after everything is on the surface: the set may have
    // shrunk (a drawing was deleted) or grown (one was just drawn), and either
    // way the host is told what is selected now rather than what changed.
    this.selectOnArrival = null;
    if (hadSelection) this.announceSelection();
  }

  /**
   * The **user's** selected drawings, by id.
   *
   * Derived ones are excluded even when SciChart says they are selected: a plan
   * level is not the user's to delete, and Delete over one should do nothing
   * rather than silently drop a line the plan will redraw on the next render.
   * Removing that line means editing the plan, which is the panel's job.
   */
  /** @see ChartBuilder.onPlacingChanged */
  onPlacingChanged(handler: ((placing: boolean) => void) | null): void {
    this.placingHandler = handler;
  }

  /**
   * @see ChartBuilder.cancelPlacement
   *
   * Every pane, not the one that has it: the host does not know which pane the
   * point is in and should not have to. `cancel` on a modifier with nothing
   * placed does nothing, so asking all of them costs a loop.
   */
  cancelPlacement(): void {
    let cancelled = false;
    for (const modifier of this.drawingModifiers.values()) {
      if (!modifier.isPlacing) continue;
      modifier.cancel();
      cancelled = true;
    }
    // Only when something was actually taken back. Clearing unconditionally would
    // pull the rubber band out from under a *drag* in progress, which Escape has
    // no business touching — the button is still down and the gesture is alive.
    if (cancelled) {
      this.clearPreview();
      this.parentSurface?.invalidateElement();
    }
  }

  /** @see ChartBuilder.onAnnotationSelected */
  onAnnotationSelected(handler: ((ids: readonly Ulid[]) => void) | null): void {
    this.selectedHandler = handler;
  }

  /**
   * Announce the selection, once, however many annotations just changed.
   *
   * SciChart fires `selectedChanged` per annotation, so clicking a second
   * drawing fires twice — deselect, then select — and a host that rebuilt its
   * editor on each would flash it shut and open again. Reading the whole
   * selection and emitting that is both simpler and what the host wants to know.
   */
  private announceSelection(): void {
    this.selectedHandler?.(this.selectedAnnotationIds());
  }

  selectedAnnotationIds(): string[] {
    const out: string[] = [];
    for (const drawn of this.annotationsByPane.values()) {
      for (const entry of drawn) {
        if (entry.origin !== "user") continue;
        if ((entry.annotation as { isSelected?: boolean }).isSelected) out.push(entry.id);
      }
    }
    return out;
  }

  /**
   * Remove every drawing the financial-tools palette placed.
   *
   * **The contract cannot reach these.** They are added straight to the surface
   * by the library's placement modifier and were never in `Pane.annotations`,
   * so clearing the workspace clears nothing they can see — which is exactly
   * what "remove every drawing does not work" was.
   *
   * Identified by exclusion rather than by a flag: anything on the surface that
   * this builder did not put there itself. That covers every one of the
   * twenty-six without naming them, and it cannot go stale when the library
   * adds a twenty-seventh.
   *
   * **"Ours" is {@link owned}, recorded at every add.** It used to be gathered
   * from the maps that track annotations — `annotationsByPane`, then the two
   * Volume-by-Price pools — and each kind added outside a map was deleted by
   * this button: the last-value axis markers among them, which is how the price
   * tags on the axis vanished on "remove every drawing".
   */
  clearPlacedDrawings(): void {
    for (const pane of this.panes.values()) {
      for (const annotation of pane.sub.annotations.asArray().slice()) {
        if (!this.owned.has(annotation)) pane.sub.annotations.remove(annotation);
      }
    }
    this.parentSurface?.invalidateElement();
  }

  /**
   * Delete whatever is selected, and say which of ours went.
   *
   * Both sources at once, because the user selected *a drawing* and does not
   * know which library drew it. The placed ones are removed here — nothing else
   * owns them — and the contract ones are only *reported*, since the workspace
   * is the host's to write and the render that follows is what removes them.
   *
   * Derived annotations are never touched: a plan level is not the user's to
   * delete, and the next render would put it back.
   */
  deleteSelected(): string[] {
    const removedIds: string[] = [];
    for (const [paneId, pane] of this.panes) {
      const drawn = this.annotationsByPane.get(paneId) ?? [];
      const byObject = new Map(drawn.map((d) => [d.annotation, d]));

      for (const annotation of pane.sub.annotations.asArray().slice()) {
        if (!(annotation as { isSelected?: boolean }).isSelected) continue;
        const mine = byObject.get(annotation);
        if (!mine) {
          // Placed by the library. Nothing else owns it, so it goes now.
          pane.sub.annotations.remove(annotation);
          continue;
        }
        if (mine.origin === "user") removedIds.push(mine.id);
      }
    }
    this.parentSurface?.invalidateElement();
    return removedIds;
  }

  /**
   * The right edge of the X window, in index space.
   *
   * A ray runs to the edge of what is *shown* rather than to the last bar, so
   * it still reaches the edge when the chart is scrolled or carries a right
   * margin. Undefined before the first render, when no axis has a range yet.
   */
  private visibleMaxTime(): number | undefined {
    for (const pane of this.panes.values()) {
      const range = pane.sub.xAxes.get(0)?.visibleRange;
      // No `ceil`: the range is a timestamp now, and rounding one up by a second
      // meant nothing. It was there to reach the next whole *bar*.
      if (range && Number.isFinite(range.max)) return range.max;
    }
    return undefined;
  }

  /**
   * Which derived level is under a page point, if any.
   *
   * For a right-click: the menu needs to know *which plan* was aimed at, and a
   * level is a horizontal line, so the question is one subtraction per level
   * once its price is in pixels.
   *
   * `tolerance` is in device pixels and generous on purpose — a 1px line is not
   * a target anybody can hit, and the alternative to a forgiving radius is a
   * menu that opens on empty space and offers to edit a plan.
   */
  levelAt(clientX: number, clientY: number, tolerance = 6): { paneId: Ulid; id: string } | null {
    for (const [paneId, levels] of this.levelsByPane) {
      if (levels.length === 0) continue;
      const pane = this.panes.get(paneId);
      const rect = pane?.sub.seriesViewRect;
      const yAxis = pane?.sub.yAxes.get(0);
      const calc = yAxis?.getCurrentCoordinateCalculator();
      if (!pane || !rect || !calc) continue;

      const surface = this.container?.getBoundingClientRect();
      if (!surface) continue;
      // Page -> canvas -> series rect, the same two steps the drawing modifier
      // takes. Horizontal position is deliberately ignored: these span to the
      // right edge, so anywhere along one is on it.
      //
      // **In device pixels**, the space `seriesViewRect` and the calculator
      // use (`PIXEL_RATIO` times CSS). The page point is scaled up into it,
      // and so is the tolerance, which is meant in CSS pixels. Mixing the two
      // missed or misattributed a level at 150% display scaling.
      const ratio = DpiHelper.PIXEL_RATIO || 1;
      const localY = (clientY - surface.top) * ratio - rect.top;
      const localX = (clientX - surface.left) * ratio - rect.left;
      if (localX < 0 || localX > rect.width || localY < 0 || localY > rect.height) continue;

      let best: { id: string; distance: number } | null = null;
      for (const level of levels) {
        const distance = Math.abs(calc.getCoordinate(level.y) - localY) / ratio;
        if (distance <= tolerance && (!best || distance < best.distance)) {
          best = { id: level.id, distance };
        }
      }
      if (best) return { paneId, id: best.id };
    }
    return null;
  }

  /**
   * The bar timestamps the loaded data carried.
   *
   * Handed in by whoever loads, because the builder never sees the frame. It is
   * **the axis's base values**: `DiscontinuousDateAxis` converts a time to a
   * position by looking it up among these, so until they arrive the axis can
   * place nothing — which is why this is called before annotations are set and
   * before any window is applied.
   *
   * It still re-places the annotations it holds. That is no longer because the
   * indices moved — they are not indices any more — but because a *wider* load
   * can bring an endpoint that was off the loaded range back into it, and one
   * that was dropped as unplaceable has to reappear.
   */
  setBarTimes(times: readonly number[], realBars?: number): void {
    this.barTimes = [...times];
    // **How many of them are bars, as against dates drawn past the last one.**
    //
    // The axis needs every time — that is what places a projection and lets the
    // crosshair read it — while the Y fit needs to know where the prices stop.
    // Absent means "all of them", which is every chart that carries no
    // projection and is what this did before it could.
    this.realBars = realBars ?? this.barTimes.length;
    this.applyBaseXValues();
    // A slot is measured from the neighbours, which a new frame may have moved.
    for (const [layer, { list }] of this.barHighlightLayers) this.setBarHighlights(list, layer);
    for (const [paneId] of this.annotationsByPane) {
      const model = this.paneModels.find((pane) => pane.id === paneId);
      // Derived annotations are the caller's to re-supply; this only re-places
      // what it already has, which is why the caller sets them again after a
      // load rather than this trying to remember them.
      if (model) this.setAnnotations(paneId, model.annotations, undefined);
    }
  }

  /**
   * Push the bar times onto every pane's axis as its base values.
   *
   * Rebuilt rather than appended to: a load is a whole frame, not a delta, and a
   * series that accumulated across two loads would carry times the chart no
   * longer holds — which on this axis is not a stale label but a *position*, so
   * every bar after the first stale one would sit in the wrong place.
   */
  private applyBaseXValues(): void {
    if (!this.wasmContext) return;
    this.baseXValues ??= new XyDataSeries(this.wasmContext);
    this.baseXValues.clear();
    // `appendRange` rather than a loop: this runs on every load, and the array
    // is five thousand long on a default chart.
    this.baseXValues.appendRange(
      this.barTimes,
      // The y is never read — the axis takes the x values alone — but the series
      // needs one per point or the two arrays disagree on length and it throws.
      this.barTimes.map(() => 0),
    );
    for (const pane of this.panes.values()) {
      const xAxis = pane.sub.xAxes.get(0);
      if (!(xAxis instanceof DiscontinuousDateAxis)) continue;
      xAxis.setBaseXValues(this.baseXValues);
      // **Pushed into the index calculator here, not on the next render pass.**
      //
      // `setBaseXValues` only stores the series and notifies; the values reach
      // `indexCalculator` in `prepareRenderData`, which SciChart runs when it
      // next renders. `setBarWindow` lands before that — a load applies the
      // window on the same beat — and building a coordinate calculator calls
      // `GetIndex` on the range's own endpoints, which an empty calculator
      // returns *unchanged*.
      //
      // The calculator that results holds a live reference to the index
      // calculator but endpoints frozen in seconds, so it places points at
      // indices (0..n) inside a range spanning epoch seconds. Every bar then
      // lands within a fraction of a pixel of each other, far off-screen, and
      // the chart draws nothing while the data, the Y fit and the X range are
      // all correct — which is what made it look like a data problem.
      //
      // It presented as symbol-specific only because whether a render pass fell
      // between the two calls was timing.
      xAxis.prepareRenderData();
    }
  }

  /**
   * One SciChart annotation, or `null` when it cannot be placed.
   *
   * `null` rather than a drawing at zero: an annotation whose bars are no longer
   * loaded has no honest position, and putting it at the origin is a line
   * nobody drew. It reappears when the range covers it again.
   */
  /**
   * One draggable level entered or left, restyled to say so.
   *
   * Entirely local to the annotation that raised the event — no shared count,
   * no reset on rebuild. A hover whose leave never arrives, because the
   * annotation was replaced under the pointer, cannot leave anything stuck:
   * the replacement is built from the plan and carries the plan's own style.
   */
  private onLevelHover(sender: unknown, isHovered: boolean, plan: AnnotationPlan): void {
    const line = sender as { stroke?: string; strokeThickness?: number };
    if (typeof line.strokeThickness !== "number") return;
    const style = hoverStyle(
      plan.style.stroke ?? this.theme.tokens.textSecondary,
      plan.style.thickness,
      // **Only a derived line takes the accent; a user's drawing keeps its own
      // colour and only thickens.**
      //
      // The accent swap was written for plan levels, where it borrows the pane
      // divider's idiom — a line you can grab lights up in `accent` — and where
      // the resting colour means something the *plan* chose, so replacing it for
      // a moment costs nothing.
      //
      // A drawing the user coloured themselves is the opposite case. It is
      // selected for exactly as long as they are choosing its colour, so painting
      // it `accent` throughout means the one thing they cannot see is the colour
      // they are picking — every swatch looked like it produced the accent. The
      // selection is not unsignalled without it: SciChart draws grips on a
      // selected editable annotation, in `annotationsGripsBorderBrush`, which is
      // the accent doing this job in a place that is not the line itself.
      plan.origin === "derived" ? this.theme.tokens.accent : undefined,
      isHovered,
    );
    if (style.stroke !== undefined) line.stroke = style.stroke;
    line.strokeThickness = style.strokeThickness;
    this.parentSurface?.invalidateElement();
  }

  /** A derived line at exactly its own times (`exactX`). */
  private buildExactLine(plan: AnnotationPlan, common: object, stroke: string | undefined): unknown {
    return new LineAnnotation({
      ...common,
      x1: plan.x1!,
      y1: plan.y1!,
      x2: plan.x2!,
      y2: plan.y2 ?? plan.y1!,
      stroke,
      strokeThickness: plan.style.thickness,
      strokeDashArray: plan.style.dash,
    });
  }

  private buildAnnotation(plan: AnnotationPlan, yAxisId?: string): unknown | null {
    if (!isPlaceable(plan)) return null;

    const stroke = plan.style.stroke ?? this.theme.tokens.textSecondary;
    const common = {
      // Above the candles: a drawing is a note *on* the data, and one behind
      // the bars is invisible on exactly the dense charts people annotate.
      annotationLayer: EAnnotationLayer.AboveChart,
      // Grips only for the user's own drawings. A derived line dragged would
      // edit something whose source is elsewhere, and the next render would
      // silently undo it.
      isEditable: plan.editable,
      opacity: plan.style.opacity,
      // **The selection box, painted in nothing, and set here because the theme
      // cannot reach it.**
      //
      // SciChart's dark themes put `annotationSelectionStroke` at `#f00e0e66` —
      // red at 40% — and for a line the selection box collapses onto the line
      // itself, so the stroke is tinted rather than outlined. A `#b5ee58` drawing
      // rendered `#cd953b`, which is exactly `0.4 × (240,14,14)` over it.
      //
      // Setting it on the theme provider does nothing: `AnnotationBase` copies
      // the value from the **global** `SciChartSurfaceBase.DEFAULT_THEME` in its
      // constructor, not from the surface it is added to — the same trap the axes
      // hit with `tickTextBrush` and `axisBandsFill`, and the reason `buildGroup`
      // has to apply the theme at all. A per-annotation option is the only place
      // our value lands.
      //
      // Nothing is lost: selection is carried by the grips, which *are* themed
      // (`annotationsGripsBorderBrush`, the accent), and by the pixel a hover
      // adds. Both say "selected" without lying about what colour the drawing is.
      selectionBoxStroke: "transparent",
      ...(yAxisId ? { yAxisId } : {}),
      // Only what can actually be dragged says so. A frozen entry is not
      // editable and must not offer a grab cursor: the server refuses the
      // change, and the same argument `plan-levels` makes about withholding the
      // handle applies to withholding the promise of one.
      ...(plan.editable
        ? {
            onHover: (args: { isHovered: boolean; sender: unknown }) =>
              this.onLevelHover(args.sender, args.isHovered, plan),
          }
        : {}),
    };

    // **A highlighted bar**: the user's own column behind one candle, the same
    // shape as `setBarHighlights`. Selectable (so the style bar can recolour it
    // and Delete can remove it) but not movable: every drag is put straight back.
    if (plan.kind === "barHighlight") {
      const slot = barSlot(this.barTimes, plan.x1!);
      if (!slot) return null;
      const box = new BoxAnnotation({
        ...common,
        x1: slot.start,
        x2: slot.end,
        y1: 0,
        y2: 1,
        yCoordinateMode: ECoordinateMode.Relative,
        yAxisId: this.priceYAxisId(),
        fill: plan.style.fill ?? this.theme.tokens.positive,
        opacity: BAR_HIGHLIGHT_OPACITY,
        stroke: "transparent",
        strokeThickness: 0,
        annotationLayer: EAnnotationLayer.BelowChart,
        onDrag: () => {
          box.x1 = slot.start;
          box.x2 = slot.end;
          box.y1 = 0;
          box.y2 = 1;
        },
      });
      return box;
    }

    if (plan.kind === "hline") {
      return new HorizontalLineAnnotation({
        ...common,
        y1: plan.y1!,
        stroke,
        strokeThickness: plan.style.thickness,
        strokeDashArray: plan.style.dash,
        showLabel: Boolean(plan.text),
        axisLabelFill: stroke,
      });
    }

    // **Everything anchored by points is built here, above the conversion.**
    //
    // Two reasons, and the branch has to be above `placeX` for both:
    //
    // - These carry no `x1`. `placeX` returns undefined without one and the
    //   `if (!found) return null` below throws the drawing away. That is
    //   exactly what happened to `freehand` — it drew a rubber band and
    //   vanished on release — for as long as no palette entry armed it and the
    //   branch was dead code.
    // - `placeX` answers "which bar" by hiding an endpoint outside the loaded
    //   range. Right for a trendline, wrong here: dropping one anchor of a
    //   pitchfork leaves two, and the drawing silently becomes a different
    //   shape. It is also unnecessary — a `DiscontinuousDateAxis` places any
    //   timestamp, including one past the last bar, which is where a Fibonacci
    //   time zone or a projected channel is *supposed* to land.
    //
    // `placedByPoints` rather than two ifs, so the two cannot come apart again.
    if (placedByPoints(plan.kind)) {
      return plan.kind === "freehand"
        ? this.buildFreehand(plan, stroke, common)
        : this.buildTradingAnnotation(plan, common, stroke);
    }

    // Everything else is placed against bars, so it needs the time -> index
    // conversion and can fail it.
    //
    // A **ray** — a `line` with a start and no end — is placed from its start to
    // the last loaded bar. Resolved here rather than by whoever minted it,
    // because "the right edge" moves: bars arrive, and a far end baked in at
    // mint time would stop short of the newest ones with a visible gap.
    // Clamped for anything **derived**: a plan written during today's session
    // starts a few hours after the last daily bar — the bars are stamped
    // midnight — so its rays and its labels placed nowhere at all, which is why
    // a plan's levels never appeared on the chart it was drawn on.
    //
    // The clamp is forward-only (see `clampToRange`), so a fill from before the
    // loaded history is still hidden rather than dragged to the left edge and
    // drawn at a date it has nothing to do with.
    const ray = isRay(plan);
    const clamp = plan.origin === "derived";
    // A ray starts on the bar its moment falls *in*, not the nearest one: on a
    // weekly or monthly chart the nearest is the following period as often as
    // not, which starts the plan a bar after it was live. See `placeRayStart`.
    const rayStart = ray ? placeRayStart(this.barTimes, plan.x1) : undefined;
    // **A derived line may end past the last bar**, and only a derived one. A
    // trendline's stub (`geometry-annotations.ts`) is extended by whole bars
    // along the detector's slope and its far end extrapolated with
    // `timeAtOffset` — the same spacing the right margin uses — so it keeps its
    // slope per bar exactly. `placeX` would hide it, rightly for a user's
    // drawing, whose far end off the data means a different slope. Its time goes
    // through untouched instead; the axis places any time, as the right margin
    // already relies on.
    const lastBar = this.barTimes[this.barTimes.length - 1];
    const beyond =
      !ray &&
      plan.origin === "derived" &&
      plan.x2 !== undefined &&
      lastBar !== undefined &&
      plan.x2 > lastBar;
    // `exactX`: the drawing's own times, untouched — see `DerivedAnnotationExtras`.
    if (plan.exactX && plan.origin === "derived" && plan.x1 !== undefined && plan.x2 !== undefined) {
      return this.buildExactLine(plan, common, stroke);
    }
    const found = ray
      ? rayStart === undefined
        ? undefined
        : { x1: rayStart, x2: undefined as number | undefined }
      : placeX(this.barTimes, plan.x1, beyond ? undefined : plan.x2, clamp);
    if (!found) return null;
    // **Index in, time out.** The rules above — nearest vs containing, hide an
    // endpoint off the loaded range, clamp a derived one forwards — are all
    // about *which bar*, and finding a bar is still a search through the
    // timestamps. What changed is what the axis then wants: a
    // `DiscontinuousDateAxis` places by time, so the answer is converted here
    // and every annotation below is positioned in time.
    //
    // This is why a wider load no longer moves a drawing. The index it resolved
    // to changes; the timestamp of the bar at that index does not.
    const at = {
      x1: timeAtIndex(this.barTimes, found.x1)!,
      x2: beyond
        ? plan.x2
        : found.x2 === undefined
          ? undefined
          : timeAtIndex(this.barTimes, found.x2),
    };
    if (at.x1 === undefined) return null;
    if (ray) {
      // **A ray always extends to the right, never back to bar zero.**
      //
      // The far end is the right edge of the *view*, not the last bar. A
      // `DiscontinuousDateAxis` extrapolates past its last base value — that is
      // what the right margin is — so a plan drawn at the newest bar still has
      // somewhere to go. Ending at the last bar instead made a ray drawn there zero pixels
      // wide, and spanning back to zero to rescue it drew the plan leftwards
      // across every bar of history that preceded it.
      //
      // `x1 + 1` as the floor, so the ray is never shorter than a bar whatever
      // the window is doing.
      // **The far end is a time now, and "one bar" is not 1.**
      //
      // On the old index axis the floor was `x1 + 1`, because one bar *was* one
      // unit. In time it is whatever this chart's interval happens to be, and
      // that differs per chart and per resample — so it is read off the data
      // rather than assumed: the gap between the last two bars, which is the
      // only spacing this chart can be said to have.
      const times = this.barTimes;
      const last = times[times.length - 1] ?? at.x1;
      const step = times.length > 1 ? Math.max(1, last - times[times.length - 2]!) : 1;
      const edge = this.visibleMaxTime();
      at.x2 = Math.max(at.x1 + step, last, edge ?? last);
    }

    if (plan.kind === "text") {
      return new NativeTextAnnotation({
        ...common,
        x1: at.x1,
        y1: plan.y1!,
        text: plan.text ?? "",
        textColor: plan.style.fontColor ?? stroke,
        fontSize: plan.style.fontSize ?? 12,
        // A label that ends at `x1` and sits on its line — Bar Intelligence's,
        // just left of the current bar (`DerivedAnnotationExtras`).
        ...(plan.textAnchor === "right"
          ? {
              horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
              verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
            }
          : {}),
      });
    }

    if (plan.kind === "box") {
      return new BoxAnnotation({
        ...common,
        x1: at.x1,
        y1: plan.y1!,
        x2: at.x2!,
        y2: plan.y2!,
        stroke,
        strokeThickness: plan.style.thickness,
        fill: plan.style.fill,
        // Behind everything. A zone is the ground a plan is read against, and
        // one painted over the candles hides the bars it is describing.
        annotationLayer: EAnnotationLayer.BelowChart,
      });
    }

    if (plan.kind === "line" || plan.kind === "trendline") {
      return new LineAnnotation({
        ...common,
        x1: at.x1,
        y1: plan.y1!,
        x2: at.x2!,
        // A ray is level: it carries one price, and the far end is the same
        // price at the right edge. `y2 ?? y1` rather than a branch, since a
        // trendline always has its own y2.
        y2: plan.y2 ?? plan.y1!,
        stroke,
        strokeThickness: plan.style.thickness,
        strokeDashArray: plan.style.dash,
      });
    }

    // `fib` is a composite: several primitives standing for one drawing.
    // `CompositeAnnotation` is what makes it still behave as one — added,
    // removed, hit-tested and dragged together — where a loose bag of child
    // annotations would let a Fibonacci's 50% line be dragged out of its own
    // retracement. `freehand` is one too, and is built above, because it is
    // placed by its path rather than by the corner pair this far.
    if (plan.kind === "fib") return this.buildFib(plan, at, stroke, common);
    return null;
  }

  /**
   * One of the palette's twenty-five drawings, rebuilt from the contract.
   *
   * **Constructed by name through SciChart's class factory**, not by importing
   * twenty-five classes and switching on the kind. The library registers every
   * one of them under exactly the string `trading-annotation-map` already
   * stores — that is what `register.ts` in the package does — so the map we
   * keep for the palette is also the constructor table, and there is no second
   * list to fall out of step with it.
   *
   * **The points are timestamps and go straight through.** No conversion, no
   * filtering: the axis speaks time, the contract stores time, and an anchor
   * out past the last bar is a legitimate place to have drawn one.
   *
   * A kind whose class the factory does not know returns `null` and draws
   * nothing, which is the documented outcome for a renderer that cannot draw a
   * kind — a workspace written by a newer client stays loadable.
   */
  private buildTradingAnnotation(
    plan: AnnotationPlan,
    common: Record<string, unknown>,
    stroke: string | undefined,
  ): unknown | null {
    const className = tradingClassFor(plan.kind);
    const wasmContext = this.wasmContext;
    if (!className || !wasmContext) return null;

    const options: Record<string, unknown> = {
      ...common,
      points: plan.points.map((p) => new Point(p.x, p.y)),
      stroke,
      strokeThickness: plan.style.thickness,
      strokeDashArray: plan.style.dash,
    };
    // Only when there is one. Several of these kinds take `fill` and the rest
    // ignore it, but `fill: undefined` is not the same as absent to every
    // options reader — and an unfilled channel is the default a user expects,
    // for the same reason an unfilled box is (see `AnnotationStyleBar`).
    if (plan.style.fill) options.fill = plan.style.fill;

    try {
      return createType(EBaseType.Annotation, className, wasmContext, options) as unknown;
    } catch {
      // A registration that moved, or options this version rejects. One
      // undrawable drawing is not worth taking the chart down for.
      return null;
    }
  }

  /**
   * A level's price, tagged on the Y axis.
   *
   * `null` for anything that is not one: a zone spans two prices and neither is
   * the level, a label is the level said twice, and a fill is an event rather
   * than a price to watch. The id prefixes are the host's (`zone|`, `label|`)
   * and are the only thing that tells them apart here.
   */
  private buildAxisMarker(plan: AnnotationPlan): unknown | null {
    if (plan.origin !== "derived") return null;
    if (plan.y1 === undefined || !Number.isFinite(plan.y1)) return null;
    if (plan.kind !== "line" && plan.kind !== "hline") return null;
    // A named tag is asked for explicitly, whatever the id — Bar Intelligence's
    // `dH 176.10` (`DerivedAnnotationExtras.axisLabel`).
    if (plan.id.includes("|") && !plan.axisLabel) return null;

    const stroke = plan.style.stroke ?? this.theme.tokens.textSecondary;
    const marker = new AxisMarkerAnnotation({
      y1: plan.y1,
      // The line's own colour, so the tag and the level read as one thing. The
      // text is dark or white by contrast with it — it was always white, and
      // white on the yellow and light-green levels was unreadable (owner,
      // 2026-10-21; `plan/axis-tags.ts`).
      backgroundColor: stroke,
      color: readableTextOn(stroke),
      fontSize: 12,
      formattedValue: plan.axisLabel ? `${plan.axisLabel} ${plan.y1.toFixed(2)}` : plan.y1.toFixed(2),
      isEditable: false,
    });
    // A level tag gives way to a value tag and to other levels
    // (`layoutAxisTags`); this is the price it goes back to when there is room.
    this.levelTagPrice.set(marker, plan.y1);
    return marker;
  }

  /** A level tag's own price — the tags `declutterAxisTags` may move. */
  private readonly levelTagPrice = new WeakMap<object, number>();
  /** Sub-surfaces already laying their tags out after each render. */
  private readonly declutterWatched = new WeakSet<object>();

  /**
   * Keep a pane's price-axis tags from covering each other (owner, 2026-10-21:
   * `qH·mH 277.40` sat under the `276.63` last-price tag).
   *
   * After each render: read every axis tag's pixel off its axis, let the pure
   * `layoutAxisTags` place the level tags around the value tags, and write a
   * moved level tag in pixel mode — back in data mode, exactly on its price,
   * when there is room. Writes only what changed and invalidates only then, so
   * a settled layout costs one pass and never loops.
   */
  private watchAxisTags(sub: SciChartSubSurface): void {
    if (this.declutterWatched.has(sub)) return;
    this.declutterWatched.add(sub);
    sub.rendered.subscribe(() => this.declutterAxisTags(sub));
  }

  private declutterAxisTags(sub: SciChartSubSurface): void {
    try {
      const markers = sub.annotations
        .asArray()
        .filter((a): a is AxisMarkerAnnotation => a instanceof AxisMarkerAnnotation && !a.isHidden);
      if (markers.length < 2) return;
      // Grouped by the axis each tag **resolves to**, not by its id: a level
      // tag carries the default id and lands on the pane's first axis, the
      // last-price tag names that same axis — keyed by id, the two never met.
      const axes = sub.yAxes.asArray();
      const byAxis = new Map<object, AxisMarkerAnnotation[]>();
      for (const m of markers) {
        const axis = axes.find((a) => a.id === m.yAxisId) ?? axes[0];
        if (!axis) continue;
        byAxis.set(axis, [...(byAxis.get(axis) ?? []), m]);
      }
      let changed = false;
      for (const [axisObj, group] of byAxis) {
        const axis = axisObj as (typeof axes)[number];
        const calc = axis.getCurrentCoordinateCalculator();
        if (!calc) continue;
        const tags = group.map((m, i) => {
          const price = this.levelTagPrice.get(m);
          return { key: String(i), y: calc.getCoordinate(price ?? m.y1), fixed: price === undefined };
        });
        const height = AXIS_TAG_HEIGHT * DpiHelper.PIXEL_RATIO;
        const placed = layoutAxisTags(tags, height);
        group.forEach((m, i) => {
          const price = this.levelTagPrice.get(m);
          if (price === undefined) return;
          const want = placed.get(String(i))!;
          const free = Math.abs(want - tags[i]!.y) < 0.5;
          if (free) {
            if (m.yCoordinateMode !== ECoordinateMode.DataValue || m.y1 !== price) {
              m.yCoordinateMode = ECoordinateMode.DataValue;
              m.y1 = price;
              changed = true;
            }
          } else if (m.yCoordinateMode !== ECoordinateMode.Pixel || Math.abs(m.y1 - want) >= 0.5) {
            m.yCoordinateMode = ECoordinateMode.Pixel;
            m.y1 = want;
            changed = true;
          }
        });
      }
      if (changed) sub.invalidateElement();
    } catch {
      // A layout pass that fails leaves the tags where they were: overlapping
      // is a nuisance, a render that throws is not.
    }
  }

  /**
   * A Fibonacci retracement: one line per ratio, grouped.
   *
   * The ratio runs from `y1` to `y2` — the direction of the *drag*, not of the
   * price — so a retracement pulled down a decline reads 0% at the high. That
   * is what anyone reading one expects, and sorting the ends first would put 0%
   * at whichever is numerically smaller and silently flip half of them.
   *
   * Each line spans only the drag's own X range rather than the pane, because a
   * retracement is a claim about one leg. Spanning would make it
   * indistinguishable from seven horizontal lines, which is a different drawing.
   */
  private buildFib(
    plan: AnnotationPlan,
    at: { x1: number; x2?: number },
    stroke: string | undefined,
    common: Record<string, unknown>,
  ): unknown {
    const children = fibPrices(plan, fibDefaults()).map(
      (level) =>
        new LineAnnotation({
          x1: at.x1,
          x2: at.x2!,
          y1: level.price,
          y2: level.price,
          stroke: level.color ?? stroke,
          strokeThickness: plan.style.thickness,
          strokeDashArray: plan.style.dash,
          // The children are never independently editable, whatever the parent
          // is: a retracement whose 50% line could be dragged on its own is no
          // longer a retracement of anything.
          isEditable: false,
        }),
    );

    return new CompositeAnnotation({
      ...common,
      x1: at.x1,
      x2: at.x2!,
      y1: plan.y1!,
      y2: plan.y2!,
      annotations: children as never,
      // The bounding box is the group's handle, not a shape — a filled one
      // would tint the candles the retracement is drawn over.
      fill: "transparent",
      strokeThickness: 0,
    });
  }

  /**
   * A freehand stroke: one segment per pair of points, grouped.
   *
   * Segments rather than a renderable series, which is the other way to draw a
   * polyline and the wrong one — a series joins the axis' autorange, appears to
   * the legend and would make a scribble change the price scale.
   */
  private buildFreehand(
    plan: AnnotationPlan,
    stroke: string | undefined,
    common: Record<string, unknown>,
  ): unknown | null {
    // Every point converts, and a stroke crossing the edge of the loaded range
    // is dropped whole rather than truncated — the same rule as a trendline's
    // two ends, for the same reason: half a drawing is a shape nobody drew.
    //
    // The conversion is gone and the *check* is not: a point is stored as a time
    // and drawn as a time, but one outside the loaded bars still has no position
    // on this axis — the base values are what a time is resolved against, and a
    // time that is not among them resolves to nothing.
    const placed: { x: number; y: number }[] = [];
    for (const point of plan.points) {
      if (indexAtTime(this.barTimes, point.x) === undefined) return null;
      placed.push({ x: point.x, y: point.y });
    }
    if (placed.length < 2) return null;

    const children = placed.slice(1).map(
      (point, i) =>
        new LineAnnotation({
          x1: placed[i]!.x,
          y1: placed[i]!.y,
          x2: point.x,
          y2: point.y,
          stroke,
          strokeThickness: plan.style.thickness,
          isEditable: false,
        }),
    );

    const xs = placed.map((p) => p.x);
    const ys = placed.map((p) => p.y);
    return new CompositeAnnotation({
      ...common,
      x1: Math.min(...xs),
      x2: Math.max(...xs),
      y1: Math.max(...ys),
      y2: Math.min(...ys),
      annotations: children as never,
      fill: "transparent",
      strokeThickness: 0,
    });
  }

  /**
   * Arm a drawing tool, or disarm with `null`.
   *
   * The modifier is attached at build time and always present; this flips what
   * it is looking for. Removing and re-adding it per tool change would drop a
   * pointer capture mid-gesture — the same bargain `setInteractions` makes.
   */
  setDrawingTool(tool: AnnotationKind | "plan" | null): void {
    this.drawingTool = tool;
    for (const [paneId, modifier] of this.drawingModifiers) {
      // **Cancel on any change of tool, not only on disarm.** A gesture in
      // progress would otherwise commit on the next press, drawing something
      // with a tool the user has just put down — and since a click leaves a
      // first point *standing*, a point placed with the trend line would become
      // the corner of a box because the palette moved under it. That state can
      // now outlive a mouse button, so it has to be cleared on the way through.
      if (modifier.tool !== tool) {
        modifier.cancel();
        // The rubber band goes with it. It survived a disarm before this mattered
        // — a drag's band died with the mouse-up either way — but a placed point
        // draws a band that is waiting for a second click, and one left behind is
        // a line the user cannot remove because nothing owns it.
        this.clearPreview();
      }
      modifier.tool = tool;

      const pane = this.panes.get(paneId);
      if (!pane) continue;

      // **Pan has to be switched off while a tool is armed.** A SciChart
      // modifier cannot consume an event — `ModifierMouseArgs` has no `handled`
      // flag, so every modifier sees every drag — and a drag that both draws a
      // trendline and pans the chart draws it against bars sliding underneath
      // the pointer. The line lands nowhere near where it was drawn.
      //
      // Flipped rather than detached, for `setInteractions`' reason: a modifier
      // removed mid-gesture leaves the surface holding a pointer capture nobody
      // owns. Re-enabling goes through `applyInteractions` so the user's own
      // `axisDrag` and `zoomAxis` settings come back rather than a hardcoded on.
      pane.interaction.zoomPan.isEnabled = tool === null;
      if (tool === null) this.applyInteractions(pane);
      else {
        // The axis drags stay live: dragging the price axis to rescale while a
        // tool is armed is not ambiguous — it starts on the axis, and nothing
        // is drawn there.
        pane.interaction.wheelZoom.isEnabled = true;
      }
    }
    this.parentSurface?.invalidateElement();
  }

  /**
   * Arm one of the financial-tools annotations, or `null` to disarm.
   *
   * The type is the library's own `ETradingAnnotationType` string, passed
   * through rather than re-declared: a second copy of that vocabulary here
   * would be a list to keep in step, and the palette already pins itself
   * against the library's enum in a test.
   *
   * An unknown type disarms rather than throwing. A toolbar is data, and a tool
   * removed from the library between versions should leave a dead button, not a
   * chart that cannot be clicked.
   *
   * **Pan goes off while one is armed**, exactly as for the built-in tools: a
   * SciChart modifier cannot consume an event, so a click that places a point
   * would also pan the chart out from under the point being placed.
   */
  setTradingTool(type: string | null): void {
    const known =
      type !== null && (Object.values(ETradingAnnotationType) as string[]).includes(type);
    // Freehand is a stroke, not a sequence of placed points, and has its own
    // modifier — see `freehandModifiers`.
    const isFreehand = known && type === ETradingAnnotationType.FreehandDrawingAnnotation;

    for (const [paneId, placement] of this.placementModifiers) {
      const pane = this.panes.get(paneId);
      const freehand = this.freehandModifiers.get(paneId);

      // Exactly one of the two is ever live: a pointer down would otherwise
      // start a stroke and place a point in the same moment.
      if (isFreehand) {
        placement.stopPlacement(true);
        freehand?.startDrawing();
      } else {
        freehand?.stopDrawing(true);
        if (known) {
          placement.startPlacement({
            // `TPlacementAnnotationType` is a union of the same strings the
            // enum holds, and the palette pins itself against that enum in a
            // test. The cast is the one place the two vocabularies meet.
            type: type as never,
            // Editable from the moment it lands, which is the whole reason
            // these are worth having over the hand-rolled kinds: grips,
            // snapping and a drag the library maintains rather than this file.
            options: { isEditable: true } as never,
          });
        } else {
          // `true` cancels a placement half-finished — a two-point channel with
          // one point down has to go, or it commits on the next click somewhere
          // else entirely.
          placement.stopPlacement(true);
        }
      }

      if (!pane) continue;
      // Pan is off while anything is armed, for the reason it is for the
      // built-in tools: a SciChart modifier cannot consume an event, so the
      // drag that draws would also scroll the chart out from under it.
      pane.interaction.zoomPan.isEnabled = !known;
      if (!known) this.applyInteractions(pane);
    }

    // Armed means a drawing is about to appear that nobody has told us about.
    // Disarmed means whatever was in flight is not going to finish.
    if (known && !isFreehand) this.watchPlacement();
    else this.stopWatchingPlacement();

    this.parentSurface?.invalidateElement();
  }

  /**
   * Start looking for a placement to finish.
   *
   * Idempotent: arming a second tool while the first is armed must not leave two
   * timers running, and `setTradingTool` is called on every palette click.
   */
  private watchPlacement(): void {
    if (this.placementPoll !== null) return;
    this.placementPoll = setInterval(() => this.pollPlacement(), PLACEMENT_POLL_MS);
  }

  private stopWatchingPlacement(): void {
    if (this.placementPoll !== null) clearInterval(this.placementPoll);
    this.placementPoll = null;
  }

  /**
   * Has a drawing landed yet?
   *
   * **Asked of the surface, not of the modifier.** The obvious version watches
   * `placement.annotation` while points go down and adopts when it clears — and
   * it drops a drawing placed faster than the poll interval. Two clicks inside
   * 100 ms is a double-click, which is a perfectly ordinary way to place a
   * two-point channel, so that version loses drawings for the users who are
   * quickest with the tool.
   *
   * So the question is asked as a fact about what is on screen: an annotation on
   * a pane that this builder did not put there and no modifier is still holding
   * has, by elimination, been finished by the library. That holds however fast
   * the gesture was, because the annotation is still there afterwards either
   * way — a cancelled placement takes its own annotation off with it
   * (`abortActivePlacement`), which is what distinguishes the two without
   * needing to have watched.
   *
   * The three exclusions are everything this builder adds to a surface: its own
   * annotations, the rubber band a built-in tool is dragging, and the placement
   * still in progress.
   */
  private pollPlacement(): void {
    for (const [paneId, pane] of this.panes) {
      const active = this.placementModifiers.get(paneId)?.annotation as unknown;
      const ours = new Set((this.annotationsByPane.get(paneId) ?? []).map((d) => d.annotation));
      for (const annotation of pane.sub.annotations.asArray()) {
        if (ours.has(annotation)) continue;
        if (annotation === active) continue;
        if (annotation === this.preview?.annotation) continue;
        // Stopped before adopting, not after: adoption reports to the host,
        // which re-renders and disarms, and a tick landing in the middle of
        // that would find the same annotation a second time.
        this.stopWatchingPlacement();
        this.adoptPlacement(paneId, annotation as PlacedAnnotation);
        return;
      }
    }
  }

  /**
   * Take a drawing the library placed and make it the workspace's.
   *
   * **The one thing that made these drawings real.** Before it, a pitchfork was
   * a picture on a surface: it was not in `Pane.annotations`, so it was not in
   * the workspace, so it did not survive a refresh — and `selectedAnnotationIds`
   * walks only what this builder created, so selecting one announced nothing and
   * the style bar had no subject. Both were the same missing step.
   *
   * The library's annotation is **removed** once its anchors are read. It is not
   * discarded: the host writes the contract, the next render calls
   * `setAnnotations`, and `buildTradingAnnotation` puts back an equivalent one
   * that this builder knows the id of. Keeping the original instead would leave
   * two drawings on the surface the moment the workspace came back, and only one
   * of them would answer to a style change.
   */
  private adoptPlacement(paneId: Ulid, placed: PlacedAnnotation): void {
    const pane = this.panes.get(paneId);
    if (!pane) return;

    const kind = kindForTradingClass(placed.type ?? "");
    const points = (placed.points ?? [])
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .map((p) => ({ x: p.x, y: p.y }));

    // Anything the map does not name, or a gesture that left fewer anchors than
    // a drawing needs, goes back the way it came: taken off the surface without
    // being stored. Leaving it would be worse than dropping it — a drawing that
    // vanishes on the next refresh with no way to select or delete it is the bug
    // this whole change exists to remove.
    pane.sub.annotations.remove(placed as never);
    this.parentSurface?.invalidateElement();
    if (!kind || points.length < 2) return;

    const annotation: Annotation = {
      id: mintUlid(),
      type: kind,
      points,
      // The first and last anchor, mirrored into the corner pair.
      //
      // Not for this renderer, which reads `points` and ignores them, but
      // because `x1..y2` are real columns in the store while `points` is a JSON
      // blob nothing can query inside. A drawing with them set can be found by
      // "what is near this price" without parsing every document.
      x1: points[0]!.x,
      y1: points[0]!.y,
      x2: points[points.length - 1]!.x,
      y2: points[points.length - 1]!.y,
    };

    // Selected on arrival, for the reason a hand-drawn one is: the object that
    // lands a frame from now is not this one, and a user who just drew a
    // pitchfork reaching for the style bar should find it open.
    this.selectOnArrival = annotation.id;
    this.drawnHandler?.(paneId, annotation);
  }

  /**
   * Where a finished gesture goes.
   *
   * The builder does not create annotations from drawings — it reports them and
   * the host writes the contract, because a drawing is workspace data and this
   * layer has no business writing the workspace. The X values are **timestamps**
   * by the time they arrive: the modifier hands over bar indices and this
   * converts, so the host never sees the index space at all.
   */
  onAnnotationDrawn(
    handler: ((paneId: Ulid, annotation: Annotation) => void) | null,
  ): void {
    this.drawnHandler = handler;
  }

  /**
   * Where a dragged **derived** line reports its new price.
   *
   * Only the y, because every line that can be dragged is a horizontal one — a
   * plan's stop or target — and its x means nothing: it spans the pane. A
   * handler that took a point would invite a caller to store one.
   *
   * The host writes the source and hands the result back through
   * `setAnnotations`, exactly as a drawn annotation round-trips. Nothing here
   * mutates the line: it already moved on screen, and the render that follows
   * the write is what makes that position true.
   */
  onAnnotationEdited(
    handler: ((paneId: Ulid, annotationId: string, y: number) => void) | null,
  ): void {
    this.editedHandler = handler;
  }

  /**
   * Where a drawn trade plan reports its entry and its stop.
   *
   * Two prices and the bar it began on. Direction, size, targets and the plan's
   * own id are all arithmetic over those plus the account — the host's
   * business, and a renderer that computed them would be a second place the
   * sizing lived.
   *
   * `at` is unix seconds, the same space an annotation's x is stored in, so the
   * host can hand it straight back as the plan's `validFrom` and the levels ray
   * from exactly where they were drawn.
   */
  onPlanDrawn(handler: ((entry: number, stop: number, at: number) => void) | null): void {
    this.planHandler = handler;
  }

  /**
   * The rubber band, redrawn on every pointer move.
   *
   * Built through the same `draftAnnotation` and `buildAnnotation` the commit
   * uses, so what is under the pointer is what gets stored. A preview drawn by
   * its own code path is the reason drawing tools jump on release: two
   * implementations of one shape, agreeing until they don't.
   *
   * Points arrive as bar indices and stay that way here — the preview is never
   * saved, so converting to timestamps and back would be a round trip whose only
   * effect is to snap the live drawing to whole bars while the pointer is
   * between two of them.
   */
  private previewDrawing(
    paneId: Ulid,
    kind: AnnotationKind | "plan",
    start: DrawPoint,
    current: DrawPoint,
    path: DrawPoint[],
  ): void {
    const pane = this.panes.get(paneId);
    if (!pane) return;
    this.clearPreview();
    // A click is the whole gesture; there is nothing to rubber-band.
    if (kind === "barHighlight") return;

    // A plan previews as the risk it is about to define: a box from the entry
    // to where the stop currently is. `box` rather than a line, because the
    // thing being chosen is the *distance* between them.
    const draft = draftAnnotation(kind === "plan" ? "box" : kind, "preview", start, current, path);
    const plan = resolveAnnotation(draft, "user", this.theme);
    // Straight to the SciChart object rather than through `buildAnnotation`,
    // which would convert the X values from timestamps it is already past.
    const annotation = this.buildDrawn(plan, kind === "plan" ? "box" : kind);
    if (!annotation) return;

    pane.sub.annotations.add(this.own(annotation) as never);
    this.preview = { paneId, annotation };
  }

  /** Take the rubber band down. Safe to call when there is none. */
  private clearPreview(): void {
    if (!this.preview) return;
    this.panes.get(this.preview.paneId)?.sub.annotations.remove(this.preview.annotation as never);
    this.preview = null;
  }

  /**
   * A finished gesture, converted back to what the contract stores.
   *
   * Two things happen here and both are the boundary this file exists to hold:
   * the gesture is checked for being a stray click, and its X values are turned
   * from **bar indices** into **timestamps**. Everything downstream — the host,
   * the workspace, the next load — sees only times.
   *
   * The drawing is *not* added to the surface. The host writes it into the
   * contract and hands it back through `setAnnotations`, so the drawn shape and
   * the stored one are the same object rendered once. Adding it here as well
   * would draw it twice and leave the copy behind when the stored one was
   * deleted.
   */
  private commitDrawing(
    paneId: Ulid,
    kind: AnnotationKind | "plan",
    start: DrawPoint,
    end: DrawPoint,
    path: DrawPoint[],
  ): void {
    this.clearPreview();

    // The trade-plan gesture: press at the entry, drag to the stop, release.
    // It produces a `Plan`, not an `Annotation`, so it leaves here before any
    // of the annotation machinery — and the two prices are all it carries,
    // because everything else about a plan is arithmetic the host already does.
    //
    // No `isWorthKeeping`: a plan needs a *distance* between entry and stop,
    // and that guard measures the drag in bar indices along x. A vertical drag
    // is the correct gesture here and would fail it.
    if (kind === "plan") {
      // **The bar the drag began on, not just the two prices.** A plan's levels
      // ray from where it starts, and a plan made today has a `createdAt` hours
      // past the last daily bar — so without this the ray had no start inside
      // the data and fell back to spanning the whole chart, which reads as a
      // line drawn leftwards from nowhere.
      //
      // Where you pressed is where you meant it to begin, and it is already in
      // the frame. Falling back to the last bar keeps a plan drawn off the end
      // of the data placeable.
      // `start.x` **is** the timestamp now — the modifier reads it off an axis
      // that speaks time — so there is nothing to convert. The fallback stays:
      // a press past the last bar still has to land on one.
      const at = start.x || this.barTimes[this.barTimes.length - 1];
      if (Number.isFinite(start.y) && Number.isFinite(end.y) && start.y !== end.y && at) {
        this.planHandler?.(start.y, end.y, at);
      }
      return;
    }
    if (!isWorthKeeping(kind, start, end)) return;

    // **One bar, snapped.** The press lands between candles as often as on one;
    // the nearest real bar is the one meant. No price is kept.
    if (kind === "barHighlight") {
      const t = nearestBarTime(this.barTimes, this.realBars, start.x);
      if (t === undefined) return;
      const highlight = draftAnnotation(kind, mintUlid(), { x: t, y: 0 }, { x: t, y: 0 });
      this.selectOnArrival = highlight.id;
      this.drawnHandler?.(paneId, highlight);
      return;
    }

    // Simplified before the timestamps go on, because the simplification cares
    // about the *shape* and a path of times has the same shape as a path of
    // indices — while the epsilon it uses is scale-free and the timestamps are
    // not, so doing it afterwards would make it depend on the epoch.
    const shaped = kind === "freehand" ? simplifyPath(path) : path;
    const timed: DrawPoint[] = [];
    for (const point of shaped) {
      // Already a timestamp. The bounds check survives the conversion going
      // away: a stroke that wandered past the last bar has no bar for those
      // points, and dropping them truncates the drawing rather than losing it —
      // the honest answer for a gesture that left the data.
      if (indexAtTime(this.barTimes, point.x) !== undefined) timed.push(point);
    }

    const x1 = indexAtTime(this.barTimes, start.x) === undefined ? undefined : start.x;
    const x2 = indexAtTime(this.barTimes, end.x) === undefined ? undefined : end.x;
    // **`hline` is exempt, because it stores no x at all.** It spans its pane
    // from one price, and `draftAnnotation` drops the x the press happened to
    // land on — so demanding that x be over a loaded bar guards nothing and
    // silently discards a click in the right margin. That margin sits beside
    // the newest price, which is exactly where somebody sets a level.
    if (kind !== "hline" && (x1 === undefined || (needsTwoPoints(kind) && x2 === undefined))) {
      return;
    }

    const annotation = draftAnnotation(
      kind,
      mintUlid(),
      { x: x1 ?? start.x, y: start.y },
      { x: x2 ?? x1 ?? start.x, y: end.y },
      timed,
    );
    if (kind === "freehand" && (annotation.points?.length ?? 0) < 2) return;

    // Selected on arrival, not now: the object on the surface a moment from now
    // is not this one. Without it a user draws a line, reaches for the style bar
    // and finds nothing — the bar keys off the selection, and a freshly drawn
    // annotation arrives unselected.
    this.selectOnArrival = annotation.id;
    this.drawnHandler?.(paneId, annotation);
  }

  /**
   * A plan whose X values are already bar indices, as a SciChart annotation.
   *
   * The preview's counterpart to {@link buildAnnotation}, which starts from
   * timestamps. Split rather than parameterised because the difference is one
   * line and a flag argument called `alreadyInIndexSpace` is the kind of thing
   * that gets passed wrong once and drifts for a release.
   */
  private buildDrawn(plan: AnnotationPlan, kind: AnnotationKind): unknown | null {
    const stroke = plan.style.stroke ?? this.theme.tokens.accent ?? this.theme.tokens.textSecondary;
    const common = {
      annotationLayer: EAnnotationLayer.AboveChart,
      // A rubber band is never editable: it exists for the length of one drag
      // and grips on it would be handles for something about to be replaced.
      isEditable: false,
      opacity: plan.style.opacity,
    };

    if (kind === "hline") {
      return new HorizontalLineAnnotation({
        ...common, y1: plan.y1!, stroke, strokeThickness: plan.style.thickness,
      });
    }
    if (kind === "text") {
      return new NativeTextAnnotation({
        ...common, x1: plan.x1!, y1: plan.y1!, text: plan.text ?? "",
        textColor: plan.style.fontColor ?? stroke, fontSize: plan.style.fontSize ?? 12,
      });
    }
    if (kind === "box") {
      return new BoxAnnotation({
        ...common, x1: plan.x1!, y1: plan.y1!, x2: plan.x2!, y2: plan.y2!,
        stroke, strokeThickness: plan.style.thickness, fill: plan.style.fill,
      });
    }
    if (kind === "line" || kind === "trendline") {
      return new LineAnnotation({
        ...common, x1: plan.x1!, y1: plan.y1!, x2: plan.x2!, y2: plan.y2!,
        stroke, strokeThickness: plan.style.thickness,
      });
    }
    if (kind === "fib") {
      return this.buildFib(plan, { x1: plan.x1!, x2: plan.x2! }, stroke, common);
    }
    if (kind === "freehand") {
      const points = plan.points;
      if (points.length < 2) return null;
      const children = points.slice(1).map(
        (point, i) =>
          new LineAnnotation({
            x1: points[i]!.x, y1: points[i]!.y, x2: point.x, y2: point.y,
            stroke, strokeThickness: plan.style.thickness, isEditable: false,
          }),
      );
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      return new CompositeAnnotation({
        ...common,
        x1: Math.min(...xs), x2: Math.max(...xs),
        y1: Math.max(...ys), y2: Math.min(...ys),
        annotations: children as never, fill: "transparent", strokeThickness: 0,
      });
    }
    return null;
  }

  setBarWindow(window: { min: number; max: number }): void {
    // **Times, not bar indices.** The name survives because a caller still
    // thinks in bars — "150 of them, ending at the last" — and converts on the
    // way in; the axis has spoken time since it became a `DiscontinuousDateAxis`
    // and a range in indices would land it at the epoch.
    if (!(window.max > window.min)) return;
    const range = new NumberRange(window.min, window.max);
    for (const pane of this.panes.values()) {
      for (const xAxis of pane.sub.xAxes.asArray()) {
        if (xAxis.autoRange !== EAutoRange.Never) xAxis.autoRange = EAutoRange.Never;
        xAxis.visibleRange = range;
      }
    }
    this.fitYAxes();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Give every pane's Y axis the same width, so their plot areas line up.
   *
   * **Why a constant floor is not enough.** SciChart lays a Y axis out as
   * `max(measuredLabels, axisThickness × dpr) + ticks`, so
   * {@link DEFAULT_Y_AXIS_THICKNESS} only equalises the panes whose labels are
   * *narrower* than it. One pane showing `12345.67` and another showing `1.23M`
   * both exceed it, by different amounts, and each takes its own width — the
   * plot areas then differ, the bars stretch by the difference, and a marker
   * drifts from the candle it belongs to, worst at the newest bar. Raising the
   * constant only moves the threshold: the next symbol with a longer label is
   * the same bug again.
   *
   * **Measured, not guessed.** The panes share a container, so a pane whose
   * `seriesViewRect.right` is smaller has a wider axis. The smallest right edge
   * is therefore the width every axis has to reach, and each pane's shortfall is
   * exactly the difference. Nothing here needs to know what a label measures or
   * what the tick allowance is — both are already inside the number being read
   * back, which is the point of measuring the result rather than the inputs.
   *
   * **Through `axisRenderer`, because the axis's own setter is broken.**
   * `axis.axisThickness = x` writes `axisLengthProperty` while the getter and
   * the layout both read `axisRenderer.axisThickness`, so it silently does
   * nothing — the same SciChart bug the lane axis documents, which is why that
   * one has to pass its thickness as a constructor option. The renderer's setter
   * writes the field the layout reads and invalidates the parent, and SciChart's
   * own doc for it says "useful to align seriesViewRects for different charts".
   *
   * **Only ever grows, and only past a pixel.** Shrinking would need the natural
   * width back, which is unmeasurable once an override is in place — reading the
   * thickness returns what was written, not what the labels need. Growing is
   * also what stops it oscillating: each pass moves every axis toward the widest
   * one and the widest one is left alone, so it converges instead of chasing
   * itself. A rebuild mints a new builder and the widths start over, and a
   * symbol change — the thing most likely to change label lengths — rebuilds.
   */
  private equalizeYAxisWidths(): void {
    const rects: Array<{ pane: PaneRender; left: number; right: number }> = [];
    for (const pane of this.panes.values()) {
      const rect = pane.sub.seriesViewRect;
      // Zero or absent means the surface has not laid out yet; a pass on those
      // numbers would write nonsense that only grows.
      if (rect && rect.right > 0) rects.push({ pane, left: rect.left, right: rect.right });
    }
    if (rects.length === 0) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    this.reportPlotLeft(Math.max(...rects.map((r) => r.left)) / dpr);
    if (rects.length < 2) return;

    // **Both edges, each grown only on its own side** (owner's screenshot,
    // 2026-10-30: an axis added on the price pane's left, and MACD's plot
    // started 70 px further left than price's). This measured the right edge
    // alone, so a left axis moved one pane's plot area and nothing followed;
    // and it grew *every* Y axis by the right-hand shortfall, which on a pane
    // with a left axis pushed the left edge in as well.
    const rightTarget = Math.min(...rects.map((r) => r.right));
    const leftTarget = Math.max(...rects.map((r) => r.left));
    let changed = false;
    for (const { pane, left, right } of rects) {
      // Device pixels short, in CSS pixels — the unit `axisThickness` and
      // `padding` are in, since the layout multiplies both by dpr.
      changed = this.growSide(pane, EAxisAlignment.Right, (right - rightTarget) / dpr) || changed;
      changed = this.growSide(pane, EAxisAlignment.Left, (leftTarget - left) / dpr) || changed;
    }

    if (!changed) return;
    this.parentSurface?.invalidateElement();

    // **One pass is not always enough, because of the `max`.** A pane whose
    // labels are wider than the thickness written to it does not move at all:
    // `max(labels, thickness × dpr)` is still the labels. So an axis needing 58
    // that is told 56 stays at 58's neighbour and is short by two. The next pass
    // sees the remaining gap and closes it, and the pass after that finds
    // nothing to do and stops.
    //
    // Bounded because a surface that cannot converge — a container resizing
    // under us every frame — must not schedule forever.
    if (this.equalizePasses < MAX_EQUALIZE_PASSES) {
      this.equalizePasses += 1;
      this.scheduleAxisEqualize();
    }
  }

  /**
   * Fit every Y axis to the bars now in the X window.
   *
   * **This is the price axes' auto-range, and it is ours rather than
   * SciChart's.** `EAutoRange.Always` left the newest bar out of the extent it
   * computed even with that bar's index inside the window — measured on ARMK,
   * the axis stopped at 58.68, the highest high of every bar *except* the last,
   * while the last bar's high was 61.52 and its index 2229 sat well inside a
   * window running to 2242.98. The candle drew with about a fifth of it on
   * screen and the rest above the pane, at every zoom level.
   *
   * The extremes come from the series' own tracked values — `highs`/`lows` for a
   * candle, the value itself for everything else — so a bar that is drawn is a
   * bar that fits. `plan/y-fit.ts` owns the arithmetic and is tested there.
   *
   * Two kinds of axis are deliberately skipped. The signal lane axis is a fixed
   * scale of lane indices and fitting it would move every lane the first time
   * one fired. An axis the contract pinned — `autoRange: "never"`, or one
   * carrying its own `visibleRange` — is a deliberate choice by whoever wrote
   * the workspace, and this is auto-ranging, not a licence to overrule it.
   */
  /**
   * Widen one side of a pane's plot inset by `grow` CSS pixels.
   *
   * Through that side's first visible Y axis when it has one; otherwise through
   * the sub-surface's padding, because a pane with no axis on that side has
   * nothing to grow — the MACD pane under a price pane with a left axis. One
   * axis, not all: two stacked axes on a side would each take the whole
   * shortfall and overshoot. Under half a pixel is rounding, not misalignment,
   * and writing it would invalidate every surface for nothing.
   */
  private growSide(pane: PaneRender, side: EAxisAlignment, grow: number): boolean {
    if (grow < 0.5) return false;
    const axis = pane.sub.yAxes.asArray().find((a) => a.isVisible && a.axisAlignment === side);
    if (axis) {
      axis.axisRenderer.axisThickness = axis.axisRenderer.axisThickness + grow;
      return true;
    }
    const p = pane.sub.padding;
    pane.sub.padding = side === EAxisAlignment.Left
      ? new Thickness(p.top, p.right, p.bottom, p.left + grow)
      : new Thickness(p.top, p.right + grow, p.bottom, p.left);
    return true;
  }

  /**
   * Where the plot areas start, in CSS pixels from the chart's left edge —
   * the width of any left axis. The host's value legend starts there, or a
   * left axis's labels and price tags draw over it (owner, 2026-10-30).
   * Reported on change only; read by `onPlotLeft`.
   */
  private plotLeft = 0;
  onPlotLeft: ((left: number) => void) | null = null;
  private reportPlotLeft(left: number): void {
    const rounded = Math.round(left);
    if (rounded === this.plotLeft) return;
    this.plotLeft = rounded;
    this.onPlotLeft?.(rounded);
  }

  /**
   * Re-equalise on the next frame.
   *
   * `seriesViewRect` is only meaningful after a layout pass, and a fit is
   * usually the thing that triggers one — so measuring in the same tick reads
   * the *previous* frame's geometry. Coalesced through one pending handle,
   * because a bulk load fits every pane in a loop and one pass covers them all.
   */
  private scheduleAxisEqualize(fromFit = false): void {
    if (fromFit) this.equalizePasses = 0;
    if (typeof requestAnimationFrame !== "function") return;
    if (this.equalizePending !== undefined) return;
    this.equalizePending = requestAnimationFrame(() => {
      this.equalizePending = undefined;
      if (!this.parentSurface) return;
      this.equalizeYAxisWidths();
    });
  }

  /** The band an axis declared, if any — see `bandedRange`. */
  private bandFor(axisId: string): { from: number; to: number } | undefined {
    for (const plan of this.plans.values()) {
      const found = plan.axes.find((a) => a.id === axisId);
      if (found) return found.band;
    }
    return undefined;
  }

  private fitYAxes(): void {
    this.scheduleAxisEqualize(true);
    for (const pane of this.panes.values()) {
      const x = pane.sub.xAxes.get(0)?.visibleRange;
      if (!x) continue;
      // **The window is a time range; `fitYRange` walks arrays.**
      //
      // It takes `from`/`to` as offsets into the series — `Math.floor(from)` to
      // `Math.ceil(to)` — so handing it epoch seconds made `start` 1.7e9 and
      // `end` the last index, the loop never ran, and no Y range was ever set.
      // With the Y axes on `EAutoRange.Never` they kept their default range and
      // every plot sat off-screen: a chart with data on it, drawing nothing.
      //
      // Converted here rather than teaching `fitYRange` about time, because
      // what it does *is* array arithmetic and the offsets are what it wants.
      const at = offsetAtTime(this.barTimes, x.min);
      const until = offsetAtTime(this.barTimes, x.max);
      if (at === undefined || until === undefined) continue;

      // **Cut at the last bar.** Anything drawn past it is a forecast, and a
      // forecast must not rescale the prices it is drawn over — a cone reaching
      // thirty per cent above the last close would otherwise push the candles
      // into the bottom third of the pane, with nothing on screen saying why.
      const window = fitWindow(at, until, this.realBars - 1);
      if (!window) continue;
      const { from, to } = window;

      for (const axis of pane.sub.yAxes.asArray()) {
        if (axis.id === SIGNAL_AXIS_ID) continue;
        if (this.pinnedYAxes.has(axis.id)) continue;
        if (this.manualYAxes.has(axis.id)) continue;

        const candidates = [...pane.byId.values()]
          // Signals, paint plots and hidden plots are left out — `fitsAxis`.
          .filter((pr) =>
            fitsAxis(
              { signal: pr.signal, paint: pr.paint, hidden: pr.hidden, yAxisId: pr.series.yAxisId },
              axis.id,
            ),
          )
          .map((pr) => ({
            highs: pr.highs ?? pr.values,
            lows: pr.lows ?? pr.values,
            // OHLC-fed is the price. See `seriesForFit` for why the data kind is
            // the discriminator rather than the plot's name or its position.
            isPrice: pr.dataKind === "ohlc",
          }));
        if (candidates.length === 0) continue;
        const series = seriesForFit(candidates, this.priceFit);

        const fitted = fitYRange(series, from, to);
        // **Banded after fitting, never instead of it.** A band says where a
        // *fitted* range lands in the pane, so there is nothing to widen until
        // the data has been measured. `growBy` cannot do this job: SciChart
        // applies it when it computes a range, and these axes are
        // `EAutoRange.Never` and are handed theirs — the same trap
        // `setRightMargin` documents for the X axis.
        const banded = fitted && bandedRange(fitted, this.bandFor(axis.id));
        const ranged = banded && this.withNoteRoom(pane, axis.id, banded);
        if (ranged) axis.visibleRange = new NumberRange(ranged.min, ranged.max);
      }
    }
  }

  /**
   * One text line more at the foot of the price axis while the `E`s carry
   * notes, so the lowest candle cannot sit on a YoY reading (owner,
   * 2026-10-07). The fit's own 5% already clears the glyph; this clears the
   * line above it. Paid back by the date axis, which lost its tick marks and
   * most of its label padding in the same change.
   */
  private withNoteRoom(
    pane: PaneRender,
    axisId: string,
    range: { min: number; max: number },
  ): { min: number; max: number } {
    if (pane !== this.pricePane() || axisId !== this.priceYAxisId()) return range;
    if (!this.markers.some((m) => m.note)) return range;
    const height = pane.sub.seriesViewRect?.height ?? 0;
    const line = (SciChartJsChartBuilder.NOTE_LINE_PX * (DpiHelper.PIXEL_RATIO || 1)) / height;
    if (!(height > 0) || !(line > 0 && line < 0.5)) return range;
    const span = range.max - range.min;
    return { min: range.min - (span * line) / (1 - line), max: range.max };
  }

  /** The X window on screen, read off the first pane's axis. */
  barWindow(): { min: number; max: number } | undefined {
    // In **times**, matching {@link setBarWindow}. Only the axis knows where a
    // pan left it, which is what makes this the way to widen a window without
    // moving its right edge.
    const x = [...this.panes.values()][0]?.sub.xAxes.get(0)?.visibleRange;
    return x ? { min: x.min, max: x.max } : undefined;
  }

  /**
   * Empty space past the last bar, as a fraction of the window.
   *
   * `growBy` is SciChart's own way of saying it, so this is a real axis property
   * rather than a phantom bar appended to the data — which was the other way to
   * get a gap and would have put a hole in every series, in the palette
   * providers, and in the last-value markers.
   *
   * It only bites where SciChart *computes* a range (the initial fit, a
   * zoom-extents), because `growBy` is applied during auto-ranging and ignored
   * for a range it was handed. {@link setBarWindow} therefore carries the same
   * margin in its `max`, and the two agree by construction rather than by luck.
   */
  setRightMargin(fraction: number): void {
    const grow = new NumberRange(0, Math.max(0, fraction));
    for (const pane of this.panes.values()) {
      for (const xAxis of pane.sub.xAxes.asArray()) xAxis.growBy = grow;
    }
    this.parentSurface?.invalidateElement();
  }

  /**
   * Scale the price axis to the bars alone, or to everything drawn on it.
   *
   * **A re-fit, never a rebuild.** It changes one number on one axis, so it must
   * stay out of `structuralKey` for `setPaneHeights`' reason: a WebGL teardown
   * plus a refetch to answer a question about scale would cost the loaded data
   * and the zoom window for a repaint.
   *
   * The problem it solves is a moving average or a band that runs well away from
   * the bars — after a gap, or a long average lagging a sharp move. The axis has
   * to cover it, so the candles compress into a strip in the middle of the pane
   * and the thing you are reading is the smallest thing on screen.
   *
   * It only affects an axis that has a price on it: `seriesForFit` falls back to
   * every series where none is OHLC-fed, so an RSI or volume pane is untouched
   * and no caller has to check a pane's role first.
   */
  setPriceFit(fit: PriceFit): void {
    if (this.priceFit === fit) return;
    this.priceFit = fit;
    this.fitYAxes();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Manual Y: the fit leaves an axis where a person put it.
   *
   * `true` freezes the price axis at its current range — the menu's *Manual*.
   * `false` hands every axis back to the fit — ›|, or picking *Price only* /
   * *All plots*. A drag or a Y wheel zoom sets it for its own pane
   * ({@link markPaneManual}). Reported through {@link setYManualHandler} so the
   * menu can tick it.
   */
  setYManual(manual: boolean): void {
    const was = this.manualYAxes.size > 0;
    if (manual) {
      this.manualYAxes.add(this.priceYAxisId());
    } else {
      this.manualYAxes.clear();
      this.fitYAxes();
      this.parentSurface?.invalidateElement();
    }
    if (was !== this.manualYAxes.size > 0) this.yManualHandler?.(this.manualYAxes.size > 0);
  }

  /** Whether any Y axis is held by hand. */
  isYManual(): boolean {
    return this.manualYAxes.size > 0;
  }

  setYManualHandler(handler: ((manual: boolean) => void) | null): void {
    this.yManualHandler = handler;
  }

  /** A drag or a Y wheel zoom in this pane: its axes stop following the fit. */
  private markPaneManual(pane: PaneRender): void {
    const was = this.manualYAxes.size > 0;
    for (const axis of pane.sub.yAxes.asArray()) {
      if (axis.id !== SIGNAL_AXIS_ID) this.manualYAxes.add(axis.id);
    }
    if (!was && this.manualYAxes.size > 0) this.yManualHandler?.(true);
  }

  /**
   * Pin one Y axis to a range, or hand it back to the fit.
   *
   * **A property write, never a rebuild.** An axis range is workspace data and
   * lives on `AxisInfo`, so it would otherwise travel through `structuralKey`
   * and cost a WebGL teardown plus a refetch every time somebody nudged a
   * minimum — the same trap `annotations`, `height`, `style` and `palette` are
   * each kept out of that key to avoid.
   *
   * `pinnedYAxes` is the part that has to move with it: {@link fitYAxes} skips
   * the axes the contract pinned, so an axis handed back to `undefined` has to
   * leave that set or it would keep its old range forever and read as a control
   * that stopped working. Re-fitting immediately is what makes going back to
   * Auto visible in the same frame rather than at the next append.
   */
  setAxisRange(axisId: string, range: { min: number; max: number } | undefined): void {
    let found = false;
    for (const pane of this.panes.values()) {
      for (const axis of pane.sub.yAxes.asArray()) {
        if (axis.id !== axisId) continue;
        found = true;
        if (range) {
          this.pinnedYAxes.add(axisId);
          axis.autoRange = EAutoRange.Never;
          axis.visibleRange = new NumberRange(range.min, range.max);
        } else {
          this.pinnedYAxes.delete(axisId);
        }
      }
    }
    if (!found) return;
    if (!range) this.fitYAxes();
    this.parentSurface?.invalidateElement();
  }

  /** One pane's modifiers, brought in line with the current interaction plan. */
  private applyInteractions(render: PaneRender): void {
    const direction = xyDirectionFor(this.interactions.zoomAxis);
    render.interaction.zoomPan.xyDirection = direction;
    render.interaction.wheelZoom.xyDirection = direction;
    render.interaction.xAxisDrag.isEnabled = this.interactions.axisDrag;
    render.interaction.yAxisDrag.isEnabled = this.interactions.axisDrag;
  }

  /** The resolved render plan for a built pane (exposed for inspection/testing). */
  getPanePlan(paneId: Ulid): PanePlan | undefined {
    return this.plans.get(paneId);
  }

  /**
   * The whole chart group as a PNG data URL, or `null` when it cannot be taken.
   *
   * **Not on the `ChartBuilder` interface**, like `debugStats` and
   * `getPanePlan`: a PNG is a browser concept, and the three native adapters
   * that implement the same contract have their own screenshot story.
   *
   * ## Why this works when a naive `toDataURL` would not
   *
   * SciChart 5.2.62 exposes no `exportToImage`/`exportToBlob`, and reading the
   * WebGL canvas directly is the obvious move that fails: `preserveDrawingBuffer`
   * is set inside the WASM GL context, so the drawing buffer is usually already
   * discarded and the read comes back **blank rather than erroring** — the worst
   * kind of failure, because a blank chart is a picture a model will happily
   * describe.
   *
   * `nextStateRender()` is the way through. Its own docs: *"if it is initialized
   * with `create` — the promise resolves after image data is copied to the 2D
   * canvas."* `buildGroup` uses `create`, so SciChart itself blits WebGL into
   * the 2D canvas and this awaits the blit before reading. D24 settled the same
   * approach for share-out.
   *
   * **One image covers every pane.** Panes are `SciChartSubSurface`s of a single
   * parent surface, so price, volume and every indicator pane are already in one
   * canvas at the right proportions.
   *
   * What it will not contain, because they are not on the canvas: SVG-layer
   * annotations and adorners, and every React overlay the app draws on top —
   * the value legend, the span controls, the PIP.
   *
   * ## `nextStateRender` waits for a render, and a still chart does not render
   *
   * It resolves on the surface's **next** render, not on a render it requests.
   * A chart with no realtime feed, nothing hovering and nothing resizing may not
   * redraw for a long time, and `freezeWhenOutOfView` stops one that is scrolled
   * away from redrawing at all. So the await had no upper bound: a first Summary
   * on a still chart was observed at ~70 seconds where the model call is ~20,
   * and every server-side measurement said 20 because the wait was in the
   * browser, before the request was sent.
   *
   * Two halves to the fix. `invalidateElement({ force: true })` **asks** for the
   * redraw rather than waiting for one to happen by chance, and the wait is
   * capped, because a picture is a nicety and the note reads fine without one —
   * the prompt is told to say so in `missing` and work from `closePath`.
   */
  async captureImage(timeoutMs = CAPTURE_TIMEOUT_MS): Promise<string | null> {
    const surface = this.parentSurface;
    if (!surface) return null;
    try {
      // Ask for the frame this is about to wait for.
      surface.invalidateElement({ force: true });

      let timer: ReturnType<typeof setTimeout> | undefined;
      const rendered = await Promise.race([
        // A rejection here is a disposed surface, not a reason to throw: it
        // lands as `false` and reads the same as a chart that never drew.
        surface.nextStateRender().then(
          () => true,
          () => false,
        ),
        new Promise<boolean>((resolve) => {
          timer = setTimeout(() => resolve(false), timeoutMs);
        }),
      ]);
      if (timer !== undefined) clearTimeout(timer);
      if (!rendered) return null;

      const canvas = surface.getMainCanvas();
      // A zero-sized canvas is a surface that has not laid out yet; its data URL
      // is a valid PNG of nothing, which would read as an empty chart.
      if (!canvas || canvas.width === 0 || canvas.height === 0) return null;
      return canvas.toDataURL("image/png");
    } catch {
      // A capture is a nicety. Whatever went wrong — a disposed surface, a lost
      // context, a tainted canvas — the caller carries on without a picture and
      // says so, which is better than failing the thing that wanted it.
      return null;
    }
  }

  /** Runtime stats for each built pane — for diagnostics/verification. */
  debugStats(): Array<{
    paneId: Ulid;
    seriesCount: number;
    xRange: { min: number; max: number };
    plot: { left: number; right: number };
    yAxes: Array<{ id: string; min: number; max: number }>;
    dataCounts: number[];
  }> {
    return [...this.panes.entries()].map(([paneId, p]) => {
      const x = p.sub.xAxes.get(0)?.visibleRange;
      return {
        paneId,
        seriesCount: p.sub.renderableSeries.asArray().length,
        xRange: { min: x?.min ?? NaN, max: x?.max ?? NaN },
        plot: { left: p.sub.seriesViewRect.x, right: p.sub.seriesViewRect.right },
        yAxes: p.sub.yAxes.asArray().map((a) => ({
          id: a.id,
          min: a.visibleRange.min,
          max: a.visibleRange.max,
        })),
        dataCounts: [...p.byId.values()].map((pr) => pr.dataSeries.count()),
      };
    });
  }

  updateSeries(delta: SeriesDelta): void {
    // **Every** plot fed by this alias, in every pane — a fill and the line it
    // is drawn under share one column, and each takes what its own `dataKind`
    // needs from the same delta. This used to stop at the first match, which is
    // why a band never drew: it was registered first and replaced.
    let touched = false;
    for (const pane of this.panes.values()) {
      for (const pr of pane.byAlias.get(delta.alias) ?? []) {
        // **An xyy series must never take a delta with no second edge.**
        // `XyyDataSeries.append` and `.update` both *default* the missing y1
        // rather than skipping the point, so a band fed a one-column delta puts
        // its far edge on zero and floods the pane — drawn confidently, and
        // nothing like the indicator. Skipping leaves it empty, which is a
        // visible gap and the answer this codebase keeps choosing.
        //
        // It matters because a fill shares its column with the line it spans: if
        // the server omits the *other* edge, that line must still draw, so the
        // delta arrives in its one-column form and only the band has to decline.
        if (pr.dataKind === "xyy" && !delta.append?.high && !delta.patchLast?.high) {
          continue;
        }
        touched = true;
        // **The patch before the append.** `diffChartGroup`'s patch is the bar
        // that was last *before* this frame, and at the session rollover one
        // delta carries both: yesterday's final values as the patch, today's
        // first bar as the append. Applied in the other order the patch landed
        // on the bar just appended — yesterday's candle drawn over today's for a
        // whole refresh period, and yesterday left as the last refresh before
        // the rollover had it.
        if (delta.patchLast) this.patchLast(pr, delta.patchLast);
        if (delta.append) this.appendData(pr, delta.append);
        if (pr.palette) pr.palette.setValues(pr.values);
        if (pr.paint || pr.seriesKind === "candlestick") this.repaintCandles(pane);
        this.updateMarker(pr);
        // A signal riding this plot has just had the ground move under it. This
        // is also what covers the ordering case: aliases arrive one message each
        // and a signal's may come first, when its anchor still has no bars and
        // every marker is dropped for want of a low to sit under.
        this.replaceSignalsAnchoredTo(pr);
      }
    }
    if (!touched) return;
    // A level fill follows its line; only panes holding one pay for this.
    for (const paneId of this.levelFills.keys()) this.refreshLevelFills(paneId);
    // Once, after the fan-out: both are whole-surface work, and a band plus its
    // edge would otherwise pay for them twice per bar.
    this.fitYAxes();
    // **This is the recompute, and the only one.** The bars moved, so the bins
    // did; nothing else changes them, which is why no pan or zoom is subscribed
    // to. It rides the fan-out rather than being addressed directly because a
    // `volumeByPrice` plot has no alias of its own — it is fed by the candles
    // and the volume column, and both arrive here.
    this.drawVolumeByPrice();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Move a series' axis marker to its latest value, creating it on first data.
   *
   * Created lazily rather than at build time: an annotation pinned to a value
   * before any data exists has nowhere meaningful to sit, and would flash at
   * zero until the first push arrived.
   */
  private updateMarker(pr: PlotRender): void {
    // A signal has no last *value* worth showing — its axis is lane indices and
    // its column is mostly zeros, so the marker would read "0" against a hidden
    // scale for every bar that did not fire.
    if (pr.signal || pr.paint) return;

    // **The last value that is a value, not the last element.** Reading `at(-1)`
    // was right while every series ended on the last bar, and stops being right
    // the moment a frame can run past it: a projection null-fills every other
    // column across its rows, so an SMA, a MACD line and the candles themselves
    // all end in `NaN` and every marker on the chart would blank out — not only
    // the one belonging to the plot that brought the projection.
    //
    // Scanning back rather than being handed a count: this is the render layer
    // and it holds the values, so the fact is local. A NaN in the middle of a
    // series — an indicator's warm-up, a symbol that did not trade — is
    // unaffected, since only the tail is skipped.
    const last = lastFinite(pr.values);
    if (last === undefined) return;

    const sub = this.surfaceOf(pr);
    if (!sub) return;

    if (!pr.marker) {
      pr.marker = new AxisMarkerAnnotation({
        y1: last,
        yAxisId: pr.series.yAxisId,
        backgroundColor: this.inkOf(pr),
        // Dark or white by contrast with the fill (`plan/axis-tags.ts`).
        color: readableTextOn(this.inkOf(pr)),
        fontSize: 11,
        // Born hidden if its series is. The marker arrives with the first bar,
        // long after the series was built, so a plot hidden in a saved workspace
        // would otherwise gain a price label the moment data landed.
        isHidden: pr.hidden ?? false,
      });
      sub.annotations.add(this.own(pr.marker));
      if (pr.dataKind === "ohlc") {
        pr.closeLine = new HorizontalLineAnnotation({
          y1: last,
          yAxisId: pr.series.yAxisId,
          stroke: this.theme.tokens.textMuted ?? "#8b949e",
          strokeThickness: 1,
          strokeDashArray: [4, 3],
          // Scenery, like a reference line: never takes the pointer, and the
          // price is already printed by the marker on the axis.
          isEditable: false,
          showLabel: false,
          isHidden: pr.hidden ?? false,
        });
        sub.annotations.add(this.own(pr.closeLine) as never);
        this.colorPriceMarker(pr);
      }
      this.raisePriceMarker(sub);
      return;
    }
    pr.marker.y1 = last;
    if (pr.closeLine) pr.closeLine.y1 = last;
    this.colorMarker(pr);
    this.colorPriceMarker(pr);
    this.labelMarker(pr, sub, last);
  }

  /**
   * The colour a plot is actually drawn in — what its axis label must wear.
   *
   * **Not `series.stroke` for every kind.** A scatter draws only its point
   * marker and never its stroke, so the stroke stays at SciChart's default
   * steel blue — and every dashed level (Persons Pivots, LCC, PPS) put a blue
   * tag on the axis beside a red or green line (owner, 2026-10-16). A column's
   * colour is its fill; its stroke is a zero-width outline.
   */
  private inkOf(pr: PlotRender): string {
    const fallback = this.theme.tokens.textSecondary ?? "#c9d1d9";
    const visible = (c: unknown): c is string =>
      typeof c === "string" && c !== "" && !/^#[0-9a-f]{6}00$/i.test(c) && c !== "transparent";
    if (pr.seriesKind === "scatter") {
      const fill = (pr.series as XyScatterRenderableSeries).pointMarker?.fill;
      if (visible(fill)) return fill;
    }
    if (pr.seriesKind === "column") {
      const fill = (pr.series as unknown as { fill?: string }).fill;
      if (visible(fill)) return fill;
    }
    const stroke = pr.series.stroke as string | undefined;
    return visible(stroke) ? stroke : fallback;
  }

  /** Keep a non-price label in its plot's colour after a restyle or theme switch. */
  private colorMarker(pr: PlotRender): void {
    if (!pr.marker || pr.dataKind === "ohlc") return;
    const ink = this.inkOf(pr);
    if (pr.marker.backgroundColor !== ink) pr.marker.backgroundColor = ink;
    const text = readableTextOn(ink);
    if (pr.marker.color !== text) pr.marker.color = text;
  }

  /**
   * A price (OHLC) label in the theme's positive colour when the last bar
   * closed above its open, negative otherwise (owner, 2026-09-29) — the
   * candle's own colour, where the series stroke is only an outline. Every
   * other plot keeps its stroke.
   */
  private raisePriceMarker(sub: SciChartSubSurface): void {
    // **The close's label on top of every other** (owner, 2026-09-29).
    // SciChart draws annotations in the order they were added, and markers
    // arrive with each plot's first data in no fixed order — an SMA's label
    // could cover the price. Re-added last whenever another label appears.
    for (const pane of this.panes.values()) {
      if (pane.sub !== sub) continue;
      for (const pr of pane.byId.values()) {
        if (pr.dataKind !== "ohlc" || !pr.marker) continue;
        sub.annotations.remove(pr.marker);
        sub.annotations.add(this.own(pr.marker));
      }
    }
  }

  private colorPriceMarker(pr: PlotRender): void {
    if (!pr.marker || pr.dataKind !== "ohlc") return;
    let i = pr.values.length - 1;
    while (i >= 0 && !Number.isFinite(pr.values[i]!)) i--;
    if (i < 0 || i >= pr.dataSeries.count()) return;
    const open = (pr.dataSeries as OhlcDataSeries).getNativeOpenValues().get(i);
    const up = pr.values[i]! > open;
    const color = up ? this.theme.tokens.positive : this.theme.tokens.negative;
    if (color && pr.marker.backgroundColor !== color) pr.marker.backgroundColor = color;
    if (color && pr.marker.color !== readableTextOn(color)) pr.marker.color = readableTextOn(color);
    // The line in the label's colour: muted grey on a dark pane was hard to
    // see (owner, 2026-09-29).
    if (color && pr.closeLine && pr.closeLine.stroke !== color) pr.closeLine.stroke = color;
  }

  /**
   * The label's text when a suffix is set; left to the axis when not.
   *
   * Formatted by the plot's own Y axis, so a price reads exactly as the axis
   * would have written it, with the countdown after it.
   */
  private labelMarker(pr: PlotRender, sub: SciChartSubSurface, value: number): void {
    if (!pr.marker) return;
    const suffix = this.labelSuffix.get(pr.plotId);
    if (!suffix) {
      // Back to the axis's own text — and untouched for every label that never
      // carried a suffix, so no other plot's marker changes format.
      if (this.suffixed.delete(pr.plotId)) {
        pr.marker.formattedValue = undefined as unknown as string;
      }
      return;
    }
    const axis = sub.yAxes.asArray().find((a) => a.id === pr.series.yAxisId) ?? sub.yAxes.get(0);
    const price = axis ? axis.labelProvider.formatCursorLabel(value) : value.toFixed(2);
    pr.marker.formattedValue = `${price}  ${suffix}`;
    this.suffixed.add(pr.plotId);
  }

  /** Plots whose label currently carries a suffix. */
  private readonly suffixed = new Set<Ulid>();

  /** The sub-surface a plot is rendered on. */
  private surfaceOf(pr: PlotRender): SciChartSubSurface | undefined {
    for (const pane of this.panes.values()) {
      if (pane.byId.has(pr.plotId)) return pane.sub;
    }
    return undefined;
  }

  /**
   * Restyle one plot in place — a **repaint, never a rebuild**.
   *
   * Colour, width and dash are all properties of series objects that already
   * exist, so the same bargain as {@link applyTheme}: rebuilding would destroy
   * the WebGL surface, drop the loaded bars and reset the zoom window to change
   * a dash array. Plot *type* is the exception and is not accepted here — a
   * candlestick and a line are different renderable classes over different data
   * shapes, so that one genuinely is structural.
   *
   * `style` is the plot's **whole** style, not a patch. Merging cannot express
   * "no dash": an absent key would be indistinguishable from "leave it alone",
   * so turning dashed back to solid would silently do nothing.
   */
  applyStyle(plotId: Ulid, style?: PlotStyle, palette?: PlotPalette): void {
    // **Written back to the model first, and before the search rather than
    // inside it.** `applyTheme` re-resolves every series from `paneModels`, so a
    // style change that skipped this would be undone by the next theme switch —
    // invisible until someone switches. It sits out here because not every plot
    // owns a renderable: a Volume-by-Price plot is drawn from the model alone,
    // so a loop that only remembered the style of plots it found a series for
    // would drop its opacity on the floor.
    this.rememberStyle(plotId, style, palette);

    for (const pane of this.panes.values()) {
      const pr = pane.byId.get(plotId);
      if (!pr) continue;
      this.restyleSeries(pr, {
        stroke: resolveColorRef(style?.stroke, this.theme),
        fill: resolveColorRef(style?.fill, this.theme),
        opacity: style?.opacity,
        thickness: style?.thickness,
        dash: resolvePlotDash(style),
        digital: style?.digital ?? false,
        markerShape: style?.markerShape ?? DEFAULT_MARKER_SHAPE,
        markerSize: style?.markerSize,
        markerText: style?.markerText,
        palette: resolvePalettePlan(palette, this.theme),
        candleStyle: palette?.candleStyle ?? "standard",
      });
      break;
    }

    // Only when the plot that changed is one of them, so a dash on a moving
    // average does not re-bin two hundred and fifty bars. The repaint reads the
    // model this method has just written, which is what makes the style panel's
    // opacity slider reach these bars at all.
    if (this.isVolumeByPrice(plotId)) this.drawVolumeByPrice();

    this.parentSurface?.invalidateElement();
  }

  /** Whether a plot id names a Volume-by-Price plot, per the retained models. */
  private isVolumeByPrice(plotId: Ulid): boolean {
    return this.paneModels.some((pane) =>
      pane.plots?.some((plot) => plot.id === plotId && plot.type === "volumeByPrice"),
    );
  }

  setPlotHidden(plotId: Ulid, hidden: boolean): void {
    // Entries rather than values: the plan is keyed by pane id, and the render
    // does not carry one.
    for (const [paneId, pane] of this.panes) {
      const pr = pane.byId.get(plotId);
      if (!pr) continue;

      pr.hidden = hidden;
      // Hiding a paint plot stops it painting; its own series is never shown.
      if (pr.paint) this.repaintCandles(pane);
      else pr.series.isVisible = !hidden;
      // Both halves, or hiding a signal leaves half of it on the chart — the
      // same pairing `createSignalSeries` makes when it builds them.
      if (pr.signal) pr.signal.series.isVisible = !hidden;
      // The axis marker is the plot's last value printed on the price axis. A
      // hidden series that keeps one leaves a number floating against nothing,
      // and on a crowded axis it is read as belonging to whatever is nearest.
      if (pr.marker) pr.marker.isHidden = hidden;
      if (pr.closeLine) pr.closeLine.isHidden = hidden;
      // A level fill shades this line, so it goes with it.
      this.refreshLevelFills(paneId);

      // Written back for `rememberStyle`'s reason: `paneModels` is what a theme
      // switch re-resolves from, and a model that still says `hidden: false`
      // would make the next repaint bring the series back.
      this.rememberHidden(plotId, hidden);

      // And the *plan*, which is a separate copy and was the one left lying.
      // `getPanePlan` is public and its `SeriesPlan.hidden` is read by callers
      // that draw their own affordances against it — a plan still saying
      // `false` tells a legend the plot is shown, so its toggle offers "hide"
      // on something already hidden and there is no way back.
      const plan = this.plans.get(paneId);
      if (plan) {
        this.plans.set(paneId, {
          ...plan,
          series: plan.series.map((sp) => (sp.plotId === plotId ? { ...sp, hidden } : sp)),
        });
      }

      // **This is the path a Volume-by-Price plot actually takes today.**
      // `plan/series.ts` maps it to a column over an `xy` DataSeries, so it has
      // a renderable and the loop finds it — an empty one, because nothing ever
      // feeds a plot with no alias. Hiding that series therefore hides nothing
      // anybody can see; the bars are annotations, and the model write above is
      // what their repaint reads.
      if (this.isVolumeByPrice(plotId)) this.drawVolumeByPrice();

      // The Y axis fits what is *visible*, so hiding an overlay that ranged
      // wider than the candles is also what gives the price back its height.
      this.fitYAxes();
      this.parentSurface?.invalidateElement();
      return;
    }

    // **And the same plot if it ever stops having one.** Volume-by-Price is the
    // only plot whose picture does not come from a renderable, so the day the
    // plan layer stops minting an empty column for it the loop above would walk
    // every pane and return having done nothing — a toggle that silently does
    // not work, found by a user rather than by a compiler. The model write is
    // the *same* one, factored rather than repeated, because two copies of "how
    // hidden is recorded" is exactly the shape the band fill was wrong in.
    if (!this.isVolumeByPrice(plotId)) return;
    this.rememberHidden(plotId, hidden);
    this.drawVolumeByPrice();
    this.parentSurface?.invalidateElement();
  }

  /**
   * Record a plot's visibility in the retained models — one place, two callers.
   *
   * `paneModels` is what a theme switch and every Volume-by-Price repaint
   * re-resolve from, so a model still saying `hidden: false` is a model that
   * brings the plot back on the next repaint.
   */
  private rememberHidden(plotId: Ulid, hidden: boolean): void {
    this.paneModels = this.paneModels.map((pane) =>
      pane.plots?.some((p) => p.id === plotId)
        ? {
            ...pane,
            plots: pane.plots.map((p) => (p.id === plotId ? { ...p, hidden } : p)),
          }
        : pane,
    );
  }

  /**
   * Fold a new style into the retained pane models.
   *
   * `plans` cannot serve: they hold colours already resolved against the theme
   * of the moment, which is exactly what a theme switch has to re-resolve *from*
   * the model. So the model is what has to stay current.
   */
  private rememberStyle(plotId: Ulid, style?: PlotStyle, palette?: PlotPalette): void {
    this.paneModels = this.paneModels.map((pane) => {
      if (!pane.plots?.some((p) => p.id === plotId)) return pane;
      return {
        ...pane,
        plots: pane.plots.map((p) =>
          p.id === plotId
            ? { ...p, ...(style ? { style } : {}), ...(palette ? { palette } : {}) }
            : p,
        ),
      };
    });
  }

  addAxis(axisInfo: AxisInfo): void {
    // The interface has no pane parameter, so add to the most-recently-built pane.
    if (!this.lastPaneId) return;
    const pane = this.panes.get(this.lastPaneId);
    const wasmContext = this.parentSurface?.webAssemblyContext2D;
    if (!pane || !wasmContext) return;
    const ap = resolveAxisPlan(axisInfo, this.theme);
    pane.sub.yAxes.add(this.createYAxis(wasmContext, ap));
    this.parentSurface?.invalidateElement();
  }

  dispose(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    // A timer outlives the surface it is asking about, and every tick after
    // this point reads modifiers whose panes are gone.
    this.stopWatchingPlacement();
    // The equalise pass runs a frame later and reads `panes` and the parent
    // surface — both of which are gone by the time it would fire.
    if (this.equalizePending !== undefined) {
      cancelAnimationFrame(this.equalizePending);
      this.equalizePending = undefined;
    }
    this.container = undefined;
    // Deleting the parent tears down its sub-surfaces too.
    this.parentSurface?.delete();
    this.parentSurface = undefined;
    this.panes.clear();
    this.linkedLines.clear();
    this.plans.clear();
    this.paneModels = [];
    // The boxes went with the surface that owned them; the map did not, and a
    // retained one would hand a later repaint annotations belonging to a deleted
    // sub-surface. Nothing rebuilds through this instance today — a rebuild
    // mints a new builder — so this is the belt to `panes.clear()`'s braces.
    this.volumeByPriceBoxes.clear();
    this.volumeByPriceLabels.clear();
    this.lastPaneId = undefined;
    // The modifiers went with the surface, but the array did not — and each one
    // still holds the host's handler, so a disposed chart would keep a closure
    // over a component that has unmounted alive for as long as this object is.
    this.hoverCursors.length = 0;
    this.emphasised = null;
  }

  // ── internals ───────────────────────────────────────────────────────────

  /**
   * The SciChart theme provider for the active QuFolio theme.
   *
   * A built-in provider is *instantiated* and then overridden rather than
   * spread into a plain object: the provider carries `getStrokeColor`/
   * `getFillColor` on its prototype for SciChart's AUTO_COLOR path, and a spread
   * copies only own properties — so a spread theme throws the moment anything
   * asks for an auto color. The light base is chosen by `mode`, so what is left
   * unmapped (3D planes, color maps) still contrasts the right way round.
   */
  private surfaceTheme(): IThemeProvider {
    const base =
      this.theme.mode === "light" ? new SciChartJSLightTheme() : new SciChartJSDarkv2Theme();
    return Object.assign(base, sciChartThemeOverrides(this.theme));
  }

  /**
   * Reapply a series' appearance from a freshly-resolved plan, in place.
   *
   * Each renderable series type exposes a different set of colour properties, so
   * this switches on the kind recorded at creation rather than probing the
   * object. Data, axes and the zoom window are untouched — which is the whole
   * point, and the reason both {@link applyTheme} and {@link applyStyle} come
   * through here rather than each doing its own half of the job.
   */
  private restyleSeries(pr: PlotRender, sp: SeriesStyle): void {
    const [up, down] = this.pairedColors(sp.palette);
    const stroke = sp.stroke ?? up;
    const thickness = sp.thickness ?? DEFAULT_THICKNESS[pr.seriesKind];

    // A paint plot's colours are the candles' business, and its series must
    // stay invisible — the column path below would hand it a palette to draw.
    if (pr.paint) {
      pr.paint = this.paintArgb(sp.palette);
      this.repaintCandles(this.paneOf(pr));
      return;
    }

    // A signal is two renderables and its two colours are *paired*, not one
    // stroke — so it does not go through the single-series path below, which
    // would repaint the up markers and leave the down ones on the old theme.
    if (pr.signal) {
      this.restyleSignal(pr, sp, up, down, thickness);
      return;
    }

    // Width first, because it is the one property every kind shares: "how heavy
    // is this series" is one question whatever shape answers it, so a candle's
    // outline and a line's stroke read the same number.
    pr.series.strokeThickness = thickness;
    pr.series.opacity = sp.opacity ?? 1;

    // A dash is only expressible on the stroked-line family — SciChart puts
    // `strokeDashArray` on the line/mountain/band bases and nowhere else, so a
    // dashed candle body is not something the renderer can say. Written even
    // when empty: omitting it would leave a dash behind when switching back to
    // solid, which reads as the control not working.
    if (hasDashArray(pr.seriesKind)) {
      (pr.series as unknown as { strokeDashArray: number[] }).strokeDashArray = sp.dash ?? [];
    }

    switch (pr.seriesKind) {
      case "candlestick": {
        const s = pr.series as FastCandlestickRenderableSeries;
        s.strokeUp = up;
        s.strokeDown = down;
        s.brushUp = up + "aa";
        s.brushDown = down + "aa";
        pr.candleStyle = sp.candleStyle;
        pr.candleArgb = candleArgb(up, down);
        this.repaintCandles(this.paneOf(pr));
        break;
      }
      case "ohlc": {
        const s = pr.series as FastOhlcRenderableSeries;
        s.strokeUp = up;
        s.strokeDown = down;
        break;
      }
      case "line": {
        const s = pr.series as FastLineRenderableSeries;
        s.stroke = stroke;
        // Rebuilt from the new plan and re-fed, for `column`'s reason below; a
        // palette taken away has to clear the provider or the colours outlive it.
        const provider = paletteProviderFor(sp.palette);
        if (provider) {
          provider.setValues(pr.values);
          pr.palette = provider;
          s.paletteProvider = provider;
        } else if (pr.palette) {
          pr.palette = undefined;
          s.paletteProvider = new DefaultPaletteProvider();
        }
        // A property write, which is the whole argument for `digital` being a
        // style rather than a `PlotType`: toggling it here is a repaint, where
        // a type change is a teardown and a refetch.
        s.isDigitalLine = sp.digital;
        break;
      }
      case "mountain": {
        const s = pr.series as FastMountainRenderableSeries;
        s.stroke = stroke;
        s.fill = sp.fill ?? stroke + "33";
        s.isDigitalLine = sp.digital;
        break;
      }
      case "band": {
        const s = pr.series as FastBandRenderableSeries;
        s.isDigitalLine = sp.digital;
        const paired = sp.palette.kind === "paired" ? sp.palette : undefined;
        s.stroke = paired?.up ?? stroke;
        s.strokeY1 = paired?.down ?? stroke;
        // **Through `bandFills`, and this is the path that decides what you
        // see.** `applyTheme` runs from the host's own mount effect, so this
        // repaints every freshly built band — a correction applied only to the
        // construction site below is overwritten before the first frame.
        const fills = bandFills(sp, stroke, paired);
        s.fill = fills.fill;
        s.fillY1 = fills.fillY1;
        break;
      }
      case "column": {
        const s = pr.series as FastColumnRenderableSeries;
        const provider = paletteProviderFor(sp.palette);
        if (provider) {
          // The provider caches per-bar colors, so it has to be rebuilt from the
          // new plan and re-fed the values it already painted. This is also the
          // whole of the sign-coloured histogram: `column` + `colorMode: "sign"`
          // paints each bar by whether it is above or below zero, so MACD's
          // histogram needs no plot type of its own.
          provider.setValues(pr.values);
          pr.palette = provider;
          s.paletteProvider = provider;
          s.fill = sp.fill ?? stroke;
        } else {
          // Dropping back to one colour has to clear the provider too, or the
          // per-bar override outlives the palette that produced it.
          pr.palette = undefined;
          s.paletteProvider = new DefaultPaletteProvider();
          s.fill = sp.fill ?? stroke;
        }
        // The outline is only drawn when a width was asked for; the default is
        // zero, which is exactly the transparent stroke a volume bar had before.
        s.stroke = thickness > 0 ? stroke : "transparent";
        break;
      }
      case "scatter": {
        const scatter = pr.series as XyScatterRenderableSeries;
        // A shape change swaps the marker — the glyph is baked into its sprite —
        // and is still a repaint: same series, same data. See `restyleSignal`.
        const retyped = sp.markerShape === "text" && pr.markerText !== sp.markerText;
        if (((pr.markerShape ?? DEFAULT_MARKER_SHAPE) !== sp.markerShape || retyped) && this.wasmContext) {
          const old = scatter.pointMarker;
          const Kind = markerKind(glyphOf(sp.markerShape), "up", sp.markerText);
          scatter.pointMarker = new Kind(this.wasmContext, {
            ...markerBox(sp.markerShape, sp.markerSize, SCATTER_MARKER_PX, sp.markerText),
            fill: stroke,
            stroke,
            strokeThickness: thickness,
          });
          old?.delete();
          pr.markerShape = sp.markerShape;
          pr.markerText = sp.markerText;
        }
        const marker = scatter.pointMarker;
        if (marker) {
          const box = markerBox(sp.markerShape, sp.markerSize, SCATTER_MARKER_PX, sp.markerText);
          if (marker.width !== box.width) marker.width = box.width;
          if (marker.height !== box.height) marker.height = box.height;
          marker.fill = stroke;
          marker.stroke = stroke;
          // A scatter's "width" is its marker outline: the series stroke is not
          // drawn at all, so putting the number there would be a control with
          // no effect.
          marker.strokeThickness = thickness;
        }
        break;
      }
    }
    // The axis label follows the new colour now, not at the next tick.
    this.colorMarker(pr);
  }

  private createYAxis(wasmContext: TSciChart, ap: AxisPlan): NumericAxis | LogarithmicAxis {
    // **An axis that would auto-range is handed to `fitYAxes` instead**, so it is
    // created `Never` and given a range by us on every window change and append.
    // See `fitYAxes` for the bar this exists to stop losing. Anything the
    // contract pinned keeps SciChart's own behaviour and is recorded so the fit
    // leaves it alone.
    const ours = ap.autoRange === "always" && !ap.visibleRange;
    if (!ours) this.pinnedYAxes.add(ap.id);

    const options = {
      id: ap.id,
      axisAlignment: ap.side === "left" ? EAxisAlignment.Left : EAxisAlignment.Right,
      autoRange: ours ? EAutoRange.Never : mapAutoRange(ap.autoRange),
      drawMinorGridLines: false,
      // Uniform width across panes → plot areas (and bars) align to the pixel.
      axisThickness: this.yAxisThickness,
      ...(ap.visibleRange
        ? { visibleRange: new NumberRange(ap.visibleRange.min, ap.visibleRange.max) }
        : {}),
      ...(ap.labelPrecision !== undefined ? { labelPrecision: ap.labelPrecision } : {}),
      // **Hidden means no width, which is the point.** An overlay axis shares
      // somebody else's pane — volume under candles — and must not push the
      // plot area in, or the pane would be inset twice and its bars would
      // stretch against every other pane's. The lane axis makes the opposite
      // choice for the opposite reason: it is the *only* axis on its strip, so
      // hiding it would leave that pane with no inset at all.
      ...(ap.visible ? {} : { isVisible: false }),
    };
    const axis = ap.logarithmic
      ? new LogarithmicAxis(wasmContext, options)
      : new NumericAxis(wasmContext, options);
    this.styleGrid(axis, ap.grid);
    this.compactLabels(axis);
    if (ap.signedLog) {
      // After `compactLabels`, which it replaces: these labels are percentages
      // of the inverted value, and a K/M on the drawn position would be wrong.
      this.signedLogAxes.add(ap.id);
      axis.tickProvider = new SignedLogTickProvider(wasmContext);
      axis.labelProvider.formatLabel = signedLogLabel;
      axis.labelProvider.formatCursorLabel = signedLogLabel;
    }
    return axis;
  }

  /**
   * K / M / B on an indicator axis once a label reaches 10,000 (owner,
   * 2026-10-06) — volume, OBV, A/D and every other count that runs into the
   * millions, including the ones already on a chart, with no list to keep.
   *
   * **Never the price axis.** A $60,000 stock reads 60,000.00, not 60K: there
   * the digits are the reading. Decided per label rather than per axis, so a
   * small reading on the same axis keeps its own precision. The axis tag reads
   * `formatCursorLabel`, so it follows.
   */
  private compactLabels(axis: AxisBase2D): void {
    const provider = axis.labelProvider;
    const label = provider.formatLabel.bind(provider);
    const cursor = provider.formatCursorLabel.bind(provider);
    const priced = () => axis.id === this.priceYAxisId();
    provider.formatLabel = (v: number) =>
      !priced() && Math.abs(v) >= COMPACT_FROM ? compactNumber(v) : label(v);
    provider.formatCursorLabel = (v: number) =>
      !priced() && Math.abs(v) >= COMPACT_FROM ? compactNumber(v) : cursor(v);
  }

  /**
   * Apply a resolved grid plan to one axis.
   *
   * `strokeDashArray` is written unconditionally, including as `undefined` —
   * omitting the key would leave a dash behind when switching dotted back to
   * solid, which reads as the setting not working. Colour and thickness are the
   * opposite: they are only written when the model actually specified them, so
   * an unstyled axis keeps whatever the active theme gave it (D13).
   */
  private styleGrid(axis: AxisBase2D, grid: AxisGridPlan): void {
    axis.drawMajorGridLines = grid.draw;
    if (!grid.draw) return;
    axis.majorGridLineStyle = {
      ...axis.majorGridLineStyle,
      ...(grid.majorColor ? { color: grid.majorColor } : {}),
      ...(grid.majorThickness !== undefined
        ? { strokeThickness: grid.majorThickness }
        : {}),
      strokeDashArray: grid.majorDash,
    };
  }

  /**
   * Change the gridline pattern without rebuilding — the same bargain as
   * {@link applyTheme} and {@link setPaneHeights}. Restyling axes in place keeps
   * the loaded bars and the zoom window; a rebuild would discard both to change
   * a dash array.
   */
  setGridLines(gridLines: GridLineStyle): void {
    this.gridLines = gridLines;
    if (!this.parentSurface) return;

    for (const pane of this.paneModels) {
      const render = this.panes.get(pane.id);
      if (!render) continue;
      const plan = buildPanePlan(pane, this.theme, gridLines, this.axisSpecs, this.eventKinds);
      this.plans.set(pane.id, plan);

      for (const xAxis of render.sub.xAxes.asArray()) this.styleGrid(xAxis, plan.xGrid);
      for (const yAxis of render.sub.yAxes.asArray()) {
        // **Never the lane axis.** Its scale is lane *indices*, so a gridline on
        // it is a line drawn at "between marker row 2 and 3" — a number with no
        // meaning to a reader, on the one axis built `drawLabels: false`
        // precisely because its numbers mean nothing. It is built with
        // `drawMajorGridLines: false`, and the fallback below (no `AxisPlan`
        // matches its id, so it takes the X grid's) would turn that back on.
        // The mirror axis goes with it: it is a zero-width, `isVisible: false`
        // twin that exists only to give a series-anchored signal the anchor's
        // scale, so gridlines on it would double every line the real axis draws.
        if (yAxis.id === SIGNAL_AXIS_ID || yAxis.id.startsWith(SIGNAL_MIRROR_PREFIX)) continue;
        const ap = plan.axes.find((a) => a.id === yAxis.id);
        this.styleGrid(yAxis, ap?.grid ?? { ...plan.xGrid });
      }
    }

    this.parentSurface.invalidateElement();
  }

  private createSeries(
    wasmContext: TSciChart,
    sp: SeriesPlan,
    defaultYAxisId: string,
  ): PlotRender {
    const yAxisId = sp.axisRef ?? defaultYAxisId;
    const [up, down] = this.pairedColors(sp.palette);
    const stroke = sp.stroke ?? up;
    // Per kind, not a single 2: a candle outline and a volume bar's edge are not
    // the same weight as a moving average, and a shared default would visibly
    // fatten every plot that never asked for a width.
    const thickness = sp.thickness ?? DEFAULT_THICKNESS[sp.seriesKind];
    // Only on the kinds that can draw one; an empty array is SciChart's "solid".
    const dash = sp.dash?.length ? { strokeDashArray: sp.dash } : {};

    // Before the switch, because a signal is not one renderable and the switch
    // below produces exactly one. `seriesKind` is "scatter" for it, which is the
    // right *shape* and the wrong count.
    if (sp.signal) return this.createSignalSeries(wasmContext, sp, up, down, thickness);

    const dataSeries = this.createDataSeries(wasmContext, sp.dataKind);
    let series: IRenderableSeries;
    let palette: PlanPaletteProvider | undefined;

    switch (sp.seriesKind) {
      case "candlestick":
        series = new FastCandlestickRenderableSeries(wasmContext, {
          dataSeries: dataSeries as OhlcDataSeries,
          yAxisId,
          strokeUp: up,
          strokeDown: down,
          brushUp: up + "aa",
          brushDown: down + "aa",
          strokeThickness: thickness,
          // SciChart's default is 0.5 — a body half the width of its slot, with
          // the other half as gap. 0.8 reads as a candle chart rather than a
          // barcode, and still leaves the wicks of adjacent bars separated.
          dataPointWidth: 0.8,
        });
        // Coloured by `repaintCandles` once bars (and any paint) arrive; the
        // rule and colours are kept on the render for it.
        break;
      case "ohlc":
        series = new FastOhlcRenderableSeries(wasmContext, {
          dataSeries: dataSeries as OhlcDataSeries,
          yAxisId,
          strokeUp: up,
          strokeDown: down,
          strokeThickness: thickness,
        });
        break;
      case "line":
        // A paired palette colours each segment by its point — the price line
        // in two colours (`TWO_TONE_LINE` in the web, 2026-10-03). Until then
        // only a column took a palette, so one on a line drew nothing.
        palette = paletteProviderFor(sp.palette);
        series = new FastLineRenderableSeries(wasmContext, {
          dataSeries: dataSeries as XyDataSeries,
          yAxisId,
          stroke,
          strokeThickness: thickness,
          isDigitalLine: sp.digital,
          ...dash,
          ...(palette ? { paletteProvider: palette } : {}),
        });
        break;
      case "mountain":
        series = new FastMountainRenderableSeries(wasmContext, {
          dataSeries: dataSeries as XyDataSeries,
          yAxisId,
          isDigitalLine: sp.digital,
          stroke,
          strokeThickness: thickness,
          fill: sp.fill ?? stroke + "33",
          ...dash,
        });
        break;
      case "column": {
        palette = paletteProviderFor(sp.palette);
        series = new FastColumnRenderableSeries(wasmContext, {
          dataSeries: dataSeries as XyDataSeries,
          yAxisId,
          // Zero-width by default, which draws exactly the transparent-stroked
          // bar this had before — so a volume pane that never asked for an
          // outline does not gain one.
          stroke: thickness > 0 ? stroke : "transparent",
          strokeThickness: thickness,
          dataPointWidth: 0.7,
          fill: sp.fill ?? stroke,
          ...(palette ? { paletteProvider: palette } : {}),
        });
        break;
      }
      case "band": {
        // A band has **two** fills, and which one you see depends on which
        // series is on top — that is what makes an RSI-against-its-average band
        // readable at a glance rather than a shaded tube.
        //
        // The paired palette already means exactly this everywhere else
        // (candles, sign-coloured histograms), so a crossover band needs no new
        // contract field: `upColor` paints where the first series leads,
        // `downColor` where the second does. A band with no palette — every
        // channel in the catalog — falls back to one translucent fill, which is
        // the right reading for Bollinger, where neither edge "leads".
        const paired = sp.palette.kind === "paired" ? sp.palette : undefined;
        // **The alpha is applied once, and `opacity` is where it lives.**
        // `series.opacity` below already carries the band's transparency, so a
        // colour that carries its own multiplies the two — and that is a bug
        // that renders rather than throws. Bollinger declares 0.08 and drew at
        // **1.6%**: present, correct, and invisible. Keltner, Donchian,
        // Envelope and SE Bands were the same, Ichimoku's cloud 14% → 2.8%.
        //
        // The tell was which bands looked *right*: Chandelier and the
        // seasonality cone, the only two naming a `fill` token, because that is
        // the one branch no alpha was appended to. Everything else went through
        // `translucent()` or the `+"33"` fallback and came out a fifth as
        // strong as the catalog asked for.
        //
        // So the colour is opaque whenever the plot states an opacity, and only
        // a band with none — nothing in the catalog, but a composed one may —
        // still needs the colour to supply it.
        series = new FastBandRenderableSeries(wasmContext, {
          dataSeries: dataSeries as XyyDataSeries,
          yAxisId,
          isDigitalLine: sp.digital,
          stroke: paired?.up ?? stroke,
          strokeY1: paired?.down ?? stroke,
          strokeThickness: thickness,
          ...bandFills(sp, stroke, paired),
          ...dash,
        });
        break;
      }
      case "scatter":
        series = new XyScatterRenderableSeries(wasmContext, {
          dataSeries: dataSeries as XyDataSeries,
          yAxisId,
          // `.up` because a scatter has no direction: `triangle` points up,
          // and a sell mark asks for `triangleDown` by name. Circle is the
          // default, which is the ellipse this always drew.
          pointMarker: new (markerKind(glyphOf(sp.markerShape), "up", sp.markerText))(wasmContext, {
            ...markerBox(sp.markerShape, sp.markerSize, SCATTER_MARKER_PX, sp.markerText),
            fill: stroke,
            stroke,
            strokeThickness: thickness,
          }),
        });
        break;
    }

    if (sp.opacity !== undefined) series.opacity = sp.opacity;
    if (sp.hidden) series.isVisible = false;
    // A paint plot draws nothing of its own — see `PlotRender.paint`.
    const paint = sp.plotType === "paint" ? this.paintArgb(sp.palette) : undefined;
    if (paint) series.isVisible = false;

    return {
      plotId: sp.plotId,
      alias: sp.outputAlias,
      series,
      dataSeries,
      dataKind: sp.dataKind,
      hidden: sp.hidden,
      seriesKind: sp.seriesKind,
      palette,
      ...(sp.seriesKind === "candlestick"
        ? { candleStyle: sp.candleStyle, candleArgb: candleArgb(up, down) }
        : {}),
      ...(paint ? { paint } : {}),
      values: [],
      ...(sp.seriesKind === "scatter" ? { markerShape: sp.markerShape } : {}),
    };
  }

  /**
   * One signal plot as two scatter series, split by the sign of its column.
   *
   * Two rather than one because per-point marker colour in SciChart is
   * `overridePointMarkerArgb`, a palette interface the scatter path does not
   * implement — where two series with a solid colour each need nothing at all.
   * They share a lane, so up and down markers sit on the same row and read as
   * one indicator rather than two.
   *
   * Both hang off {@link SIGNAL_AXIS_ID} rather than the pane's own axis. A
   * signal's y is a lane index, so putting it on the price axis would place the
   * markers at $1 and $-1 and drag the axis down to reach them.
   */
  /**
   * Repaint both halves of a signal, keeping up and down distinct.
   *
   * A signal's colour is the *paired* tier by nature — the two directions are
   * the point — so it reads `up`/`down` where every other kind here reads a
   * single stroke. `PlotStyle.stroke` deliberately does not override them: one
   * colour for both would leave the triangles as the only distinction, which is
   * a shape difference of a few pixels.
   */
  private restyleSignal(
    pr: PlotRender,
    sp: SeriesStyle,
    up: string,
    down: string,
    thickness: number,
  ): void {
    const signal = pr.signal;
    if (!signal) return;

    const shape = effectiveSignalShape(sp.markerShape, signal.anchor);
    const retyped = shape === "text" && signal.markerText !== sp.markerText;
    if ((signal.markerShape !== shape || retyped) && this.wasmContext) {
      const wasmContext = this.wasmContext;
      if (shape === "column" || signal.markerShape === "column") {
        // **To or from `column` swaps the renderable**, not the marker: a column
        // is a `FastColumnRenderableSeries`, a glyph a scatter. Still a repaint
        // — the two XyDataSeries are handed across untouched, so nothing is
        // refetched and the zoom window stays — and the ys are rewritten by
        // `placeSignal`, because a column's y is its lane's top, not its centre.
        const size = sp.markerSize;
        const make = (old: IRenderableSeries, half: SignalDirection, colour: string) =>
          this.createSignalRenderable(wasmContext, old.dataSeries as XyDataSeries,
            old.yAxisId ?? SIGNAL_AXIS_ID, shape, half, colour, size, thickness, signal.lane, sp.markerText);
        const nextUp = make(pr.series, "up", up);
        const nextDown = make(signal.series, "down", down);
        this.swapRenderable(pr, pr.series, nextUp);
        this.swapRenderable(pr, signal.series, nextDown);
        pr.series = nextUp;
        signal.series = nextDown;
        signal.markerShape = shape;
        signal.markerText = sp.markerText;
        this.placeSignal(pr);
      } else {
        // **A glyph change swaps the marker; everything else edits it in place.**
        // SciChart bakes the glyph into the marker's sprite, so there is no
        // property to set — but the marker is all that changes, so this is still
        // a repaint: same series, same DataSeries, nothing refetched.
        const swap = (series: IRenderableSeries, Kind: SignalMarkerCtor): void => {
          const scatter = series as XyScatterRenderableSeries;
          const old = scatter.pointMarker;
          scatter.pointMarker = new Kind(wasmContext, {
            ...markerBox(shape, sp.markerSize, SIGNAL_MARKER_PX, sp.markerText),
            fill: "#000",
            stroke: "#000",
            strokeThickness: thickness,
          });
          old?.delete();
        };
        swap(pr.series, markerKind(glyphOf(shape), "up", sp.markerText));
        swap(signal.series, markerKind(glyphOf(shape), "down", sp.markerText));
        signal.markerShape = shape;
        signal.markerText = sp.markerText;
      }
    }

    if (signal.markerShape === "column") {
      // A column has no glyph and no size: its fill is the colour, its width is
      // the bar's slot and its height the lane's. No outline — a stroke in the
      // fill's own colour would only eat the gap between neighbouring columns.
      const fillColumn = (series: IRenderableSeries, colour: string): void => {
        const column = series as FastColumnRenderableSeries;
        column.opacity = sp.opacity ?? 1;
        column.fill = colour;
        column.stroke = "transparent";
        column.strokeThickness = 0;
      };
      fillColumn(pr.series, up);
      fillColumn(signal.series, down);
      return;
    }

    const paint = (series: IRenderableSeries, colour: string): void => {
      series.strokeThickness = thickness;
      series.opacity = sp.opacity ?? 1;
      const pointMarker = (series as XyScatterRenderableSeries).pointMarker;
      if (pointMarker) {
        pointMarker.fill = colour;
        pointMarker.stroke = colour;
        // A scatter's series stroke is never drawn; the number belongs on the
        // marker's outline or the width control does nothing.
        pointMarker.strokeThickness = thickness;
        // Size edits the marker in place — SciChart re-renders the sprite.
        const box = markerBox(signal.markerShape, sp.markerSize, SIGNAL_MARKER_PX, sp.markerText);
        if (pointMarker.width !== box.width) pointMarker.width = box.width;
        if (pointMarker.height !== box.height) pointMarker.height = box.height;
      }
    };

    paint(pr.series, up);
    paint(signal.series, down);
  }

  private createSignalSeries(
    wasmContext: TSciChart,
    sp: SeriesPlan,
    up: string,
    down: string,
    thickness: number,
  ): PlotRender {
    // `containsNaN`, because both series are dense and carry a NaN on every bar
    // that did not fire — see `placeSignal`. The flag is an optimisation hint
    // SciChart trusts: left at its default the distribution calculator may treat
    // the column as NaN-free and resample it as if every slot were drawable.
    const options = { containsNaN: true, isSorted: true };
    const upData = new XyDataSeries(wasmContext, options);
    const downData = new XyDataSeries(wasmContext, options);

    const anchor = sp.signal!.anchor;
    const lane = sp.signal!.lane;
    const shape = effectiveSignalShape(sp.markerShape, anchor);

    const upSeries = this.createSignalRenderable(
      wasmContext, upData, SIGNAL_AXIS_ID, shape, "up", up, sp.markerSize, thickness, lane, sp.markerText);
    const downSeries = this.createSignalRenderable(
      wasmContext, downData, SIGNAL_AXIS_ID, shape, "down", down, sp.markerSize, thickness, lane, sp.markerText);

    if (sp.opacity !== undefined) {
      upSeries.opacity = sp.opacity;
      downSeries.opacity = sp.opacity;
    }
    if (sp.hidden) {
      // Both halves, or hiding a signal leaves half of it on the chart.
      upSeries.isVisible = false;
      downSeries.isVisible = false;
    }

    return {
      plotId: sp.plotId,
      alias: sp.outputAlias,
      series: upSeries,
      dataSeries: upData,
      dataKind: sp.dataKind,
      hidden: sp.hidden,
      seriesKind: sp.seriesKind,
      values: [],
      signal: {
        lane,
        anchor,
        eventKind: sp.eventKind,
        series: downSeries,
        dataSeries: downData,
        // Only for a series anchor. `anchorRef` is resolved either way by the
        // plan, and carrying it for a pane-anchored signal would make it look
        // like it rides something.
        ...(sp.signal!.anchor === "series" && sp.signal!.anchorRef
          ? { anchorPlotId: sp.signal!.anchorRef }
          : {}),
        marks: [],
        xs: [],
        markerShape: shape,
        ...(sp.markerText ? { markerText: sp.markerText } : {}),
      },
    };
  }

  /**
   * One half of a signal — up or down — as the renderable its shape needs.
   *
   * A glyph is a scatter whose point marker is stamped at the lane. A `column`
   * is a `FastColumnRenderableSeries` whose `zeroLineY` is the lane's bottom and
   * whose y on a firing bar is the lane's top (`signalColumnSpan`), so each
   * column fills its own lane and nothing else; a bar that did not fire is NaN
   * in the same dense series and draws nothing. Both go through one dense
   * `XyDataSeries` per half, so zoom, pan and the realtime patch reach a column
   * exactly as they reach a marker.
   */
  private createSignalRenderable(
    wasmContext: TSciChart,
    dataSeries: XyDataSeries,
    yAxisId: string,
    shape: MarkerShape,
    half: SignalDirection,
    colour: string,
    size: number | undefined,
    thickness: number,
    lane: number,
    text?: string,
  ): IRenderableSeries {
    if (shape === "column") {
      return new FastColumnRenderableSeries(wasmContext, {
        dataSeries,
        yAxisId,
        zeroLineY: signalColumnSpan(lane).base,
        dataPointWidth: SIGNAL_COLUMN_WIDTH,
        fill: colour,
        stroke: "transparent",
        strokeThickness: 0,
      });
    }
    const pointMarker: IPointMarker = new (markerKind(shape, half, text))(wasmContext, {
      ...markerBox(shape, size, SIGNAL_MARKER_PX, text),
      fill: colour,
      stroke: colour,
      strokeThickness: thickness,
    });
    return new XyScatterRenderableSeries(wasmContext, { dataSeries, yAxisId, pointMarker });
  }

  /**
   * Put `next` where `old` is in its pane's series list, and dispose of `old`
   * **without** its DataSeries — `next` was built over the same one.
   *
   * Same slot rather than appended, so a swapped signal keeps its draw order
   * against everything else in the pane. Visibility and opacity carry over: a
   * hidden signal restyled to a column stays hidden.
   */
  private swapRenderable(pr: PlotRender, old: IRenderableSeries, next: IRenderableSeries): void {
    next.isVisible = old.isVisible;
    next.opacity = old.opacity;
    for (const pane of this.panes.values()) {
      if (pane.byId.get(pr.plotId) !== pr) continue;
      const list = pane.sub.renderableSeries;
      const at = list.asArray().indexOf(old);
      // `false`: removing must not delete — that would take the shared data with it.
      list.remove(old, false);
      if (at >= 0) list.insert(at, next);
      else list.add(next);
      break;
    }
    // Detached before `delete()`, which otherwise deletes its DataSeries too.
    old.dataSeries = undefined as unknown as XyDataSeries;
    old.delete();
  }

  private createDataSeries(wasmContext: TSciChart, kind: SeriesPlan["dataKind"]): AnyDataSeries {
    switch (kind) {
      case "ohlc":
        return new OhlcDataSeries(wasmContext);
      case "xyy":
        return new XyyDataSeries(wasmContext);
      default:
        return new XyDataSeries(wasmContext);
    }
  }

  /** Up/down colors for OHLC series — from a paired palette, else theme pos/neg. */
  private pairedColors(plan: PalettePlan): [string, string] {
    if (plan.kind === "paired") return [plan.up, plan.down];
    const pos = resolveColorRef("positive", this.theme) ?? "#26a69a";
    const neg = resolveColorRef("negative", this.theme) ?? "#ef5350";
    return [pos, neg];
  }

  /**
   * **One `appendRange` per series, not an `append` per point.** A bulk load is
   * the whole daily file now — 11,500 bars for AAPL, 24,789 for `^GSPC` — across
   * every plot on the chart, and SciChart's own guidance is that the range call
   * is "higher performance for larger datasets" (Tutorial 04). A refresh is one
   * or two bars and takes the same path; a range of one is still correct.
   *
   * Each column is copied once into a `Float64Array` with a missing value as
   * `0` — the default the per-point calls applied — so what is drawn is exactly
   * what it was before, only handed over in one call.
   */
  /** Whether this plot is drawn against a signed log axis. */
  private onSignedLog(pr: PlotRender): boolean {
    const id = pr.series.yAxisId;
    return this.signedLogAxes.size > 0 && id !== undefined && this.signedLogAxes.has(id);
  }

  /** A column as drawn — `toSignedLog` on a signed log axis, else itself. */
  private drawnYs(pr: PlotRender, ys: Float64Array): Float64Array {
    if (!this.onSignedLog(pr)) return ys;
    for (let i = 0; i < ys.length; i++) ys[i] = toSignedLog(ys[i]!);
    return ys;
  }

  /** One value as drawn, for {@link drawnYs}' reason. */
  private drawnY(pr: PlotRender, y: number): number {
    return this.onSignedLog(pr) ? toSignedLog(y) : y;
  }

  private appendData(pr: PlotRender, append: NonNullable<SeriesDelta["append"]>): void {
    const { x } = append;
    if (pr.signal) {
      this.appendSignal(pr, append);
      return;
    }
    if (pr.dataKind === "ohlc") {
      const ds = pr.dataSeries as OhlcDataSeries;
      const { open = [], high = [], low = [], close = [] } = append;
      pr.lows ??= [];
      pr.highs ??= [];
      // Bar times, kept for the same reason as the two above — something outside
      // the DataSeries needs them and reading them back out of WASM is not a
      // thing this file does anywhere. Volume-by-Price's `visible` period maps
      // the axis' time window onto bar indices, and the search is in the plan
      // layer, so the shell's whole job is to have the timestamps to hand.
      pr.xs ??= [];
      const xs = dense(x, x.length);
      const lows = dense(low, x.length);
      const highs = dense(high, x.length);
      const closes = dense(close, x.length);
      ds.appendRange(xs, dense(open, x.length), highs, lows, closes);
      for (let i = 0; i < x.length; i++) {
        pr.values.push(closes[i]!);
        pr.xs.push(xs[i]!);
        // Kept for a signal anchored to this plot. `values` is the close, and a
        // marker at the close sits inside the candle rather than clear of it.
        pr.lows.push(lows[i]!);
        pr.highs.push(highs[i]!);
      }
    } else if (pr.dataKind === "xyy") {
      const ds = pr.dataSeries as XyyDataSeries;
      const { y, high = [] } = append;
      const ys = this.drawnYs(pr, dense(y, x.length));
      ds.appendRange(dense(x, x.length), ys, this.drawnYs(pr, dense(high, x.length)));
      for (let i = 0; i < x.length; i++) pr.values.push(ys[i]!);
    } else {
      const ds = pr.dataSeries as XyDataSeries;
      const ys = this.drawnYs(pr, dense(append.y, x.length));
      ds.appendRange(dense(x, x.length), ys);
      for (let i = 0; i < x.length; i++) pr.values.push(ys[i]!);
    }
  }

  /**
   * A signed signal column into two sparse series.
   *
   * Only the bars that fired get a point, which is the difference between a
   * signal and every other series here: the others carry one y per x, and this
   * one carries a marker per *event*. The zeros are dropped rather than plotted
   * at zero — a marker on every bar is not a signal.
   *
   * `pr.values` still records the full column, zeros included. It drives the
   * palette recompute and the last-value marker, both of which index by bar, so
   * a compacted copy would silently misalign them.
   */
  private appendSignal(pr: PlotRender, append: NonNullable<SeriesDelta["append"]>): void {
    const signal = pr.signal;
    if (!signal) return;

    const { x, y = [] } = append;
    // The bar index is into the *whole* column, not into this batch — the
    // anchor's low/high is looked up by it, and that array spans every append.
    const base = pr.values.length;
    for (const point of signalPoints(y, signal.eventKind)) {
      const at = x[point.index];
      if (at === undefined) continue;
      signal.marks.push({ x: at, index: base + point.index, direction: point.direction });
    }
    for (let i = 0; i < x.length; i++) {
      pr.values.push(y[i] ?? 0);
      signal.xs.push(x[i] ?? 0);
    }

    this.placeSignal(pr);
  }

  /**
   * The live bar of a signal, re-read from its new value — the realtime half of
   * {@link appendSignal}.
   *
   * A signal that fires, stops or reverses on the live bar arrives as a patch,
   * because the bar already exists. This used to return early, on the belief
   * that the 60-second re-push would draw it; it could not, because every frame
   * after the first reaches here as a diff. So an intraday signal appeared only
   * on a page load, one that stopped firing kept its marker, and at the rollover
   * yesterday's final value never landed.
   *
   * **The bar is the last real one held, not the last element.** With a
   * projection on the chart the column runs past the last bar with NaN, and the
   * end of the array is a date months out. `realBars` is still the count from
   * *before* this refresh when this runs, because the host moves it with
   * `setBarTimes` only after every delta is applied — so it names exactly the
   * bar the patch refers to.
   */
  private patchSignal(pr: PlotRender, value: number | undefined): void {
    const signal = pr.signal;
    if (!signal || value === undefined) return;

    const held = this.realBars > 0 ? Math.min(this.realBars, pr.values.length) : pr.values.length;
    const at = held - 1;
    const x = signal.xs[at];
    if (at < 0 || x === undefined) return;

    pr.values[at] = value;
    signal.marks = withSignalAt(
      signal.marks,
      at,
      value,
      (direction) => ({ x, index: at, direction }),
      signal.eventKind,
    );
    this.placeSignal(pr);
  }

  /**
   * (Re)write a signal's two data series from its marks.
   *
   * Rewritten wholesale rather than appended to, because a bar-anchored marker's
   * y is not a property of its own bar alone — it is the anchor's value *plus an
   * offset read off the current visible range*, so every existing marker moves
   * when the range does. Cheap in practice: a signal is sparse by nature, and
   * clearing and refilling a few dozen points is nothing beside the redraw that
   * prompted it.
   *
   * A pane-anchored signal does not depend on the range at all and could append,
   * but it goes the same way — one path that is always right beats two where the
   * cheaper one is right most of the time.
   */
  private placeSignal(pr: PlotRender): void {
    const signal = pr.signal;
    if (!signal) return;

    const upData = pr.dataSeries as XyDataSeries;
    const downData = signal.dataSeries;

    const anchor = this.anchorFor(pr);
    const offset = anchor ? signalOffset(this.rangeOf(anchor), signal.lane) : 0;
    // A column's y is its lane's far edge — the renderable draws from
    // `zeroLineY` (the near edge) to it — where a glyph sits on the lane centre.
    const laneY =
      signal.markerShape === "column" ? signalColumnSpan(signal.lane).top : signal.lane;

    // **Both series are dense — one slot per bar, NaN where nothing fired.**
    //
    // Under the old `CategoryAxis` this was a *correctness* requirement, and the
    // reason the obvious implementation was wrong: SciChart positioned a point by
    // its **index within its own data series**, not by the x it was appended
    // with — `PointMarkerDrawingProvider.draw` passes `pointSeries.indexes`
    // rather than the x values when the coordinate calculator reports itself as a
    // category one. A series holding only the bars that fired therefore drew its
    // first marker on bar 0, its second on bar 1, and so on: every signal stacked
    // against the left edge, up and down triangles on top of each other, however
    // right the timestamp handed to `append` was.
    //
    // **That specific failure is gone.** `IndexCoordinateCalculator` never sets
    // `isCategoryCoordinateCalculator`, so under a `DiscontinuousDateAxis` the
    // same call passes the x values and a marker lands on its own timestamp.
    //
    // The dense layout stays regardless. It is what the wire sends, and one slot
    // per bar is what keeps these arrays indexable the same way as every other
    // per-bar series here. A sparse encoding has not been tried against the new
    // axis, so this is the shape known to work rather than the only one that
    // could — NaN rather than a skipped slot, for that same alignment.
    // Cleared first and unconditionally: this also runs before any data has
    // landed (`attachSignalAnchors` places once at build time), and a reload
    // that returns fewer bars has to shed the slots it no longer has.
    const n = signal.xs.length;
    upData.clear();
    downData.clear();
    if (n === 0) return;

    const upY = new Float64Array(n).fill(NaN);
    const downY = new Float64Array(n).fill(NaN);

    for (const mark of signal.marks) {
      // A mark whose bar has been dropped by a smaller reload has no slot to sit
      // in; the alternative is writing past the end of the spine.
      if (mark.index < 0 || mark.index >= n) continue;
      const y = anchor
        ? anchoredSignalY(mark.direction, barExtremes(anchor, mark.index), offset)
        : // A pane lane: both directions share the row and are told apart by
          // shape and colour, not by height.
          laneY;
      if (!Number.isFinite(y)) continue;
      (mark.direction === "up" ? upY : downY)[mark.index] = y;
    }

    // `appendRange` over a typed array, not `append` per point: this runs on
    // every visible-range change for a bar-anchored signal, and a dense rewrite
    // is now one call per direction rather than fifteen hundred.
    const xs = Float64Array.from(signal.xs);
    upData.appendRange(xs, upY);
    downData.appendRange(xs, downY);
  }

  /**
   * Put every bar-anchored signal on its anchor's axis, and keep it there.
   *
   * Two things, both of which are wrong if only one is done:
   *
   * - **The axis.** A bar-anchored marker's y is in the anchor's units — dollars
   *   for a candle, MACD units for a MACD line — so it has to draw against the
   *   anchor's axis. Left on the lane axis it would be placed at a price against
   *   a 0.5–1.5 scale and land somewhere off the pane.
   * - **The subscription.** The offset is a fraction of that axis' *visible*
   *   range, so every zoom and pan changes it. Without re-placing, the gap is
   *   correct at the range the data arrived on and drifts from there — which
   *   looks like the markers slowly detaching from their bars.
   *
   * Both halves are here rather than at creation because a signal can be built
   * before the plot it rides.
   */
  private attachSignalAnchors(wasmContext: TSciChart, pane: PaneRender): void {
    for (const pr of pane.byId.values()) {
      if (!pr.signal?.anchorPlotId) continue;
      const anchor = this.anchorFor(pr);
      if (!anchor) continue;

      const anchorAxis = pane.sub.yAxes.asArray().find((a) => a.id === anchor.series.yAxisId);
      if (!anchorAxis) continue;

      const mirror = this.mirrorAxis(wasmContext, pane, anchorAxis);
      pr.series.yAxisId = mirror.id;
      pr.signal.series.yAxisId = mirror.id;

      mirror.visibleRange = anchorAxis.visibleRange;
      this.placeSignal(pr);

      anchorAxis.visibleRangeChanged.subscribe((args) => {
        if (!args) return;
        // Range first, then the points: `placeSignal` reads the *mirror's*
        // range through `rangeOf`, so re-placing against a stale one would put
        // every marker a frame behind the chart it is pinned to.
        mirror.visibleRange = args.visibleRange;
        this.placeSignal(pr);
      });
    }
  }

  /** The zero-width twin of `anchorAxis`, created once per pane. */
  private mirrorAxis(
    wasmContext: TSciChart,
    pane: PaneRender,
    anchorAxis: AxisBase2D,
  ): AxisBase2D {
    const id = `${SIGNAL_MIRROR_PREFIX}${anchorAxis.id}`;
    const existing = pane.sub.yAxes.asArray().find((a) => a.id === id);
    if (existing) return existing;

    const mirror = new NumericAxis(wasmContext, {
      id,
      autoRange: EAutoRange.Never,
      visibleRange: anchorAxis.visibleRange,
      isVisible: false,
      drawLabels: false,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
    });
    pane.sub.yAxes.add(mirror);
    return mirror;
  }

  /** Re-place every signal riding `anchor`, whose values just changed under it. */
  private replaceSignalsAnchoredTo(anchor: PlotRender): void {
    if (anchor.signal) return; // a signal cannot be ridden
    for (const pane of this.panes.values()) {
      for (const pr of pane.byId.values()) {
        if (pr.signal?.anchorPlotId === anchor.plotId) this.placeSignal(pr);
      }
    }
  }

  /** The plot a signal rides, if it rides one and that plot is still on the chart. */
  private anchorFor(pr: PlotRender): PlotRender | undefined {
    const id = pr.signal?.anchorPlotId;
    if (!id) return undefined;
    for (const pane of this.panes.values()) {
      const anchor = pane.byId.get(id);
      // Never itself, and never another signal — a signal has no value of its
      // own to sit beside, so a chain of them would have no base case.
      if (anchor && anchor !== pr && !anchor.signal) return anchor;
    }
    return undefined;
  }

  /** The visible range of the axis a plot draws against. */
  private rangeOf(pr: PlotRender): { min: number; max: number } {
    for (const pane of this.panes.values()) {
      const axis = pane.sub.yAxes.asArray().find((a) => a.id === pr.series.yAxisId);
      if (axis) return { min: axis.visibleRange.min, max: axis.visibleRange.max };
    }
    return { min: 0, max: 0 };
  }

  private patchLast(pr: PlotRender, patch: NonNullable<SeriesDelta["patchLast"]>): void {
    // A signal is not `update(last)`: its two series are rewritten from its
    // marks, so a changed bar is a change of which marks exist. See
    // `patchSignal`.
    if (pr.signal) {
      this.patchSignal(pr, patch.y);
      return;
    }

    const last = pr.dataSeries.count() - 1;
    if (last < 0) return;
    if (pr.dataKind === "ohlc") {
      const ds = pr.dataSeries as OhlcDataSeries;
      // **A missing field keeps the bar's own value; it does not become zero.**
      //
      // A realtime tick patches the *close* and says nothing about the other
      // three, and `?? 0` turned that into open 0, high 0, low 0 — a candle
      // drawn from the axis floor up to the last price, every time today's bar
      // was patched. The data was never wrong; only what was written over it.
      //
      // Read back from the series rather than remembered, because the series is
      // what is actually on screen — a cached copy would be a second answer to
      // "what does this bar say" and would drift the first time a bulk load
      // replaced it.
      ds.update(
        last,
        patch.open ?? ds.getNativeOpenValues().get(last),
        patch.high ?? ds.getNativeHighValues().get(last),
        patch.low ?? ds.getNativeLowValues().get(last),
        patch.close ?? ds.getNativeCloseValues().get(last),
      );
      pr.values[last] = patch.close ?? pr.values[last] ?? 0;
      // **And the copies the Y fit reads.** `fitYAxes` ranges the price axis
      // from `highs`/`lows`, not from the DataSeries; left behind, a live bar
      // that ran past its first high drew above the pane while the axis still
      // fitted the bar as it first arrived (owner's screenshot, 2026-09-29).
      if (pr.highs && patch.high !== undefined) pr.highs[last] = patch.high;
      if (pr.lows && patch.low !== undefined) pr.lows[last] = patch.low;
    } else if (pr.dataKind === "xyy" && patch.y !== undefined) {
      // A band carries two y values per point, and `update(index, y)` with the
      // second omitted does not leave it alone — it writes the default, which
      // drops the far edge to zero and fills the whole pane on the first
      // realtime tick. The append path already pairs them (`y` and `high`); the
      // patch path has to as well.
      const ds = pr.dataSeries as XyyDataSeries;
      const y = this.drawnY(pr, patch.y);
      ds.update(last, y, patch.high !== undefined ? this.drawnY(pr, patch.high) : ds.getNativeY1Values().get(last));
      pr.values[last] = y;
    } else if (patch.y !== undefined) {
      const y = this.drawnY(pr, patch.y);
      (pr.dataSeries as XyDataSeries).update(last, y);
      pr.values[last] = y;
    }
  }
}

/** Vertical weight of a pane from its height ("3fr" → 3); non-fr heights weigh 1. */

/** Pair each pane with the normalised band it occupies. */
function stackPanes(
  panes: readonly Pane[],
): [Pane, { x: number; y: number; width: number; height: number }][] {
  const fractions = stackFractions(paneWeights(panes));
  return panes.map((pane, i) => [
    pane,
    { x: 0, y: fractions[i]!.y, width: 1, height: fractions[i]!.height },
  ]);
}

function mapAutoRange(mode: AxisPlan["autoRange"]): EAutoRange {
  switch (mode) {
    case "once":
      return EAutoRange.Once;
    case "never":
      return EAutoRange.Never;
    default:
      return EAutoRange.Always;
  }
}

/** A PaletteProvider only for the solid/paired tiers; none/expression → undefined. */
function paletteProviderFor(plan: PalettePlan): PlanPaletteProvider | undefined {
  return plan.kind === "solid" || plan.kind === "paired"
    ? new PlanPaletteProvider(plan)
    : undefined;
}
/**
 * A column as the typed array `appendRange` takes, `n` long, a missing value as
 * `0` — the default the per-point `append` calls applied, so the switch to one
 * range call changes nothing that is drawn. An absent column is all zeros, which
 * is what `open[i] ?? 0` gave a delta that carried no `open`.
 */
function dense(values: readonly (number | undefined)[] | undefined, n: number): Float64Array {
  const out = new Float64Array(n);
  if (!values) return out;
  for (let i = 0; i < n; i++) out[i] = values[i] ?? 0;
  return out;
}
